import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  getNotificationPermissionState,
  listenForForegroundMessages,
  requestNotificationPermission,
} from './firebase';
import Dashboard from './Dashboard';
import StaffManagement from './StaffManagement';
import './OrdersReceived.css';
import './DeliveryDispatch.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://vakra.co.in';
const STANDARD_ORDERS_API = `${BASE_URL}/api/standard-orders`;
const DELIVERY_API = `${BASE_URL}/api/delivery-dispatch`;
const EXTERNAL_FOOD_ORDERS_API = `${BASE_URL}/api/external-food-orders`;

const FASTFOOD_NOTIFICATION_DEVICE_ID_KEY = 'fastfood_notification_device_id';
const FASTFOOD_INVOICE_DRAFTS_KEY = 'fastfood_invoice_drafts_v1';

const STAFF_TOKEN_KEY = 'staff_token';
const STAFF_USER_KEY = 'staff_user';
const STAFF_PERMISSIONS_KEY = 'staff_permissions';
const STAFF_VENDOR_ID_KEY = 'staff_vendor_id';
const STAFF_VENDOR_TYPE_KEY = 'staff_vendor_type';

const readStaffStorage = (key) => {
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const safeStaffJsonParse = (value, fallback = {}) => {
  try {
    if (!value) return fallback;
    if (typeof value === 'object') return value;
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const getStaffSessionSnapshot = () => {
  const token = readStaffStorage(STAFF_TOKEN_KEY);
  const staff = safeStaffJsonParse(readStaffStorage(STAFF_USER_KEY), {});
  const permissionsFromStorage = safeStaffJsonParse(
    readStaffStorage(STAFF_PERMISSIONS_KEY),
    {}
  );

  const permissionsFromStaff = safeStaffJsonParse(
    staff?.permissions,
    staff?.permissions || {}
  );

  const vendorId =
    readStaffStorage(STAFF_VENDOR_ID_KEY) ||
    staff?.vendorId ||
    staff?.vendor_id ||
    staff?.ownerVendorId ||
    '';

  const vendorType =
    readStaffStorage(STAFF_VENDOR_TYPE_KEY) ||
    staff?.vendorType ||
    staff?.vendor_type ||
    'fastfood';

  return {
    isStaff: Boolean(token),
    token,
    staff,
    vendorId,
    vendorType,
    permissions: {
      ...(permissionsFromStaff || {}),
      ...(permissionsFromStorage || {}),
    },
  };
};

const hasAnyPermission = (permissions = {}, keys = []) =>
  keys.some((key) => Boolean(permissions?.[key]));

const getDefaultStaffPanel = (permissions = {}) => {
  // Staff ko poora page kitchen-only nahi banayenge.
  // Bas same ordersReceived page me uske allowed sections dikhenge.
  if (hasAnyPermission(permissions, ['ordersView', 'kotView', 'kitchenQueue', 'markPreparing', 'markReady', 'printKot', 'counterOrder'])) {
    return 'orders';
  }
  if (hasAnyPermission(permissions, ['invoiceView', 'invoiceEdit', 'billPrint', 'paymentUpdate', 'completeOrder'])) return 'invoice';
  if (hasAnyPermission(permissions, ['deliveryDispatch', 'assignRider', 'reassignRider', 'deliveryRecords', 'riderManage'])) return 'delivery';
  if (permissions.recordsView) return 'customers';
  if (permissions.reportsView || permissions.expenseManage) return 'reports';
  if (permissions.staffManagement) return 'staff';
  if (permissions.settingsManage) return 'settings';
  return 'orders';
};


const getOrCreateNotificationDeviceId = () => {
  try {
    const existing = localStorage.getItem(FASTFOOD_NOTIFICATION_DEVICE_ID_KEY);

    if (existing) return existing;

    const generated =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `device_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

    const safe = String(generated)
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .slice(0, 120);

    localStorage.setItem(FASTFOOD_NOTIFICATION_DEVICE_ID_KEY, safe);

    return safe;
  } catch {
    return `device_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
};

const getNotificationDeviceLabel = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

  if (/android/i.test(ua)) return 'Android Chrome';
  if (/iphone|ipad|ipod/i.test(ua)) return 'iPhone Browser';
  if (/windows/i.test(ua)) return 'Windows Browser';
  if (/macintosh|mac os x/i.test(ua)) return 'Mac Browser';

  return 'Browser Device';
};


const STATUS_FLOW = {
  new: {
    label: 'New',
    longLabel: 'New Order',
    action: 'Accept',
    next: 'accepted',
    icon: '📋',
    className: 'new',
  },
  accepted: {
    label: 'Accepted',
    longLabel: 'Accepted',
    action: 'Kitchen',
    next: 'preparing',
    icon: '✅',
    className: 'accepted',
  },
  preparing: {
    label: 'Kitchen',
    longLabel: 'Kitchen',
    action: 'Ready',
    next: 'ready',
    icon: '👨‍🍳',
    className: 'preparing',
  },
  ready: {
    label: 'Ready',
    longLabel: 'Ready',
    action: 'Invoice',
    next: null,
    icon: '🍽️',
    className: 'ready',
  },
  done: {
    label: 'Completed',
    longLabel: 'Completed',
    action: 'Completed',
    next: null,
    icon: '✅',
    className: 'done',
  },
  cancelled: {
    label: 'Cancelled',
    longLabel: 'Cancelled',
    action: 'Cancelled',
    next: null,
    icon: '✕',
    className: 'cancelled',
  },
};

const KEYBOARD_STATUS_RANK = {
  new: 0,
  accepted: 1,
  preparing: 2,
  ready: 3,
  done: 4,
  cancelled: 4,
};

const getKeyboardStatusRank = (status) =>
  KEYBOARD_STATUS_RANK[String(status || '').toLowerCase()] ?? -1;

const mergeKeyboardStatus = (currentStatus, incomingStatus) => {
  const current = String(currentStatus || '').toLowerCase();
  const incoming = String(incomingStatus || '').toLowerCase();

  if (!current) return incoming;
  if (!incoming) return current;

  /*
    Cancelled terminal status hai. Silent refresh, old order snapshot ya
    orders.status='pending' isko dobara New/Completed me convert nahi karega.
  */
  if (current === 'cancelled' || incoming === 'cancelled') {
    return 'cancelled';
  }

  /*
    Order workflow forward-only hai:
    new → accepted → preparing → ready → done
  */
  return getKeyboardStatusRank(incoming) >= getKeyboardStatusRank(current)
    ? incoming
    : current;
};

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'preparing', label: 'Kitchen' },
  { key: 'ready', label: 'Ready' },
  { key: 'done', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const MODE_LABELS = {
  dine_in: 'Dine-in',
  takeaway: 'Takeaway',
  delivery: 'Delivery',
};

const PAYMENT_STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'partial', label: 'Partial' },
  { value: 'unpaid', label: 'Credit / Udhari' },
  { value: 'cod', label: 'COD' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online' },
  { value: 'razorpay', label: 'Razorpay' },
  { value: 'credit', label: 'Credit' },
  { value: 'cod', label: 'COD' },
];

const DEFAULT_VENDOR_BILLING_SETTINGS = {
  gstEnabled: false,
  defaultGstPercent: '5',
};

const initialInvoiceState = {
  manualItemName: '',
  manualItemQty: '1',
  manualItemPrice: '',
  manualItems: [],
  discountType: 'amount',
  discount: '',
  deliveryCharge: '',
  packingCharge: '',
  gstEnabled: false,
  gstPercent: '',
  gstTouched: false,
  roundOff: '',
  paymentStatus: 'paid',
  paymentMethod: 'cash',
  amountPaid: '',
  notes: '',
};

const emptyDeliveryRiderForm = {
  name: '',
  mobile: '',
  pin: '',
  vehicleType: 'bike',
  vehicleNumber: '',
  serviceArea: '',
  status: 'available',
  notes: '',
};

const DELIVERY_RIDER_STATUS_LABELS = {
  available: 'Available',
  busy: 'Busy',
  on_delivery: 'On Delivery',
  inactive: 'Inactive',
  offline: 'Offline',
};

const DELIVERY_TRIP_STATUS_LABELS = {
  assigned: 'Assigned',
  picked_up: 'Picked Up',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const StandardOrdersReceived = () => {
  const [vendorId, setVendorId] = useState(null);
  const [vendorType, setVendorType] = useState('fastfood');
  const [staffSession, setStaffSession] = useState(() => getStaffSessionSnapshot());

  const isStaffLogin = Boolean(staffSession?.isStaff);
  const staffPermissions = staffSession?.permissions || {};
  const staffName =
    staffSession?.staff?.staffName ||
    staffSession?.staff?.staff_name ||
    staffSession?.staff?.name ||
    'Staff';

  const canAccess = useCallback(
    (permissionKey) => {
      if (!isStaffLogin) return true;
      if (!permissionKey) return true;
      return Boolean(staffPermissions?.[permissionKey]);
    },
    [isStaffLogin, staffPermissions]
  );

  const canAccessAny = useCallback(
    (permissionKeys = []) => {
      if (!isStaffLogin) return true;
      return permissionKeys.some((key) => Boolean(staffPermissions?.[key]));
    },
    [isStaffLogin, staffPermissions]
  );

  const canViewOrdersPanel = canAccessAny([
    'ordersView',
    'kotView',
    'kitchenQueue',
    'markPreparing',
    'markReady',
    'printKot',
    'counterOrder',
    'invoiceView',
    'billPrint',
  ]);

  const canUseKitchenTools = canAccessAny([
    'kotView',
    'kitchenQueue',
    'markPreparing',
    'markReady',
    'printKot',
  ]);

  const canUseBillingTools = canAccessAny([
    'invoiceView',
    'invoiceEdit',
    'billPrint',
    'paymentUpdate',
    'completeOrder',
  ]);

  const [activeOrders, setActiveOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);

  const [currentScreen, setCurrentScreen] = useState('list');
  const [activeTab, setActiveTab] = useState('active');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelOrderTarget, setCancelOrderTarget] = useState(null);
  const [cancelOrderLoading, setCancelOrderLoading] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);

  // Vendor verbal / walk-in order creation
  const [counterOrderOpen, setCounterOrderOpen] = useState(false);
  const [counterProducts, setCounterProducts] = useState([]);
  const [counterProductsLoading, setCounterProductsLoading] = useState(false);
  const [counterSaving, setCounterSaving] = useState(false);
  const [counterSearch, setCounterSearch] = useState('');
  const [counterCategoryFilter, setCounterCategoryFilter] = useState('All');
  const [counterCategorySidebarOpen, setCounterCategorySidebarOpen] = useState(false);
  const [counterOrderMode, setCounterOrderMode] = useState('dine_in');
  const [counterTableNumber, setCounterTableNumber] = useState('');
  const [counterCustomerName, setCounterCustomerName] = useState('');
  const [counterCustomerMobile, setCounterCustomerMobile] = useState('');
  const [counterCustomers, setCounterCustomers] = useState([]);
  const [counterCustomersLoading, setCounterCustomersLoading] = useState(false);
  const [counterCustomerSearch, setCounterCustomerSearch] = useState('');
  const [counterSelectedCustomerId, setCounterSelectedCustomerId] = useState('');
  const [counterCustomerSaving, setCounterCustomerSaving] = useState(false);
  const [counterCustomerPanel, setCounterCustomerPanel] = useState('');
  const [counterPaymentMethod, setCounterPaymentMethod] = useState('cash');
  const [counterCart, setCounterCart] = useState([]);
  const [counterKeyboardProductIndex, setCounterKeyboardProductIndex] = useState(-1);

  // Invoice edit mode product picker
  const [invoiceProductSearch, setInvoiceProductSearch] = useState('');
  const [invoiceKeyboardProductIndex, setInvoiceKeyboardProductIndex] = useState(-1);
  const [invoiceProductPickerOpen, setInvoiceProductPickerOpen] = useState(false);
  const [invoiceProductPickerOrderId, setInvoiceProductPickerOrderId] = useState(null);
  const [invoiceCategoryFilter, setInvoiceCategoryFilter] = useState('All');
  const [invoiceCategorySidebarOpen, setInvoiceCategorySidebarOpen] = useState(false);
  // Invoice details default me hidden rahenge. Jis order ka button dabega,
  // sirf usi order ka complete Invoice / Bill section open hoga.
  const [invoiceDetailsOpenOrderId, setInvoiceDetailsOpenOrderId] = useState(null);

  const [actionDropdown, setActionDropdown] = useState(null);
  const [historyFilterOpen, setHistoryFilterOpen] = useState(false);
  const [historyDateFilter, setHistoryDateFilter] = useState({
    type: 'all',
    customFrom: '',
    customTo: '',
  });

  const [sidePanel, setSidePanel] = useState(() => getDefaultStaffPanel(getStaffSessionSnapshot().permissions));

  // Online Orders UI (Swiggy / Zomato)
  // NOTE: Abhi ye existing VAKRA orders me order source fields ko read karta hai.
  // Actual live Swiggy/Zomato webhook backend later connect hoga.
  const [onlineOrderFilter, setOnlineOrderFilter] = useState('all');
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [onlineOrdersLoading, setOnlineOrdersLoading] = useState(false);

  const normalizeOrderSource = (order = {}) => {
    const raw = String(
      order.order_source ||
        order.orderSource ||
        order.source ||
        order.platform ||
        order.online_source ||
        order.onlineSource ||
        ''
    )
      .toLowerCase()
      .trim();

    if (raw.includes('swiggy')) return 'swiggy';
    if (raw.includes('zomato')) return 'zomato';
    return 'vakra';
  };

  const isCounterCreatedOrder = (order = {}) => {
    const rawSource = String(
      order.order_source ||
        order.orderSource ||
        order.source ||
        order.platform ||
        order.online_source ||
        order.onlineSource ||
        ''
    )
      .toLowerCase()
      .trim();

    return (
      Boolean(order.is_counter_order || order.isCounterOrder) ||
      ['admin', 'counter', 'pos', 'walk_in', 'walk-in'].some((value) =>
        rawSource.includes(value)
      )
    );
  };


  const [deliveryReadyOrders, setDeliveryReadyOrders] = useState([]);
  const [deliveryRiders, setDeliveryRiders] = useState([]);
  const [deliveryTrips, setDeliveryTrips] = useState([]);
  const [deliveryTripHistory, setDeliveryTripHistory] = useState([]);
  const [selectedDeliveryRiderRecord, setSelectedDeliveryRiderRecord] = useState(null);
  const [selectedDeliveryRecordMetric, setSelectedDeliveryRecordMetric] = useState('trips');
  const [deliverySelectedOrderIds, setDeliverySelectedOrderIds] = useState([]);
  const [deliverySelectedRiderId, setDeliverySelectedRiderId] = useState('');
  const [deliverySelectedArea, setDeliverySelectedArea] = useState('all');
  const [deliverySearch, setDeliverySearch] = useState('');
  const [deliveryTab, setDeliveryTab] = useState('dispatch');
  const [deliveryTrackingTripId, setDeliveryTrackingTripId] = useState(null);
  const [deliveryFormOpen, setDeliveryFormOpen] = useState(false);
  const [deliveryRiderForm, setDeliveryRiderForm] = useState(emptyDeliveryRiderForm);

  // Fast-food vendor-controlled delivery rider management
  const [resetPinRider, setResetPinRider] = useState(null);
  const [resetPinValue, setResetPinValue] = useState('');
  const [deleteRiderTarget, setDeleteRiderTarget] = useState(null);

  const [reassignOrder, setReassignOrder] = useState(null);
  const [reassignRiderId, setReassignRiderId] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [reassignError, setReassignError] = useState('');

  const [customerDues, setCustomerDues] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [reportData, setReportData] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [uiSettings, setUiSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('fastfood_ui_settings');
      return saved
        ? JSON.parse(saved)
        : {
            compactOrders: true,
            autoRefresh: true,
            paperSize: '58mm',
            shopName: 'QuickBite',
          };
    } catch {
      return {
        compactOrders: true,
        autoRefresh: true,
        paperSize: '58mm',
        shopName: 'QuickBite',
      };
    }
  });

  const [invoiceByOrder, setInvoiceByOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(FASTFOOD_INVOICE_DRAFTS_KEY);
      const parsed = saved ? JSON.parse(saved) : {};

      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  });
  const [billingSettings, setBillingSettings] = useState(
    DEFAULT_VENDOR_BILLING_SETTINGS
  );
  const [billingSettingsLoading, setBillingSettingsLoading] = useState(false);
  const [billingSettingsSaving, setBillingSettingsSaving] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [ringReady, setRingReady] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() =>
    getNotificationPermissionState()
  );
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationDevices, setNotificationDevices] = useState([]);
  const [notificationDevicesLoading, setNotificationDevicesLoading] = useState(false);
  const [currentNotificationDeviceId] = useState(() => getOrCreateNotificationDeviceId());
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const pollingRef = useRef(null);
  const requestInFlightRef = useRef(false);
  const activeSignatureRef = useRef('');
  // Track already-seen active order IDs so a genuinely new order can become
  // the selected order automatically without disturbing normal manual selection.
  const knownActiveOrderIdsRef = useRef(new Set());
  const hasLoadedOrdersOnceRef = useRef(false);
  const billingSettingsRef = useRef(DEFAULT_VENDOR_BILLING_SETTINGS);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const foregroundFcmHandledAtRef = useRef(0);
  const actionDropdownRef = useRef(null);
  const historyFilterRef = useRef(null);
  const detailHistoryEntryRef = useRef(false);
  const orderSearchInputRef = useRef(null);

  // Counter Order keyboard navigation refs
  const counterOrderDrawerRef = useRef(null);
  const counterSearchInputRef = useRef(null);
  const counterTableSelectRef = useRef(null);
  const counterCustomerNameInputRef = useRef(null);
  const counterCustomerMobileInputRef = useRef(null);
  const counterKeyboardProductIndexRef = useRef(-1);
  /*
    Counter drawer products async load hote hain aur search type karne par
    filtered list re-render hoti hai. Global keydown listener stale render
    capture na kare, isliye latest visible products aur cart live refs me
    rakhenge. Arrow / Enter / - hamesha current screen list par work karega.
  */
  const counterFilteredProductsRef = useRef([]);
  const counterCartRef = useRef([]);
  const counterTableKeyboardBufferRef = useRef('');
  const counterTableKeyboardResetTimerRef = useRef(null);

  // Invoice edit mode refs
  const invoiceProductSearchInputRef = useRef(null);
  const invoiceKeyboardProductIndexRef = useRef(-1);
  const invoiceFilteredProductsRef = useRef([]);
  const invoiceEditControlRefs = useRef({});
  const invoiceEditControlOrderRef = useRef([
    'discount',
    'gstToggle',
    'gstPercent',
    'deliveryCharge',
    'packingCharge',
    'roundOff',
    'paymentStatus',
    'paymentMethod',
    'amountPaid',
  ]);

  /*
    Keyboard shortcut safety:
    - React state render hone me thoda time lag sakta hai.
    - Vendor fast A → K → R press kare to actions sequence me execute honge.
    - Latest status ref stale selectedOrder status ko override karega.
  */
  const keyboardStatusByOrderRef = useRef(new Map());
  const keyboardActionQueueRef = useRef(Promise.resolve());
  const keyboardSelectedOrderRef = useRef(null);

  /*
    Hidden iframe print dialog focus le leta hai.
    Print close hone ke baad shortcut keys immediately active karne ke liye
    dashboard focus restore karna zaruri hai.
  */
  const printKeyboardFocusRestorePendingRef = useRef(false);

  const getToken = () => readStaffStorage(STAFF_TOKEN_KEY) || localStorage.getItem('token') || '';

  const showMessage = useCallback((text, type = 'success', duration = 2600) => {
    setMessage(text);
    setMessageType(type);
    window.clearTimeout(showMessage._timer);
    showMessage._timer = window.setTimeout(() => {
      setMessage('');
    }, duration);
  }, []);


  const getStaffDeniedMessage = (featureName = 'is section') =>
    `${staffName} ko ${featureName} ka access nahi hai.`;

  const isOrderAllowedForCurrentStaff = useCallback(
    (order) => {
      if (!isStaffLogin) return true;
      if (!order) return false;

      const status = String(order.displayStatus || '').toLowerCase();
      const mode = String(order.displayMode || '').toLowerCase();

      if (canAccess('ordersView')) return true;

      if (canUseKitchenTools) {
        // Kitchen staff ko sirf accepted/kitchen/ready order dikhna chahiye.
        return ['accepted', 'preparing', 'ready'].includes(status);
      }

      if (canUseBillingTools) {
        return ['ready'].includes(status);
      }

      if (canAccessAny(['deliveryDispatch', 'assignRider', 'reassignRider'])) {
        return mode === 'delivery' && status === 'ready';
      }

      return false;
    },
    [isStaffLogin, canAccess, canAccessAny, canUseKitchenTools, canUseBillingTools]
  );

  const renderStaffAccessDenied = (title = 'Access Denied') => (
    <section className="ff-staff-access-denied">
      <div>
        <span>🔒</span>
        <h2>{title}</h2>
        <p>Owner ne is staff ko is section ka access nahi diya hai.</p>
        <small>Staff: {staffName}</small>
      </div>
    </section>
  );

  useEffect(() => {
    const latestStaffSession = getStaffSessionSnapshot();
    setStaffSession(latestStaffSession);

    if (!latestStaffSession.isStaff) return;

    const permissions = latestStaffSession.permissions || {};
    const defaultPanel = getDefaultStaffPanel(permissions);

    setSidePanel(defaultPanel);
    setCurrentScreen('list');
    setActiveTab('active');

    if (!permissions.ordersView && hasAnyPermission(permissions, ['kotView', 'kitchenQueue', 'markPreparing', 'markReady', 'printKot'])) {
      setStatusFilter('preparing');
      return;
    }

    if (!permissions.ordersView && hasAnyPermission(permissions, ['invoiceView', 'billPrint', 'completeOrder'])) {
      setStatusFilter('ready');
      return;
    }

    setStatusFilter('all');
  }, []);

  useEffect(() => {
    if (isStaffLogin && activeTab === 'history' && !canAccess('recordsView')) {
      setActiveTab('active');
    }
  }, [isStaffLogin, activeTab, canAccess]);

  const normalizeCounterProduct = (product = {}) => {
    const id = Number(product.id || product.productId || 0);
    const price = toNumber(
      product.price_show_to_customer ??
        product.selling_price ??
        product.price,
      0
    );

    return {
      ...product,
      id,
      productId: id,
      name: product.name || product.product_name || 'Food Item',
      image: product.image_url || product.image || '',
      image_url: product.image_url || product.image || '',
      category: product.category || 'Others',
      food_type: product.food_type || product.foodType || 'veg',
      description: product.description || '',
      selling_price: price,
      price,
      stock_quantity:
        product.stock_quantity == null
          ? 999999
          : Number(product.stock_quantity || 0),
      is_active: Number(product.is_active ?? 1),
      availability: String(product.availability || product.status || 'available')
        .toLowerCase(),
    };
  };

  const resetCounterOrderForm = () => {
    setCounterSearch('');
    setCounterCategoryFilter('All');
    setCounterCategorySidebarOpen(false);
    setCounterOrderMode('dine_in');
    setCounterTableNumber('');
    setCounterCustomerName('');
    setCounterCustomerMobile('');
    setCounterCustomerSearch('');
    setCounterSelectedCustomerId('');
    setCounterCustomerPanel('');
    setCounterPaymentMethod('cash');
    setCounterCart([]);
    counterKeyboardProductIndexRef.current = -1;
    setCounterKeyboardProductIndex(-1);
    counterTableKeyboardBufferRef.current = '';
    window.clearTimeout(counterTableKeyboardResetTimerRef.current);
  };

  const fetchCounterProducts = async () => {
    if (!vendorId) return [];

    try {
      setCounterProductsLoading(true);

      const res = await axios.get(`${BASE_URL}/api/public/products`, {
        params: {
          vendorId,
          businessType: 'fastfood',
        },
      });

      const rawProducts = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.products)
          ? res.data.products
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      const normalized = rawProducts
        .map(normalizeCounterProduct)
        .filter((product) => {
          const active = product.is_active !== 0;
          const available = ![
            'unavailable',
            'inactive',
            'out_of_stock',
            'out-of-stock',
          ].includes(product.availability);

          return product.id && active && available;
        });

      setCounterProducts(normalized);

      return normalized;
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Counter order ke products load nahi hue.',
        'error',
        4200
      );

      return [];
    } finally {
      setCounterProductsLoading(false);
    }
  };

  const fetchCounterCustomers = async () => {
    const token = getToken();
    if (!token) return [];

    try {
      setCounterCustomersLoading(true);

      const res = await axios.get(`${STANDARD_ORDERS_API}/counter-customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const customers = Array.isArray(res?.data?.customers)
        ? res.data.customers
        : [];

      setCounterCustomers(customers);
      return customers;
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Saved customers load nahi hue.',
        'error',
        3600
      );
      return [];
    } finally {
      setCounterCustomersLoading(false);
    }
  };

  const selectCounterCustomer = (customerId) => {
    const normalizedId = String(customerId || '');
    setCounterSelectedCustomerId(normalizedId);

    if (!normalizedId) {
      setCounterCustomerName('');
      setCounterCustomerMobile('');
      return;
    }

    const customer = counterCustomers.find(
      (item) => String(item.id) === normalizedId
    );

    if (!customer) return;

    setCounterCustomerName(String(customer.customer_name || ''));
    setCounterCustomerMobile(
      String(customer.customer_mobile || '').replace(/\D/g, '').slice(0, 10)
    );
  };

  const saveCounterCustomer = async () => {
    const customerName = String(counterCustomerName || '').trim();
    const customerMobile = String(counterCustomerMobile || '')
      .replace(/\D/g, '')
      .slice(0, 10);

    if (!customerName) {
      showMessage('Customer save karne ke liye name enter karein.', 'error');
      counterCustomerNameInputRef.current?.focus?.();
      return;
    }

    if (counterCustomerMobile && customerMobile.length !== 10) {
      showMessage('Mobile blank rakhein ya valid 10-digit number enter karein.', 'error');
      counterCustomerMobileInputRef.current?.focus?.();
      return;
    }

    try {
      setCounterCustomerSaving(true);
      const token = getToken();
      const res = await axios.post(
        `${STANDARD_ORDERS_API}/counter-customers`,
        {
          customer_name: customerName,
          customer_mobile: customerMobile || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const savedCustomer = res?.data?.customer;

      if (savedCustomer?.id) {
        setCounterCustomers((current) => {
          const withoutSaved = current.filter(
            (item) => String(item.id) !== String(savedCustomer.id)
          );
          return [savedCustomer, ...withoutSaved].sort((a, b) =>
            String(a.customer_name || '').localeCompare(String(b.customer_name || ''))
          );
        });
        setCounterSelectedCustomerId(String(savedCustomer.id));
        setCounterCustomerName(String(savedCustomer.customer_name || customerName));
        setCounterCustomerMobile(String(savedCustomer.customer_mobile || customerMobile));
        setCounterCustomerPanel('');
      }

      showMessage(res?.data?.message || 'Customer saved.');
    } catch (err) {
      showMessage(
        err?.response?.data?.message || err?.message || 'Customer save nahi hua.',
        'error',
        4000
      );
    } finally {
      setCounterCustomerSaving(false);
    }
  };

  const openCounterOrder = async () => {
    if (!canAccess('counterOrder')) {
      showMessage(getStaffDeniedMessage('Counter Order'), 'error', 3600);
      return;
    }

    if (!vendorId) {
      showMessage('Vendor details load hone ke baad counter order create karein.', 'error');
      return;
    }

    setCounterOrderOpen(true);
    setCounterOrderMode('dine_in');
    setCounterTableNumber('');
    counterTableKeyboardBufferRef.current = '';
    counterKeyboardProductIndexRef.current = -1;
    setCounterKeyboardProductIndex(-1);

    if (!counterProducts.length) {
      await fetchCounterProducts();
    }

    await fetchCounterCustomers();

    /*
      Counter Order open hote hi Dine-in default rahega aur cursor
      Table Number option par rahega. Table select hote hi cursor product
      search box me chala jayega.
    */
    window.setTimeout(() => {
      counterTableSelectRef.current?.focus?.();
    }, 0);
    window.setTimeout(() => {
      counterTableSelectRef.current?.focus?.();
    }, 120);
  };

  const closeCounterOrder = () => {
    if (counterSaving) return;

    setCounterCategorySidebarOpen(false);
    setCounterOrderOpen(false);
    resetCounterOrderForm();
  };

  const addCounterProduct = (product) => {
    if (!product?.id) return;

    setCounterCart((prev) => {
      const existing = prev.find((item) => Number(item.id) === Number(product.id));

      if (existing) {
        return prev.map((item) =>
          Number(item.id) === Number(product.id)
            ? {
                ...item,
                quantity: Number(item.quantity || 0) + 1,
              }
            : item
        );
      }

      return [
        ...prev,
        {
          ...product,
          quantity: 1,
        },
      ];
    });
  };

  const decreaseCounterProduct = (productId) => {
    setCounterCart((prev) =>
      prev
        .map((item) =>
          Number(item.id) === Number(productId)
            ? {
                ...item,
                quantity: Math.max(0, Number(item.quantity || 0) - 1),
              }
            : item
        )
        .filter((item) => Number(item.quantity || 0) > 0)
    );
  };

  const removeCounterProduct = (productId) => {
    setCounterCart((prev) =>
      prev.filter((item) => Number(item.id) !== Number(productId))
    );
  };

  const counterCartSubtotal = counterCart.reduce(
    (sum, item) =>
      sum +
      Number(item.selling_price || item.price || 0) *
        Number(item.quantity || 0),
    0
  );

  const counterCategories = useMemo(() => {
    const categoryList = counterProducts
      .map((product) => String(product.category || 'Others').trim() || 'Others')
      .filter(Boolean);

    return ['All', ...Array.from(new Set(categoryList)).sort((a, b) => a.localeCompare(b))];
  }, [counterProducts]);

  const getCounterCategoryCount = (categoryName) => {
    if (categoryName === 'All') return counterProducts.length;
    return counterProducts.filter(
      (product) => String(product.category || 'Others') === String(categoryName)
    ).length;
  };

  const selectCounterCategory = (categoryName) => {
    setCounterCategoryFilter(categoryName);
    setCounterCategorySidebarOpen(false);
    counterKeyboardProductIndexRef.current = -1;
    setCounterKeyboardProductIndex(-1);
    focusCounterDrawer();
  };

  const invoiceCategories = counterCategories;

  const getInvoiceCategoryCount = (categoryName) => {
    if (categoryName === 'All') return counterProducts.length;
    return counterProducts.filter(
      (product) => String(product.category || 'Others') === String(categoryName)
    ).length;
  };

  const selectInvoiceCategory = (categoryName) => {
    setInvoiceCategoryFilter(categoryName);
    setInvoiceCategorySidebarOpen(false);
    invoiceKeyboardProductIndexRef.current = -1;
    setInvoiceKeyboardProductIndex(-1);
    window.setTimeout(() => {
      invoiceProductSearchInputRef.current?.focus?.();
    }, 0);
  };

  const getInvoicePickerOrder = () =>
    [...activeOrders, ...historyOrders].find(
      (order) => Number(order.id) === Number(invoiceProductPickerOrderId)
    ) || selectedOrder || null;

  const normalizeCounterSearchText = (value) =>
    String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const getCounterProductInitials = (value) =>
    normalizeCounterSearchText(value)
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word[0])
      .join('');

  const counterFilteredProducts = counterProducts.filter((product) => {
    const q = normalizeCounterSearchText(counterSearch);
    const productCategory = String(product.category || 'Others').trim() || 'Others';
    const categoryOk =
      counterCategoryFilter === 'All' ||
      productCategory === counterCategoryFilter;

    if (!categoryOk) return false;

    if (!q) return true;

    const rawPrice = Number(
      product.selling_price ?? product.price ?? 0
    );
    const price = Number.isFinite(rawPrice) ? rawPrice : 0;

    const roundedPrice = String(Math.round(price));
    const exactPrice = String(price);
    const productInitials = getCounterProductInitials(product.name);
    const categoryInitials = getCounterProductInitials(product.category);

    /*
      Counter product search supports:
      - Product name: Pearl Millet Pulao
      - Short initials: pmp
      - Price: 120
      - Category select + search together
      - Existing category / description search
    */
    const searchableText = normalizeCounterSearchText(
      [
        product.name,
        product.category,
        product.description,
        productInitials,
        categoryInitials,
        roundedPrice,
        exactPrice,
      ]
        .filter(Boolean)
        .join(' ')
    );

    return searchableText.includes(q);
  });


  const invoiceFilteredProducts = counterProducts.filter((product) => {
    const q = normalizeCounterSearchText(invoiceProductSearch);
    const productCategory = String(product.category || 'Others').trim() || 'Others';
    const categoryOk =
      invoiceCategoryFilter === 'All' ||
      productCategory === invoiceCategoryFilter;

    if (!categoryOk) return false;

    if (!q) return true;

    const rawPrice = Number(product.selling_price ?? product.price ?? 0);
    const price = Number.isFinite(rawPrice) ? rawPrice : 0;

    const roundedPrice = String(Math.round(price));
    const exactPrice = String(price);
    const productInitials = getCounterProductInitials(product.name);
    const categoryInitials = getCounterProductInitials(product.category);

    const searchableText = normalizeCounterSearchText(
      [
        product.name,
        product.category,
        product.description,
        productInitials,
        categoryInitials,
        roundedPrice,
        exactPrice,
      ]
        .filter(Boolean)
        .join(' ')
    );

    return searchableText.includes(q);
  });

  /*
    React global keydown listener kabhi purani render values capture kar sakta
    hai. Latest visible search results aur cart ko every render live refs me
    sync karte hain.
  */
  counterFilteredProductsRef.current = counterFilteredProducts;
  counterCartRef.current = counterCart;

  invoiceFilteredProductsRef.current = invoiceFilteredProducts;
  const setCounterKeyboardIndexSafe = (nextIndex, {
    scroll = true,
  } = {}) => {
    const visibleProducts = counterFilteredProductsRef.current || [];
    const total = visibleProducts.length;

    const safeIndex =
      total > 0
        ? ((Number(nextIndex ?? 0) % total) + total) % total
        : -1;

    counterKeyboardProductIndexRef.current = safeIndex;
    setCounterKeyboardProductIndex(safeIndex);

    if (scroll && safeIndex >= 0) {
      window.setTimeout(() => {
        const card = document.querySelector(
          `[data-counter-keyboard-product-index="${safeIndex}"]`
        );

        card?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 0);
    }

    return safeIndex;
  };

  useEffect(() => {
    const total = counterFilteredProducts.length;

    if (!total) {
      counterKeyboardProductIndexRef.current = -1;
      setCounterKeyboardProductIndex(-1);
      return;
    }

    /*
      Search text ya loaded products change hote hi first visible matching item
      automatically highlight hoga. Product cart me tabhi add hoga jab vendor
      Enter / + press kare ya Add button click kare.
    */
    counterKeyboardProductIndexRef.current = 0;
    setCounterKeyboardProductIndex(0);

    window.setTimeout(() => {
      const firstVisibleCard = document.querySelector(
        '[data-counter-keyboard-product-index="0"]'
      );

      firstVisibleCard?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 0);
  }, [counterSearch, counterCategoryFilter, counterProducts.length]);

  const getCounterKeyboardSelectedProduct = () => {
    const visibleProducts = counterFilteredProductsRef.current || [];
    const selectedIndex = Number(counterKeyboardProductIndexRef.current);

    if (selectedIndex < 0 || selectedIndex >= visibleProducts.length) {
      return null;
    }

    return visibleProducts[selectedIndex] || null;
  };

  const moveCounterKeyboardProduct = (direction) => {
    const visibleProducts = counterFilteredProductsRef.current || [];
    const total = visibleProducts.length;

    if (!total) {
      showMessage('Matching item nahi mila.', 'error');
      return;
    }

    const current = Number(counterKeyboardProductIndexRef.current);
    const nextIndex =
      current < 0
        ? direction < 0
          ? total - 1
          : 0
        : current + direction;

    setCounterKeyboardIndexSafe(nextIndex);
  };

  const focusCounterDrawer = () => {
    window.setTimeout(() => {
      counterOrderDrawerRef.current?.focus?.();
    }, 0);
  };

  const releaseCounterFieldFocus = (target) => {
    target?.blur?.();
    focusCounterDrawer();
  };

  const focusCounterSearch = ({
    selectAll = true,
  } = {}) => {
    window.setTimeout(() => {
      const input = counterSearchInputRef.current;

      input?.focus?.();

      if (selectAll) {
        input?.select?.();
      }
    }, 0);
  };

  const focusCounterTable = () => {
    counterTableKeyboardBufferRef.current = '';
    window.clearTimeout(counterTableKeyboardResetTimerRef.current);

    window.setTimeout(() => {
      counterTableSelectRef.current?.focus?.();
    }, 0);
  };

  const setCounterTableFromKeyboardDigit = (digit) => {
    const cleanDigit = String(digit || '').replace(/\D/g, '').slice(-1);

    if (!cleanDigit) return;

    const previousBuffer = String(counterTableKeyboardBufferRef.current || '');
    const appended = `${previousBuffer}${cleanDigit}`.replace(/^0+/, '');

    /*
      Tables 1-30 available hain.
      Example: D → 1 → 2 → Enter means Table 12.
      Buffer invalid ho jaaye (e.g. 39), to latest digit se fresh start hoga.
    */
    const nextValue =
      appended && Number(appended) >= 1 && Number(appended) <= 30
        ? appended
        : cleanDigit !== '0' && Number(cleanDigit) <= 30
          ? cleanDigit
          : '';

    if (!nextValue) return;

    counterTableKeyboardBufferRef.current = nextValue;
    setCounterTableNumber(nextValue);

    window.clearTimeout(counterTableKeyboardResetTimerRef.current);
    counterTableKeyboardResetTimerRef.current = window.setTimeout(() => {
      counterTableKeyboardBufferRef.current = '';
    }, 1100);
  };

  const removeLastCounterTableKeyboardDigit = () => {
    const current = String(counterTableKeyboardBufferRef.current || '');
    const nextValue = current.slice(0, -1);

    counterTableKeyboardBufferRef.current = nextValue;
    setCounterTableNumber(nextValue);
  };

  const confirmCounterTableAndFocusSearch = () => {
    const selectedTable = String(
      counterTableKeyboardBufferRef.current || counterTableNumber || ''
    ).trim();

    if (!selectedTable) {
      showMessage('Table number select karein.', 'error');
      return;
    }

    /*
      Digit press ke turant baad Enter aaye to React render ka wait nahi karenge.
      Ref me stored latest typed table number ko final state me commit kar denge.
    */
    setCounterTableNumber(selectedTable);
    counterTableKeyboardBufferRef.current = '';
    window.clearTimeout(counterTableKeyboardResetTimerRef.current);
    focusCounterSearch({ selectAll: false });
  };

  const focusCounterCustomerName = () => {
    setCounterCustomerPanel('add');
    window.setTimeout(() => {
      counterCustomerNameInputRef.current?.focus?.();
    }, 0);
  };

  const focusCounterCustomerMobile = () => {
    setCounterCustomerPanel('add');
    window.setTimeout(() => {
      counterCustomerMobileInputRef.current?.focus?.();
    }, 0);
  };

  const addHighlightedCounterProduct = () => {
    const product = getCounterKeyboardSelectedProduct();

    if (!product) {
      showMessage('Arrow key se jis item ko highlight karein, Enter se sirf wahi item add hoga.', 'error');
      return;
    }

    addCounterProduct(product);
  };

  const decreaseHighlightedCounterProduct = () => {
    const product = getCounterKeyboardSelectedProduct();

    if (!product) {
      showMessage('Quantity kam karne ke liye matching item select karein.', 'error');
      return;
    }

    const existing = (counterCartRef.current || []).find(
      (item) => Number(item.id) === Number(product.id)
    );

    if (!existing) {
      showMessage(`${product.name} cart me add nahi hai.`, 'error');
      return;
    }

    decreaseCounterProduct(product.id);
  };

  const setInvoiceKeyboardIndexSafe = (nextIndex, { scroll = true } = {}) => {
    const visibleProducts = invoiceFilteredProductsRef.current || [];
    const total = visibleProducts.length;

    const safeIndex =
      total > 0
        ? ((Number(nextIndex ?? 0) % total) + total) % total
        : -1;

    invoiceKeyboardProductIndexRef.current = safeIndex;
    setInvoiceKeyboardProductIndex(safeIndex);

    if (scroll && safeIndex >= 0) {
      window.setTimeout(() => {
        const card = document.querySelector(
          `[data-invoice-keyboard-product-index="${safeIndex}"]`
        );

        card?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 0);
    }

    return safeIndex;
  };

  useEffect(() => {
    const total = invoiceFilteredProducts.length;

    if (!total) {
      invoiceKeyboardProductIndexRef.current = -1;
      setInvoiceKeyboardProductIndex(-1);
      return;
    }

    invoiceKeyboardProductIndexRef.current = 0;
    setInvoiceKeyboardProductIndex(0);

    window.setTimeout(() => {
      const firstVisibleCard = document.querySelector(
        '[data-invoice-keyboard-product-index="0"]'
      );

      firstVisibleCard?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 0);
  }, [invoiceProductSearch, counterProducts.length]);

  const getInvoiceKeyboardSelectedProduct = () => {
    const visibleProducts = invoiceFilteredProductsRef.current || [];
    const selectedIndex = Number(invoiceKeyboardProductIndexRef.current);

    if (selectedIndex < 0 || selectedIndex >= visibleProducts.length) {
      return null;
    }

    return visibleProducts[selectedIndex] || null;
  };

  const moveInvoiceKeyboardProduct = (direction) => {
    const visibleProducts = invoiceFilteredProductsRef.current || [];
    const total = visibleProducts.length;

    if (!total) {
      showMessage('Invoice edit me matching product nahi mila.', 'error');
      return;
    }

    const current = Number(invoiceKeyboardProductIndexRef.current);
    const nextIndex =
      current < 0
        ? direction < 0
          ? total - 1
          : 0
        : current + direction;

    setInvoiceKeyboardIndexSafe(nextIndex);
  };

  const focusInvoiceProductSearch = ({ selectAll = true } = {}) => {
    const focus = () => {
      const input = invoiceProductSearchInputRef.current;

      input?.focus?.();

      if (selectAll) {
        input?.select?.();
      }
    };

    window.setTimeout(focus, 0);
    window.setTimeout(focus, 90);
    window.setTimeout(focus, 220);
  };

  const focusInvoiceEditControl = (controlKey = 'discount') => {
    const focus = () => {
      const orderKeys = invoiceEditControlOrderRef.current || [];
      const preferredIndex = Math.max(0, orderKeys.indexOf(controlKey));
      const orderedKeys = [
        ...orderKeys.slice(preferredIndex),
        ...orderKeys.slice(0, preferredIndex),
      ];

      const nextKey = orderedKeys.find((keyName) => {
        const node = invoiceEditControlRefs.current?.[keyName];
        return node && !node.disabled;
      });

      const node = nextKey ? invoiceEditControlRefs.current?.[nextKey] : null;

      if (!node) {
        window.focus();
        return;
      }

      node.focus?.({ preventScroll: true });

      if (
        typeof node.select === 'function' &&
        !['checkbox', 'select-one'].includes(String(node.type || '').toLowerCase())
      ) {
        node.select();
      }

      node.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    };

    window.setTimeout(focus, 0);
    window.setTimeout(focus, 90);
  };

  const moveInvoiceEditControl = (direction, target = null) => {
    const orderKeys = (invoiceEditControlOrderRef.current || []).filter((keyName) => {
      const node = invoiceEditControlRefs.current?.[keyName];
      return node && !node.disabled;
    });

    if (!orderKeys.length) {
      window.focus();
      return;
    }

    const currentKey =
      target?.dataset?.invoiceEditControl ||
      orderKeys.find((keyName) => invoiceEditControlRefs.current?.[keyName] === document.activeElement) ||
      orderKeys[0];

    const currentIndex = Math.max(0, orderKeys.indexOf(currentKey));
    const nextIndex =
      ((currentIndex + direction) % orderKeys.length + orderKeys.length) %
      orderKeys.length;

    focusInvoiceEditControl(orderKeys[nextIndex]);
  };

  const releaseInvoiceProductSearchFocus = ({
    clearSearch = false,
    focusFirstControl = false,
  } = {}) => {
    const input = invoiceProductSearchInputRef.current;

    if (input && document.activeElement === input) {
      input.blur();
    }

    if (clearSearch) {
      setInvoiceProductSearch('');
      invoiceKeyboardProductIndexRef.current = -1;
      setInvoiceKeyboardProductIndex(-1);
    }

    if (focusFirstControl) {
      focusInvoiceEditControl('discount');
      return;
    }

    window.focus();
  };

  const releaseInvoiceKeyboardFocus = ({
    clearSearch = false,
  } = {}) => {
    /*
      Invoice edit mode me Discount / GST / Delivery / Packing / Round
      jaise input par cursor active rahe to Alt + A / Alt + H / Alt + Q
      action chalne ke baad bhi caret stuck dikh sakta hai. Global shortcut
      run hone se pehle current invoice input ko force blur karte hain.
    */
    releaseInvoiceProductSearchFocus({ clearSearch });

    const activeElement = document.activeElement;

    if (
      activeElement &&
      activeElement !== document.body &&
      activeElement?.dataset?.invoiceEditControl &&
      typeof activeElement.blur === 'function'
    ) {
      activeElement.blur();
    }

    window.focus();
  };

  const getInvoiceProductManualItemId = (productId) =>
    `invoice_product_${Number(productId || 0)}`;

  const addInvoiceProductToManualItems = (order, product) => {
    if (!order || !product?.id) return;

    if (order.locked) {
      showMessage('Completed orders are read-only.', 'error');
      return;
    }

    const productId = Number(product.id);
    const manualId = getInvoiceProductManualItemId(productId);
    const price = Number(product.selling_price ?? product.price ?? 0);

    if (!Number.isFinite(price) || price <= 0) {
      showMessage('Selected product ka price valid nahi hai.', 'error');
      return;
    }

    setInvoiceStateForOrder(order.id, (prev) => {
      const existingItems = prev.manualItems || [];
      const existing = existingItems.find((item) => item.id === manualId);

      if (existing) {
        return {
          ...prev,
          manualItems: existingItems.map((item) =>
            item.id === manualId
              ? {
                  ...item,
                  quantity: Number(item.quantity || 0) + 1,
                  qty: Number(item.quantity || 0) + 1,
                  total: (Number(item.quantity || 0) + 1) * Number(item.price || item.rate || price),
                }
              : item
          ),
        };
      }

      return {
        ...prev,
        manualItems: [
          ...existingItems,
          {
            id: manualId,
            productId,
            product_id: productId,
            item_name: product.name || 'Food Item',
            name: product.name || 'Food Item',
            quantity: 1,
            qty: 1,
            price,
            rate: price,
            total: price,
            source_type: 'manual',
            manual_added_at: new Date().toISOString(),
            image: product.image || product.image_url || '',
            category: product.category || 'Others',
          },
        ],
      };
    });
  };

  const decreaseInvoiceProductFromManualItems = (order, product) => {
    if (!order || !product?.id) return;

    if (order.locked) {
      showMessage('Completed orders are read-only.', 'error');
      return;
    }

    const manualId = getInvoiceProductManualItemId(product.id);

    setInvoiceStateForOrder(order.id, (prev) => ({
      ...prev,
      manualItems: (prev.manualItems || [])
        .map((item) =>
          item.id === manualId
            ? {
                ...item,
                quantity: Math.max(0, Number(item.quantity || 0) - 1),
                qty: Math.max(0, Number(item.quantity || 0) - 1),
                total:
                  Math.max(0, Number(item.quantity || 0) - 1) *
                  Number(item.price || item.rate || 0),
              }
            : item
        )
        .filter((item) => Number(item.quantity || 0) > 0),
    }));
  };

  const addHighlightedInvoiceProduct = () => {
    const order = getLatestKeyboardOrder();

    if (!order) {
      showMessage('Invoice edit ke liye order select karein.', 'error');
      return;
    }

    const product = getInvoiceKeyboardSelectedProduct();

    if (!product) {
      showMessage('Invoice product search me item select karein.', 'error');
      return;
    }

    addInvoiceProductToManualItems(order, product);
  };

  const decreaseHighlightedInvoiceProduct = () => {
    const order = getLatestKeyboardOrder();

    if (!order) {
      showMessage('Invoice edit ke liye order select karein.', 'error');
      return;
    }

    const product = getInvoiceKeyboardSelectedProduct();

    if (!product) {
      showMessage('Invoice product search me item select karein.', 'error');
      return;
    }

    decreaseInvoiceProductFromManualItems(order, product);
  };

  const openInvoiceProductPicker = async (order) => {
    if (!canAccess('invoiceEdit')) {
      showMessage(getStaffDeniedMessage('Invoice Edit'), 'error', 3600);
      return;
    }

    if (!order) {
      showMessage('Invoice me item add karne ke liye order select karein.', 'error');
      return;
    }

    if (activeTab !== 'active' || order.locked) {
      showMessage('History / completed invoice read-only hai.', 'error');
      return;
    }

    if (!counterProducts.length) {
      await fetchCounterProducts();
    }

    setInvoiceProductPickerOrderId(order.id);
    setInvoiceProductPickerOpen(true);
    setInvoiceCategoryFilter('All');
    setInvoiceCategorySidebarOpen(false);
    setInvoiceProductSearch('');
    invoiceKeyboardProductIndexRef.current = 0;
    setInvoiceKeyboardProductIndex(0);

    window.setTimeout(() => {
      invoiceProductSearchInputRef.current?.focus?.();
    }, 120);
  };

  const closeInvoiceProductPicker = () => {
    setInvoiceProductPickerOpen(false);
    setInvoiceProductPickerOrderId(null);
    setInvoiceCategorySidebarOpen(false);
    setInvoiceProductSearch('');
    invoiceKeyboardProductIndexRef.current = -1;
    setInvoiceKeyboardProductIndex(-1);
  };

  const openInvoiceEditMode = async (order) => {
    if (!canAccess('invoiceEdit')) {
      showMessage(getStaffDeniedMessage('Invoice Edit'), 'error', 3600);
      return;
    }

    if (!order) {
      showMessage('Invoice edit ke liye order select karein.', 'error');
      return;
    }

    if (activeTab !== 'active' || order.locked) {
      showMessage('History / completed invoice read-only hai.', 'error');
      return;
    }

    if (!counterProducts.length) {
      await fetchCounterProducts();
    }

    setInvoiceProductSearch('');
    invoiceKeyboardProductIndexRef.current = 0;
    setInvoiceKeyboardProductIndex(0);
    openDetail(order);
    await openInvoiceProductPicker(order);
    showMessage('Invoice item picker open. Category select karke product add karein.');
  };


  const createCounterOrder = async () => {
    if (counterSaving) return;

    if (!counterCart.length) {
      showMessage('Counter order me kam se kam ek item add karein.', 'error');
      return;
    }

    if (counterOrderMode === 'dine_in' && !String(counterTableNumber || '').trim()) {
      showMessage('Dine-in counter order ke liye table number select karein.', 'error');
      return;
    }

    const mobile = String(counterCustomerMobile || '')
      .replace(/\D/g, '')
      .slice(0, 10);

    if (counterCustomerMobile && mobile.length !== 10) {
      showMessage('Mobile number blank rakhein ya valid 10-digit number enter karein.', 'error');
      return;
    }

    const products = counterCart.map((item) => ({
      type: 'product',
      productId: Number(item.productId || item.id),
      id: Number(item.productId || item.id),
      name: item.name,
      price: Number(item.selling_price || item.price || 0),
      selling_price: Number(item.selling_price || item.price || 0),
      quantity: Number(item.quantity || 0),
      image: item.image || item.image_url || '',
      category: item.category || 'Others',
      food_type: item.food_type || 'veg',
      description: item.description || '',
      line_total:
        Number(item.selling_price || item.price || 0) *
        Number(item.quantity || 0),
    }));

    try {
      setCounterSaving(true);

      const token = getToken();

      const res = await axios.post(
        `${STANDARD_ORDERS_API}/counter-order`,
        {
          customer_number: mobile || null,
          customer_name: String(counterCustomerName || '').trim() || null,
          customer_mobile: mobile || null,
          products,
          order_type: 'individual',
          order_mode: counterOrderMode,
          table_number:
            counterOrderMode === 'dine_in'
              ? String(counterTableNumber || '').trim()
              : null,
          payment_method: counterPaymentMethod,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCounterOrderOpen(false);
      resetCounterOrderForm();

      showMessage(
        res?.data?.message ||
          `Counter order #${res?.data?.orderId || ''} created successfully.`
      );

      await fetchOrders({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.details ||
          err?.message ||
          'Counter order create nahi hua.',
        'error',
        4200
      );
    } finally {
      setCounterSaving(false);
    }
  };

  const getDeliveryBoyLoginLink = () => {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://vakra.co.in';

    const cleanVendorType = String(vendorType || 'fastfood').toLowerCase();

    return `${origin}/delivery-boy?vendor=${vendorId || ''}&vendorType=${encodeURIComponent(
      cleanVendorType
    )}`;
  };

  const copyDeliveryBoyLoginLink = async () => {
    const link = getDeliveryBoyLoginLink();

    if (!vendorId) {
      showMessage('Vendor details load hone ke baad link copy hoga.', 'error');
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const input = document.createElement('input');
        input.value = link;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      showMessage('Delivery boy login link copied.');
    } catch {
      showMessage('Link copy failed. Please manually copy karein.', 'error');
    }
  };

  const shareDeliveryBoyOnWhatsApp = (rider = null) => {
    if (!vendorId) {
      showMessage('Vendor details load hone ke baad WhatsApp share hoga.', 'error');
      return;
    }

    const link = getDeliveryBoyLoginLink();
    const riderLine = rider?.name
      ? `Delivery Boy: ${rider.name}%0AMobile: ${rider.mobile || ''}%0A`
      : '';

    const messageText = `Delivery Boy Login%0A${riderLine}Link: ${encodeURIComponent(
      link
    )}%0A%0AIs link ko open karke mobile number aur PIN se login karein.`;

    window.open(`https://wa.me/?text=${messageText}`, '_blank', 'noopener,noreferrer');
  };

  const renderDeliveryLoginLinkActions = (rider = null) => (
    <div className="dd-rider-link-actions">
      <button
        type="button"
        className="copy"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          copyDeliveryBoyLoginLink();
        }}
      >
        Copy Link
      </button>

      <button
        type="button"
        className="whatsapp"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          shareDeliveryBoyOnWhatsApp(rider);
        }}
      >
        WhatsApp
      </button>
    </div>
  );

  const toNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const sanitizeGstInput = (value) => {
    const raw = String(value ?? '')
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');

    if (raw === '') return '';

    const [whole = '', decimal = ''] = raw.split('.');
    const cleanWhole = whole.slice(0, 3);
    const cleanDecimal = decimal.slice(0, 2);

    return raw.includes('.')
      ? `${cleanWhole}.${cleanDecimal}`
      : cleanWhole;
  };

  const formatMoney = (value) =>
    `₹${Math.round(toNumber(value, 0)).toLocaleString('en-IN')}`;

  const formatTime = (value) => {
    if (!value) return 'Just now';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Just now';

    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  const toDateInputValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const getOrderHistoryDateValue = (order) =>
    toDateInputValue(
      order?.cancelled_at ||
        order?.cancelledAt ||
        order?.completed_at ||
        order?.updated_at ||
        order?.created_at ||
        order?.invoice_saved_at
    );

  const matchesHistoryDateFilter = (order) => {
    if (activeTab !== 'history') return true;

    const filterType = historyDateFilter.type || 'all';
    if (filterType === 'all') return true;

    const orderDate = getOrderHistoryDateValue(order);
    if (!orderDate) return false;

    const now = new Date();
    const today = toDateInputValue(now);

    const yesterdayDate = new Date(now);
    yesterdayDate.setDate(now.getDate() - 1);
    const yesterday = toDateInputValue(yesterdayDate);

    const last7Date = new Date(now);
    last7Date.setDate(now.getDate() - 6);
    const last7Start = toDateInputValue(last7Date);

    if (filterType === 'today') return orderDate === today;
    if (filterType === 'yesterday') return orderDate === yesterday;
    if (filterType === 'last7') return orderDate >= last7Start && orderDate <= today;
    if (filterType === 'custom') {
      const from = historyDateFilter.customFrom || '';
      const to = historyDateFilter.customTo || '';

      if (from && to) return orderDate >= from && orderDate <= to;
      if (from) return orderDate >= from;
      if (to) return orderDate <= to;

      return true;
    }

    return true;
  };

  const getHistoryFilterLabel = () => {
    if (historyDateFilter.type === 'today') return 'Today';
    if (historyDateFilter.type === 'yesterday') return 'Yesterday';
    if (historyDateFilter.type === 'last7') return 'Last 7 days';
    if (historyDateFilter.type === 'custom') {
      const from = historyDateFilter.customFrom || '';
      const to = historyDateFilter.customTo || '';

      if (from && to) return `${from} to ${to}`;
      if (from) return `From ${from}`;
      if (to) return `Till ${to}`;

      return 'Custom range';
    }

    return 'All dates';
  };


  const safeJsonParse = (value, fallback = []) => {
    try {
      if (!value) return fallback;
      if (Array.isArray(value)) return value;
      if (typeof value === 'object') return value;
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const getImageUrl = (value) => {
    if (!value) return 'https://via.placeholder.com/64?text=Food';
    const raw = Array.isArray(value) ? value[0] : value;
    const text = String(raw || '').trim();
    if (!text) return 'https://via.placeholder.com/64?text=Food';
    if (/^https?:\/\//i.test(text)) return text;
    return `${BASE_URL}/${text.replace(/^\/+/, '')}`;
  };

  const getDbQueryParam = () => {
    const type = String(vendorType || 'fastfood').toLowerCase();

    if (type === 'cosmetic') return 'cods=true';
    if (type === 'garments' || type === 'garment') return 'grdb=true';

    return 'userdb=true';
  };

  const normalizeOrderMode = (order) => {
    const raw = String(order?.order_mode || order?.orderMode || '').toLowerCase();

    if (raw === 'delivery') return 'delivery';
    if (raw === 'takeaway' || raw === 'pickup') return 'takeaway';
    if (raw === 'dine_in' || raw === 'dine-in') return 'dine_in';

    return order?.table_number ? 'dine_in' : 'takeaway';
  };

  const normalizeStatus = (order) => {
    /*
      Cancellation ko sabse pehle resolve karenge because current DB orders.status
      enum cancelled store nahi karta. Backend status log / cancelled_at /
      bill_status cancellation ka permanent source ho sakta hai.
    */
    const explicitCancelValues = [
      order?.display_status,
      order?.displayStatus,
      order?.order_status,
      order?.latest_status,
      order?.latestStatus,
      order?.bill_status,
      order?.billStatus,
      order?.delivery_status,
      order?.deliveryStatus,
    ]
      .map((value) => String(value || '').toLowerCase().trim())
      .filter(Boolean);

    const hasCancelledMarker =
      Boolean(order?.cancelled_at || order?.cancelledAt) ||
      explicitCancelValues.some((value) =>
        ['cancelled', 'canceled'].includes(value)
      );

    if (hasCancelledMarker) return 'cancelled';

    /*
      IMPORTANT: backend orders.status enum currently pending / preparing / done
      store karta hai. Accepted aur Ready exact UI stages status logs se aate hain.
    */
    const raw = String(
      order?.display_status ||
        order?.displayStatus ||
        order?.order_status ||
        order?.latest_status ||
        order?.latestStatus ||
        order?.status ||
        ''
    )
      .toLowerCase()
      .trim();

    if (raw === 'pending' || raw === 'new' || raw === 'placed') return 'new';
    if (raw === 'accepted' || raw === 'confirm' || raw === 'confirmed') return 'accepted';
    if (raw === 'preparing' || raw === 'packing' || raw === 'kitchen') return 'preparing';
    if (raw === 'ready' || raw === 'ready_for_pickup') return 'ready';
    if (raw === 'done' || raw === 'completed' || raw === 'complete' || raw === 'delivered') return 'done';
    if (raw === 'cancelled' || raw === 'canceled') return 'cancelled';

    return 'new';
  };

  const normalizePaymentStatus = (order) => {
    const status = String(order?.payment_status || order?.paymentStatus || '').toLowerCase();
    const method = String(order?.payment_method || order?.paymentMethod || '').toLowerCase();

    if (status === 'paid') return 'paid';
    if (status === 'partial') return 'partial';
    if (status === 'cod') return 'cod';
    if (status === 'credit') return 'unpaid';
    if (status === 'unpaid') return 'unpaid';

    if (method === 'razorpay' || method === 'online' || method === 'upi') return 'paid';
    if (method === 'cod') return 'cod';

    return 'unpaid';
  };

  const getFullAddress = (order) =>
    [
      order?.address_line1,
      order?.address_line2,
      order?.landmark,
      order?.city,
      order?.state,
      order?.pincode,
    ]
      .filter(Boolean)
      .join(', ');

  const normalizeOrder = (order) => {
    const products = safeJsonParse(order?.productsParsed || order?.products, []);
    const displayStatus = normalizeStatus(order);
    const displayMode = normalizeOrderMode(order);

    const productsTotal = products.reduce((sum, item) => {
      const qty = toNumber(item?.quantity || item?.qty, 1);
      const rate = toNumber(item?.price ?? item?.selling_price ?? item?.rate, 0);
      const lineTotal = toNumber(item?.line_total, qty * rate);
      return sum + lineTotal;
    }, 0);

    const customerName =
      order?.customer_name ||
      order?.customerName ||
      order?.delivery_details?.customer_name ||
      order?.name ||
      'Walk-in Customer';

    const customerMobile =
      order?.customer_mobile ||
      order?.customerMobile ||
      order?.customer_number ||
      order?.delivery_details?.customer_mobile ||
      '';

    const customerNote = String(
      order?.customer_note ||
        order?.order_note ||
        order?.special_instructions ||
        order?.specialInstructions ||
        ''
    ).trim();

    const kotSent = Boolean(order?.kot_id || order?.kot_no || order?.kot_status);
    const counterCreated = isCounterCreatedOrder(order);
    const runningTable =
      displayMode === 'dine_in' && !['done', 'cancelled'].includes(displayStatus);

    return {
      ...order,
      id: order?.id || order?.order_id,
      productsParsed: products,
      displayStatus,
      displayMode,
      displayPaymentStatus: normalizePaymentStatus(order),
      customerName,
      customerMobile,
      customerNote,
      fullAddress: order?.full_address || getFullAddress(order),
      table_number:
        order?.table_number ||
        order?.tableNumber ||
        order?.table_no ||
        order?.tableNo ||
        order?.table ||
        '',
      displayTotal: toNumber(
        order?.display_total ?? order?.final_total ?? order?.bill_total ?? order?.total_price,
        productsTotal
      ),
      coinsRedeemed: Math.max(
        0,
        Math.floor(toNumber(order?.coins_redeemed ?? order?.coinsRedeemed, 0))
      ),
      coinDiscountAmount:
        String(order?.coin_redeem_status || order?.coinRedeemStatus || 'none').toLowerCase() === 'applied'
          ? Math.max(
              0,
              toNumber(order?.coin_discount_amount ?? order?.coinDiscountAmount, 0)
            )
          : 0,
      coinRedeemStatus: String(
        order?.coin_redeem_status || order?.coinRedeemStatus || 'none'
      ).toLowerCase(),
      itemCount: products.reduce((sum, item) => sum + toNumber(item?.quantity || item?.qty, 1), 0),
      kotSent,
      counterCreated,
      runningTable,
      locked:
        ['done', 'cancelled'].includes(displayStatus) ||
        ['completed', 'cancelled'].includes(String(order?.bill_status || '').toLowerCase()),
    };
  };

  const uniqueById = (orders) => {
    const map = new Map();
    (orders || []).forEach((order) => {
      const id = order?.id || order?.order_id;
      if (!id) return;
      map.set(Number(id), order);
    });
    return Array.from(map.values());
  };

  const buildSignature = (orders) =>
    JSON.stringify(
      orders.map((order) => ({
        id: order.id,
        status: order.displayStatus,
        total: order.displayTotal,
        updated: order.updated_at || order.created_at,
        count: order.itemCount,
      }))
    );

  const getInvoiceState = (orderId) => {
    if (!orderId) return initialInvoiceState;
    return invoiceByOrder[orderId] || initialInvoiceState;
  };

  const setInvoiceStateForOrder = (orderId, updater) => {
    if (!orderId) return;

    setInvoiceByOrder((prev) => {
      const current = prev[orderId] || initialInvoiceState;
      return {
        ...prev,
        [orderId]: typeof updater === 'function' ? updater(current) : updater,
      };
    });
  };

  const normalizeSavedBillManualItems = (order) => {
    const billItems =
      order?.billItems ||
      order?.bill_items ||
      order?.invoiceItems ||
      order?.invoice_items ||
      order?.bill?.items ||
      order?.bill?.bill_items ||
      order?.invoice?.items ||
      [];

    if (!Array.isArray(billItems)) return [];

    return billItems
      .filter((item) => {
        const source = String(item?.source_type || item?.sourceType || '').toLowerCase();
        return source && source !== 'ordered';
      })
      .map((item) => {
        const productId = Number(item?.product_id || item?.productId || 0) || null;
        const qty = Math.max(1, toNumber(item?.quantity || item?.qty, 1));
        const rate = toNumber(item?.rate ?? item?.price ?? item?.selling_price, 0);
        const stableId = productId
          ? getInvoiceProductManualItemId(productId)
          : `manual_saved_${item?.id || item?.item_name || Date.now()}`;

        return {
          id: stableId,
          saved_item_id: item?.id || null,
          productId,
          product_id: productId,
          item_name: item?.item_name || item?.name || 'Manual Item',
          name: item?.item_name || item?.name || 'Manual Item',
          quantity: qty,
          qty,
          price: rate,
          rate,
          total: toNumber(item?.line_total ?? item?.total, qty * rate),
          source_type: item?.source_type || 'manual',
          image: item?.image || item?.image_url || '',
          category: item?.category || 'Manual',
        };
      });
  };

  const initInvoiceForOrder = (order) => {
    if (!order?.id) return;

    setInvoiceByOrder((prev) => {
      if (prev[order.id]) return prev;

      const paymentStatus = normalizePaymentStatus(order);
      const total = toNumber(order?.displayTotal || order?.total_price, 0);
      const isDeliveryOrder = normalizeOrderMode(order) === 'delivery';

      /*
        Fast-food delivery rule:
        - New delivery order default COD pending rahega.
        - Token verify hone ke baad backend COD ko Paid karega.
        - Vendor manually Credit / Partial / Paid select kare to selected state preserve hogi.
      */
      const invoicePaymentStatus =
        isDeliveryOrder && paymentStatus === 'unpaid'
          ? 'cod'
          : paymentStatus === 'unpaid'
            ? 'paid'
            : paymentStatus;

      const invoicePaymentMethod =
        invoicePaymentStatus === 'cod'
          ? 'cod'
          : invoicePaymentStatus === 'unpaid'
            ? 'credit'
            : String(order?.payment_method || 'cash').toLowerCase();

      const savedBillGstValue =
        order?.bill?.gst_percent ??
        order?.invoice?.gst_percent;

      const hasSavedBillGst =
        savedBillGstValue !== undefined &&
        savedBillGstValue !== null &&
        savedBillGstValue !== '';

      const configuredDefaultPercent = Math.max(
        0,
        toNumber(
          billingSettingsRef.current?.defaultGstPercent,
          5
        )
      );

      const savedBillGstPercent = Math.max(
        0,
        toNumber(savedBillGstValue, 0)
      );

      const gstEnabled = hasSavedBillGst
        ? savedBillGstPercent > 0
        : Boolean(billingSettingsRef.current?.gstEnabled);

      const gstPercent = gstEnabled
        ? String(
            hasSavedBillGst
              ? savedBillGstPercent
              : configuredDefaultPercent
          )
        : '';

      const savedManualItems = normalizeSavedBillManualItems(order);
      const savedDiscountType = String(
        order?.bill?.discount_type ||
          order?.invoice?.discount_type ||
          'amount'
      ).toLowerCase() === 'percentage'
        ? 'percentage'
        : 'amount';
      const storedDiscountValue = toNumber(
        order?.bill?.discount_value ?? order?.invoice?.discount_value,
        0
      );
      const storedDiscountAmount = toNumber(
        order?.bill?.manual_discount ?? order?.invoice?.manual_discount,
        0
      );
      const savedDiscountValue =
        savedDiscountType === 'percentage'
          ? storedDiscountValue
          : storedDiscountValue > 0
            ? storedDiscountValue
            : storedDiscountAmount;

      return {
        ...prev,
        [order.id]: {
          ...initialInvoiceState,
          deliveryCharge: String(toNumber(order?.delivery_charge, 0) || ''),
          packingCharge: String(toNumber(order?.packing_charge, 0) || ''),
          gstEnabled,
          gstPercent,
          gstTouched: false,
          paymentStatus: invoicePaymentStatus,
          paymentMethod: invoicePaymentMethod,
          amountPaid: invoicePaymentStatus === 'paid' ? String(total) : '',
          manualItems: savedManualItems,
          discountType: savedDiscountType,
          discount: String(toNumber(savedDiscountValue, 0) || ''),
        },
      };
    });
  };

  const calculateInvoice = (order) => {
    const invoice = getInvoiceState(order?.id);
    const products = order?.productsParsed || [];

    const itemsTotal = products.reduce((sum, item) => {
      const qty = toNumber(item?.quantity || item?.qty, 1);
      const rate = toNumber(item?.price ?? item?.selling_price ?? item?.rate, 0);
      return sum + qty * rate;
    }, 0);

    const manualTotal = (invoice.manualItems || []).reduce((sum, item) => {
      const qty = toNumber(item.quantity, 0);
      const rate = toNumber(item.price, 0);
      return sum + qty * rate;
    }, 0);

    const subtotal = itemsTotal + manualTotal;
    const discountType = String(invoice.discountType || 'amount').toLowerCase() === 'percentage'
      ? 'percentage'
      : 'amount';
    const discountValue = discountType === 'percentage'
      ? Math.min(100, Math.max(0, toNumber(invoice.discount, 0)))
      : Math.max(0, toNumber(invoice.discount, 0));
    const discount = Math.min(
      subtotal,
      discountType === 'percentage'
        ? (subtotal * discountValue) / 100
        : discountValue
    );
    const coinRedeemStatus = String(
      order?.coinRedeemStatus ||
        order?.coin_redeem_status ||
        'none'
    ).toLowerCase();
    const requestedCoinDiscount =
      coinRedeemStatus === 'applied'
        ? Math.max(
            0,
            toNumber(
              order?.coinDiscountAmount ??
                order?.coin_discount_amount,
              0
            )
          )
        : 0;
    const coinDiscount = Math.min(
      Math.max(0, subtotal - discount),
      requestedCoinDiscount
    );
    const taxable = Math.max(0, subtotal - discount - coinDiscount);
    const deliveryCharge = Math.max(0, toNumber(invoice.deliveryCharge, 0));
    const packingCharge = Math.max(0, toNumber(invoice.packingCharge, 0));
    const gstEnabled = Boolean(invoice.gstEnabled);
    const gstPercent = gstEnabled
      ? Math.max(0, toNumber(invoice.gstPercent, 0))
      : 0;
    const gstTotal = gstEnabled
      ? (taxable * gstPercent) / 100
      : 0;
    const roundOff = toNumber(invoice.roundOff, 0);

    const finalTotal = Math.max(0, taxable + deliveryCharge + packingCharge + gstTotal + roundOff);

    let paymentStatus = String(invoice.paymentStatus || 'paid').toLowerCase();
    let paymentMethod = String(invoice.paymentMethod || 'cash').toLowerCase();
    let amountPaid = toNumber(invoice.amountPaid, 0);

    if (paymentStatus === 'paid') {
      amountPaid = finalTotal;
      if (!paymentMethod || paymentMethod === 'credit' || paymentMethod === 'cod') {
        paymentMethod = 'cash';
      }
    } else if (paymentStatus === 'partial') {
      amountPaid = Math.max(0, Math.min(amountPaid, finalTotal));
      if (amountPaid >= finalTotal) {
        paymentStatus = 'paid';
        amountPaid = finalTotal;
      }
      if (!paymentMethod || paymentMethod === 'credit' || paymentMethod === 'cod') {
        paymentMethod = 'cash';
      }
    } else if (paymentStatus === 'cod') {
      paymentMethod = 'cod';
      amountPaid = 0;
    } else {
      paymentStatus = 'unpaid';
      paymentMethod = 'credit';
      amountPaid = 0;
    }

    const dueAmount = Math.max(0, finalTotal - amountPaid);

    return {
      itemsTotal,
      manualTotal,
      subtotal,
      discount,
      discountType,
      discountValue,
      coinsRedeemed: Math.max(
        0,
        Math.floor(
          toNumber(order?.coinsRedeemed ?? order?.coins_redeemed, 0)
        )
      ),
      coinDiscount,
      coinRedeemStatus,
      taxable,
      deliveryCharge,
      packingCharge,
      gstEnabled,
      gstPercent,
      gstTotal,
      roundOff,
      finalTotal,
      paymentStatus,
      paymentMethod,
      amountPaid,
      dueAmount,
      manualItems: invoice.manualItems || [],
      notes: invoice.notes || '',
    };
  };

  const getAudioContext = () => {
    try {
      const AudioContextClass =
        window.AudioContext ||
        window.webkitAudioContext;

      if (!AudioContextClass) return null;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      return audioContextRef.current;
    } catch {
      return null;
    }
  };

  const unlockAudioAlerts = async () => {
    try {
      const context = getAudioContext();

      if (!context) return false;

      if (context.state === 'suspended') {
        await context.resume();
      }

      audioUnlockedRef.current = context.state === 'running';
      setRingReady(audioUnlockedRef.current);

      return audioUnlockedRef.current;
    } catch {
      setRingReady(false);
      return false;
    }
  };

  const playBeep = async () => {
    try {
      const context = getAudioContext();

      if (!context) {
        /*
          Old browser fallback. Main flow Web Audio API use karta hai,
          external remote MP3 par depend nahi karta.
        */
        if (!audioRef.current) {
          audioRef.current = new Audio('/sounds/new-order.mp3');
        }

        audioRef.current.currentTime = 0;
        await audioRef.current.play().catch(() => {});
        return;
      }

      if (context.state === 'suspended') {
        await context.resume().catch(() => {});
      }

      if (context.state !== 'running') return;

      audioUnlockedRef.current = true;

      const startAt = context.currentTime + 0.01;
      const notes = [
        { at: 0.00, frequency: 880, duration: 0.16 },
        { at: 0.24, frequency: 1046, duration: 0.18 },
        { at: 0.50, frequency: 1320, duration: 0.30 },
      ];

      notes.forEach(({ at, frequency, duration }) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startAt + at);

        gain.gain.setValueAtTime(0.0001, startAt + at);
        gain.gain.exponentialRampToValueAtTime(0.26, startAt + at + 0.02);
        gain.gain.exponentialRampToValueAtTime(
          0.0001,
          startAt + at + duration
        );

        oscillator.connect(gain);
        gain.connect(context.destination);

        oscillator.start(startAt + at);
        oscillator.stop(startAt + at + duration + 0.04);
      });
    } catch {
      // Browser autoplay policy ki wajah se first interaction se pehle audio block ho sakta hai.
    }
  };

  const speak = (text) => {
    if (!voiceEnabled || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'hi-IN';
    utter.rate = 1;
    window.speechSynthesis.speak(utter);
  };

  const getVoiceText = (order) => {
    const products = (order?.productsParsed || [])
      .map((item) => `${item?.name || item?.item_name || 'item'} ${item?.quantity || item?.qty || 1}`)
      .join(', ');

    return `Order number ${order?.id}. ${MODE_LABELS[order?.displayMode] || 'Order'}. Items ${products}. Total ${Math.round(order?.displayTotal || 0)} rupees.`;
  };

  const handleVisibleOrderAlert = async () => {
    if (document.visibilityState !== 'visible') return;

    /*
      onMessage() aur service-worker postMessage() dono fire ho sakte hain.
      Same order par ek hi ring play honi chahiye.
    */
    const now = Date.now();

    if (now - foregroundFcmHandledAtRef.current < 1800) {
      return;
    }

    foregroundFcmHandledAtRef.current = now;

    await playBeep();
    fetchOrders({ silent: true });
  };

  const testAndActivateRing = async () => {
    const unlocked = await unlockAudioAlerts();

    if (!unlocked) {
      showMessage(
        'Browser ne ring audio block kiya hai. Screen par tap karke dobara try karein.',
        'error',
        4200
      );
      return;
    }

    await playBeep();
    showMessage('New order ring active hai.');
  };

  const enableVoice = async () => {
    await unlockAudioAlerts();

    setVoiceEnabled((previous) => {
      const next = !previous;

      showMessage(
        next
          ? 'Voice alerts enabled. New order ring bhi active hai.'
          : 'Voice reading off hai. New order ring active rahegi.'
      );

      return next;
    });

    playBeep();
  };

  const fetchNotificationDevices = async ({
    authToken = getToken(),
    silent = true,
  } = {}) => {
    if (!authToken || !currentNotificationDeviceId) return [];

    try {
      if (!silent) setNotificationDevicesLoading(true);

      const res = await axios.get(
        `${STANDARD_ORDERS_API}/notification-devices`,
        {
          params: {
            deviceId: currentNotificationDeviceId,
          },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const devices = Array.isArray(res?.data?.devices)
        ? res.data.devices
        : [];

      setNotificationDevices(devices);

      return devices;
    } catch (err) {
      console.error('[FCM] Notification devices fetch failed:', err);

      if (!silent) {
        showMessage(
          err?.response?.data?.message ||
            err?.message ||
            'Notification devices load nahi hue.',
          'error',
          4200
        );
      }

      return [];
    } finally {
      setNotificationDevicesLoading(false);
    }
  };

  const toggleCurrentDeviceCompulsory = async (checked) => {
    const token = getToken();

    if (!token || !currentNotificationDeviceId) {
      showMessage('Current device identify nahi hua.', 'error');
      return;
    }

    try {
      setNotificationSaving(true);

      const res = await axios.patch(
        `${STANDARD_ORDERS_API}/notification-devices/${encodeURIComponent(
          currentNotificationDeviceId
        )}/compulsory`,
        {
          isCompulsory: checked,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showMessage(
        res?.data?.message ||
          (checked
            ? 'Current device compulsory notifications ke liye protected hai.'
            : 'Current device compulsory list se remove ho gaya.')
      );

      await fetchNotificationDevices({
        authToken: token,
        silent: true,
      });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Compulsory notification setting update nahi hui.',
        'error',
        4200
      );
    } finally {
      setNotificationSaving(false);
    }
  };

  const removeNotificationDevice = async (deviceId) => {
    const token = getToken();

    if (!token || !deviceId) return;

    try {
      setNotificationSaving(true);

      const res = await axios.delete(
        `${STANDARD_ORDERS_API}/notification-devices/${encodeURIComponent(
          deviceId
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showMessage(
        res?.data?.message ||
          'Notification device removed successfully.'
      );

      await fetchNotificationDevices({
        authToken: token,
        silent: true,
      });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Notification device remove nahi hua.',
        'error',
        4200
      );
    } finally {
      setNotificationSaving(false);
    }
  };

  const requestAndSaveNotificationToken = async (
    nextVendorId,
    authToken,
    { askPermission = false, showSuccess = false } = {}
  ) => {
    if (!nextVendorId || !authToken) return false;

    if (typeof Notification === 'undefined') {
      setNotificationPermission('unsupported');

      if (showSuccess) {
        showMessage('Is browser me notifications supported nahi hain.', 'error');
      }

      return false;
    }

    try {
      setNotificationSaving(true);

      const result = await requestNotificationPermission({ askPermission });
      setNotificationPermission(result.permission || Notification.permission);

      if (!result.success || !result.token) {
        if (showSuccess) {
          showMessage(result.message || 'Notification permission allow nahi hui.', 'error', 4200);
        }

        return false;
      }

      const saveResponse = await axios.post(
        `${STANDARD_ORDERS_API}/save-fcm-token`,
        {
          fcmToken: result.token,
          deviceId: currentNotificationDeviceId,
          deviceName: getNotificationDeviceLabel(),
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      localStorage.setItem('fastfood_fcm_token', result.token);
      localStorage.setItem('fastfood_fcm_token_synced_at', new Date().toISOString());
      setNotificationPermission('granted');

      if (Array.isArray(saveResponse?.data?.devices)) {
        setNotificationDevices(saveResponse.data.devices);
      } else {
        await fetchNotificationDevices({
          authToken,
          silent: true,
        });
      }

      if (showSuccess) {
        showMessage(
          saveResponse?.data?.message ||
            'New order phone notifications enabled successfully.',
          'success',
          4200
        );
      }

      return true;
    } catch (err) {
      console.error('[FCM] Backend token sync failed:', err);

      if (showSuccess) {
        showMessage(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Notification token backend me save nahi hua.',
          'error',
          4200
        );
      }

      return false;
    } finally {
      setNotificationSaving(false);
    }
  };

  const enablePhoneNotifications = async () => {
    const token = getToken();

    if (!token || !vendorId) {
      showMessage('Vendor details load hone ke baad notification enable karein.', 'error');
      return;
    }

    /*
      Ye button user gesture hai. Isi click par foreground ring unlock karte hain.
    */
    await unlockAudioAlerts();

    const saved = await requestAndSaveNotificationToken(vendorId, token, {
      askPermission: true,
      showSuccess: true,
    });

    if (saved) {
      await playBeep();
    }
  };

  const applyBillingDefaultsToUntouchedInvoices = (settings) => {
    const enabled = Boolean(settings?.gstEnabled);
    const percent = Math.max(
      0,
      toNumber(settings?.defaultGstPercent, 5)
    );

    setInvoiceByOrder((prev) => {
      const next = {};

      Object.entries(prev).forEach(([orderId, invoice]) => {
        if (invoice?.gstTouched) {
          next[orderId] = invoice;
          return;
        }

        next[orderId] = {
          ...invoice,
          gstEnabled: enabled,
          gstPercent: enabled ? String(percent) : '',
        };
      });

      return next;
    });
  };

  const fetchBillingSettings = async (authToken = getToken()) => {
    if (!authToken) return DEFAULT_VENDOR_BILLING_SETTINGS;

    try {
      setBillingSettingsLoading(true);

      const res = await axios.get(
        `${STANDARD_ORDERS_API}/billing-settings`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const nextSettings = {
        gstEnabled: Boolean(res?.data?.settings?.gstEnabled),
        defaultGstPercent: String(
          toNumber(res?.data?.settings?.defaultGstPercent, 5)
        ),
      };

      billingSettingsRef.current = nextSettings;
      setBillingSettings(nextSettings);
      applyBillingDefaultsToUntouchedInvoices(nextSettings);

      return nextSettings;
    } catch (err) {
      console.error('[BILLING SETTINGS] fetch failed:', err);

      billingSettingsRef.current = DEFAULT_VENDOR_BILLING_SETTINGS;
      setBillingSettings(DEFAULT_VENDOR_BILLING_SETTINGS);

      return DEFAULT_VENDOR_BILLING_SETTINGS;
    } finally {
      setBillingSettingsLoading(false);
    }
  };

  const saveBillingSettings = async () => {
    const token = getToken();

    if (!token) {
      showMessage('Please login again.', 'error');
      return;
    }

    const defaultGstPercent = Math.max(
      0,
      Math.min(100, toNumber(billingSettings.defaultGstPercent, 0))
    );

    try {
      setBillingSettingsSaving(true);

      const res = await axios.patch(
        `${STANDARD_ORDERS_API}/billing-settings`,
        {
          gstEnabled: Boolean(billingSettings.gstEnabled),
          defaultGstPercent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const nextSettings = {
        gstEnabled: Boolean(res?.data?.settings?.gstEnabled),
        defaultGstPercent: String(
          toNumber(res?.data?.settings?.defaultGstPercent, defaultGstPercent)
        ),
      };

      billingSettingsRef.current = nextSettings;
      setBillingSettings(nextSettings);
      applyBillingDefaultsToUntouchedInvoices(nextSettings);

      showMessage(
        nextSettings.gstEnabled
          ? `Default GST ${nextSettings.defaultGstPercent}% saved.`
          : 'Default GST disabled. New bills par GST automatically apply nahi hoga.'
      );
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'GST billing setting save nahi hui.',
        'error',
        4200
      );
    } finally {
      setBillingSettingsSaving(false);
    }
  };

  const fetchVendorDetails = async () => {
    const token = getToken();
    const latestStaffSession = getStaffSessionSnapshot();

    if (!token) {
      setLoading(false);
      showMessage('Please login to view orders.', 'error');
      return;
    }

    try {
      if (latestStaffSession.isStaff) {
        const nextVendorId = Number(latestStaffSession.vendorId || 0);
        const nextVendorType = String(latestStaffSession.vendorType || 'fastfood').toLowerCase();

        if (!nextVendorId) {
          setLoading(false);
          showMessage('Staff login me vendor id missing hai. Owner se naya staff link lo.', 'error', 5200);
          return;
        }

        setStaffSession(latestStaffSession);
        setVendorId(nextVendorId);
        setVendorType(nextVendorType);

        await fetchBillingSettings(token);

        if (!canAccess('counterOrder') && counterOrderOpen) {
          setCounterOrderOpen(false);
        }

        return;
      }

      const res = await axios.get(`${STANDARD_ORDERS_API}/get-vendor-id`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const nextVendorId = res?.data?.vendorId || res?.data?.vendor_id || null;
      const nextVendorType = String(res?.data?.vendorType || 'fastfood').toLowerCase();

      setVendorId(nextVendorId);
      setVendorType(nextVendorType);

      await fetchBillingSettings(token);

      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted'
      ) {
        await requestAndSaveNotificationToken(nextVendorId, token, {
          askPermission: false,
          showSuccess: false,
        });
      } else {
        setNotificationPermission(getNotificationPermissionState());
      }

      await fetchNotificationDevices({
        authToken: token,
        silent: true,
      });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to fetch vendor details.',
        'error'
      );
      setLoading(false);
    }
  };

  const fetchOnlineOrders = async ({ silent = false } = {}) => {
    const token = getToken();

    if (!token || !vendorId) return;

    try {
      if (!silent) {
        setOnlineOrdersLoading(true);
      }

      const response = await axios.get(
        `${EXTERNAL_FOOD_ORDERS_API}/online-orders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const rows = Array.isArray(response?.data?.orders)
        ? response.data.orders
        : [];

      /*
        /online-orders endpoint external_orders + orders join return karta hai.
        Existing activeOrders me StandardOrders backend ka enriched object hota hai
        (displayStatus, productsParsed, delivery details, bill/KOT, etc).

        Same VAKRA order id milne par dono objects merge karte hain:
        - workflow data activeOrders se
        - platform/source data external_orders se
      */
      const mergedRows = rows.map((row) => {
        const activeMatch = activeOrders.find(
          (order) => Number(order.id) === Number(row.id)
        );

        if (!activeMatch) {
          return {
            ...row,
            productsParsed: Array.isArray(row.productsParsed)
              ? row.productsParsed
              : parseJsonSafe(row.products, []),
            displayStatus:
              String(row.status || '').toLowerCase() === 'pending'
                ? 'new'
                : String(row.status || 'new').toLowerCase(),
          };
        }

        return {
          ...row,
          ...activeMatch,

          // External source fields must always win.
          order_source: row.order_source,
          source: row.source,
          external_platform: row.external_platform,
          external_order_id: row.external_order_id,
          external_outlet_id: row.external_outlet_id,

          external_customer_name: row.external_customer_name,
          external_customer_mobile: row.external_customer_mobile,
          external_total_amount: row.external_total_amount,
          external_payment_status: row.external_payment_status,
          external_payment_method: row.external_payment_method,
          external_status: row.external_status,
        };
      });

      setOnlineOrders(mergedRows);
    } catch (error) {
      console.error('[ONLINE ORDERS] fetch error:', error);

      if (!silent) {
        showMessage(
          error?.response?.data?.message ||
            'Online orders load nahi hue.',
          'error'
        );
      }
    } finally {
      if (!silent) {
        setOnlineOrdersLoading(false);
      }
    }
  };


  const fetchOrders = async ({ silent = false } = {}) => {
    const token = getToken();

    if (!token || !vendorId || requestInFlightRef.current) return;

    requestInFlightRef.current = true;

    try {
      if (!silent) setLoading(true);

      const queryParam = getDbQueryParam();

      const [activeRes, historyRes] = await Promise.all([
        axios.get(`${STANDARD_ORDERS_API}/today-orders?${queryParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${STANDARD_ORDERS_API}/history?${queryParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const activeRaw = Array.isArray(activeRes?.data?.orders) ? activeRes.data.orders : [];
      const historyRaw = Array.isArray(historyRes?.data?.orders) ? historyRes.data.orders : [];

      /*
        IMPORTANT UI STATUS SYNC:
        Shortcut action successful hone ke turant baad silent refresh chal sakta hai.
        Kabhi-kabhi backend ka previous snapshot ek refresh cycle ke liye milta hai.

        Example:
        A press → local accepted
        silent fetch → stale new

        Pehle keyboard ref accepted preserve karta tha, lekin activeOrders me stale new
        set ho jata tha. Isliye next shortcut K work karta tha par card New dikhata tha.

        Ab refreshed order ka DISPLAY status bhi forward-only merged status use karega.
        Isse card, tabs aur counters ek hi latest workflow status dikhayenge.
      */
      const mergeServerOrderWithKeyboardStatus = (order) => {
        if (!order?.id) return order;

        const orderId = Number(order.id);
        const previousStatus = keyboardStatusByOrderRef.current.get(orderId);
        const mergedStatus = mergeKeyboardStatus(
          previousStatus,
          order.displayStatus
        );

        keyboardStatusByOrderRef.current.set(orderId, mergedStatus);

        return {
          ...order,
          displayStatus: mergedStatus,
          status: mergedStatus === 'new' ? 'pending' : mergedStatus,
          locked:
            ['done', 'cancelled'].includes(mergedStatus) ||
            Boolean(order.locked),
        };
      };

      const active = uniqueById(activeRaw)
        .filter((order) => Number(order.vendor_id) === Number(vendorId))
        .map(normalizeOrder)
        .map(mergeServerOrderWithKeyboardStatus)
        .filter((order) => !['done', 'cancelled'].includes(order.displayStatus));

      const history = uniqueById(historyRaw)
        .filter((order) => Number(order.vendor_id) === Number(vendorId))
        .map(normalizeOrder)
        .map(mergeServerOrderWithKeyboardStatus);

      const previousActiveOrderIds = knownActiveOrderIdsRef.current;
      const newlyArrivedNewOrder = hasLoadedOrdersOnceRef.current
        ? active.find(
            (order) =>
              order.displayStatus === 'new' &&
              !previousActiveOrderIds.has(Number(order.id))
          ) || null
        : null;

      knownActiveOrderIdsRef.current = new Set(
        active.map((order) => Number(order.id)).filter(Boolean)
      );
      hasLoadedOrdersOnceRef.current = true;

      const nextSignature = buildSignature(active);

      if (activeSignatureRef.current && activeSignatureRef.current !== nextSignature) {
        let oldCount = 0;
        try {
          oldCount = JSON.parse(activeSignatureRef.current).length;
        } catch {
          oldCount = 0;
        }

        if (active.length > oldCount) {
          const foregroundFcmHandledRecently =
            Date.now() - foregroundFcmHandledAtRef.current < 5000;

          if (!foregroundFcmHandledRecently) {
            playBeep();
          }

          speak(getVoiceText(active[0]));
        }
      }

      activeSignatureRef.current = nextSignature;

      setActiveOrders(active);
      setHistoryOrders(history);

      if (newlyArrivedNewOrder) {
        keyboardSelectedOrderRef.current = newlyArrivedNewOrder;
      }

      setSelectedOrderId((prev) => {
        const all = [...active, ...history];

        if (!all.length) return null;

        // A fresh counter/QR/customer order should immediately drive the
        // right-side Items panel, so the vendor sees exactly what was ordered.
        if (newlyArrivedNewOrder) return newlyArrivedNewOrder.id;

        if (prev && all.some((order) => Number(order.id) === Number(prev))) return prev;

        return all[0].id;
      });

      [...active, ...history].forEach(initInvoiceForOrder);

      if (!silent && active.length === 0 && history.length === 0) {
        showMessage('No orders found.', 'error');
      }
    } catch (err) {
      if (!silent) {
        showMessage(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Failed to load orders.',
          'error'
        );
      }
    } finally {
      requestInFlightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    /*
      Print dialog close karke dashboard tab visible / focused ho to
      shortcut keys ke liye focus automatic restore hoga.
    */
    const handleDashboardFocusReturn = () => {
      if (printKeyboardFocusRestorePendingRef.current) {
        restoreDashboardKeyboardFocus();
      }
    };

    window.addEventListener('focus', handleDashboardFocusReturn);
    document.addEventListener('visibilitychange', handleDashboardFocusReturn);

    return () => {
      window.removeEventListener('focus', handleDashboardFocusReturn);
      document.removeEventListener('visibilitychange', handleDashboardFocusReturn);
    };
  }, []);

  useEffect(() => {
    const handlePhoneBack = () => {
      if (cancelOrderTarget) {
        setCancelOrderTarget(null);
        return;
      }

      if (currentScreen === 'detail') {
        closeDetailScreen({ fromPopState: true });
      }
    };

    window.addEventListener('popstate', handlePhoneBack);

    return () => {
      window.removeEventListener('popstate', handlePhoneBack);
    };
  }, [currentScreen, cancelOrderTarget]);

  useEffect(() => {
    try {
      localStorage.setItem('fastfood_ui_settings', JSON.stringify(uiSettings));
    } catch {
      // ignore localStorage errors
    }
  }, [uiSettings]);

  useEffect(() => {
    try {
      localStorage.setItem(
        FASTFOOD_INVOICE_DRAFTS_KEY,
        JSON.stringify(invoiceByOrder || {})
      );
    } catch {
      // ignore invoice draft storage errors
    }
  }, [invoiceByOrder]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event.target;

      if (
        actionDropdown &&
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(target)
      ) {
        setActionDropdown(null);
      }

      if (
        historyFilterOpen &&
        historyFilterRef.current &&
        !historyFilterRef.current.contains(target)
      ) {
        setHistoryFilterOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setActionDropdown(null);
        setHistoryFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [actionDropdown, historyFilterOpen]);

  useEffect(() => {
    fetchVendorDetails();
  }, []);

  useEffect(() => {
    /*
      Browser audio autoplay policy:
      Vendor ke first tap / click ke baad audio context unlock rahega.
      Login ke baad normal dashboard interaction usually enough hota hai.
    */
    const unlock = () => {
      unlockAudioAlerts();
    };

    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('touchstart', unlock, { passive: true });
    window.addEventListener('keydown', unlock);

    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    let unsubscribe = () => {};

    listenForForegroundMessages(() => {
      /*
        Primary foreground FCM path:
        Dashboard visible ho to sirf ring + silent refresh.
      */
      handleVisibleOrderAlert();
    }).then((cleanup) => {
      unsubscribe = typeof cleanup === 'function' ? cleanup : () => {};
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    /*
      Some mobile browsers visible page par Firebase onMessage() ko inconsistent
      tarike se fire karte hain. Service worker visible tab ko direct message bhejega.
    */
    if (!('serviceWorker' in navigator)) return undefined;

    const handleServiceWorkerMessage = (event) => {
      if (event?.data?.type !== 'VAKRA_VISIBLE_ORDER_PUSH') return;

      handleVisibleOrderAlert();
    };

    navigator.serviceWorker.addEventListener(
      'message',
      handleServiceWorkerMessage
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        'message',
        handleServiceWorkerMessage
      );
    };
  }, []);

  useEffect(() => {
    if (!vendorId) return;

    fetchOrders({ silent: false });

    pollingRef.current = window.setInterval(() => {
      if (!document.hidden && currentScreen === 'list' && uiSettings.autoRefresh) {
        fetchOrders({ silent: true });
      }
    }, 9000);

    return () => {
      window.clearInterval(pollingRef.current);
    };
  }, [vendorId, vendorType, currentScreen, uiSettings.autoRefresh]);

  const allOrders = activeTab === 'active' ? activeOrders : historyOrders;

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();

    return allOrders
      .filter((order) => {
        const statusOk =
          statusFilter === 'all' ||
          order.displayStatus === statusFilter ||
          (statusFilter === 'preparing' && order.displayStatus === 'preparing');

        /*
          Active aur History dono queue me practical search:
          - Order ID
          - Table number
          - Amount / total
          - Customer name / mobile
          - Address
          - Product name
          - Bill / invoice number
          - Payment status / method
          - Order mode / status
        */
        const searchableAmounts = [
          order.displayTotal,
          order.total_price,
          order.final_total,
          order.bill_total,
          order.bill?.final_total,
          order.invoice?.final_total,
        ]
          .filter((value) => value !== undefined && value !== null && value !== '')
          .flatMap((value) => {
            const numericValue = toNumber(value, 0);

            return [
              String(value),
              String(numericValue),
              String(Math.round(numericValue)),
              formatMoney(numericValue),
              `amount ${numericValue}`,
              `total ${numericValue}`,
            ];
          });

        const productSearchValues = (order.productsParsed || []).flatMap((item) => [
          item?.name,
          item?.item_name,
          item?.category,
          item?.quantity,
          item?.qty,
        ]);

        const text = [
          order.id,
          `order ${order.id}`,
          `#${order.id}`,
          order.customerName,
          order.customerMobile,
          order.customer_number,
          order.fullAddress,
          order.table_number,
          order.table_number ? `table ${order.table_number}` : '',
          order.table_number ? `table-${order.table_number}` : '',
          order.redeem?.token,
          order.bill_no,
          order.invoice_no,
          order.bill?.bill_no,
          order.invoice?.bill_no,
          order.displayMode,
          MODE_LABELS[order.displayMode],
          order.displayStatus,
          order.displayPaymentStatus,
          order.payment_status,
          order.payment_method,
          ...searchableAmounts,
          ...productSearchValues,
        ]
          .filter((value) => value !== undefined && value !== null && value !== '')
          .join(' ')
          .toLowerCase();

        const dateOk = matchesHistoryDateFilter(order);
        const staffAllowed = isOrderAllowedForCurrentStaff(order);

        return staffAllowed && statusOk && dateOk && (!q || text.includes(q));
      })
      .sort((a, b) => {
        /*
          Active queue priority:
          1. Fresh QR / Counter order
          2. KOT printed / kitchen-running order
          3. Running dine-in table
          4. Remaining active orders

          History keeps normal newest-first order.
        */
        if (activeTab === 'active') {
          const getPriority = (order) => {
            if (order.displayStatus === 'new' && !order.kotSent) return 0;
            if (order.kotSent && !order.locked) return 1;
            if (order.runningTable && !order.locked) return 2;
            return 3;
          };

          const priorityDifference = getPriority(a) - getPriority(b);
          if (priorityDifference !== 0) return priorityDifference;
        }

        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
  }, [allOrders, search, statusFilter, activeTab, historyDateFilter, isOrderAllowedForCurrentStaff]);

  useEffect(() => {
    /*
      Search query change hone par selected card filtered list ke andar hi rahe.
      Isse search ke baad Arrow aur Enter correct visible result par work karenge.
    */
    if (!filteredOrders.length) return;

    const selectedStillVisible = filteredOrders.some(
      (order) => Number(order.id) === Number(selectedOrderId)
    );

    if (!selectedStillVisible) {
      const firstVisibleOrder = filteredOrders[0];

      setSelectedOrderId(firstVisibleOrder.id);
      keyboardSelectedOrderRef.current = firstVisibleOrder;
      initInvoiceForOrder(firstVisibleOrder);
    }
  }, [filteredOrders, selectedOrderId]);

  const selectedOrder = useMemo(() => {
    return (
      [...activeOrders, ...historyOrders].find((order) => Number(order.id) === Number(selectedOrderId)) ||
      filteredOrders[0] ||
      null
    );
  }, [activeOrders, historyOrders, selectedOrderId, filteredOrders]);

  /*
    Keyboard handler ko latest selected order synchronously chahiye.
    React event listener ke stale render snapshot par depend nahi karenge.
  */
  if (selectedOrder) {
    const latestKeyboardStatus = mergeKeyboardStatus(
      keyboardStatusByOrderRef.current.get(Number(selectedOrder.id)),
      selectedOrder.displayStatus
    );

    keyboardStatusByOrderRef.current.set(
      Number(selectedOrder.id),
      latestKeyboardStatus
    );

    keyboardSelectedOrderRef.current = {
      ...selectedOrder,
      displayStatus: latestKeyboardStatus,
      status:
        latestKeyboardStatus === 'new'
          ? 'pending'
          : latestKeyboardStatus,
      locked: ['done', 'cancelled'].includes(latestKeyboardStatus),
    };
  } else {
    keyboardSelectedOrderRef.current = null;
  }

  useEffect(() => {
    if (selectedOrder) {
      initInvoiceForOrder(selectedOrder);
    }
  }, [selectedOrder?.id]);

  useEffect(() => {
    if (!selectedOrder?.id || isMobile) return;

    const selectedCard = document.querySelector(
      `[data-keyboard-order-id="${selectedOrder.id}"]`
    );

    selectedCard?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [selectedOrder?.id, isMobile]);

  useEffect(() => {
    setActionDropdown(null);
  }, [selectedOrder?.id, currentScreen, activeTab, sidePanel]);



  const selectedInvoice = useMemo(
    () => calculateInvoice(selectedOrder),
    [selectedOrder, invoiceByOrder]
  );

  const summary = useMemo(() => {
    const active = activeOrders;

    return {
      newCount: active.filter((order) => order.displayStatus === 'new').length,
      kitchenCount: active.filter((order) => order.displayStatus === 'preparing').length,
      readyCount: active.filter((order) => order.displayStatus === 'ready').length,
      completedCount: historyOrders.filter((order) => order.displayStatus === 'done').length,
    };
  }, [activeOrders, historyOrders]);

  const updateLocalStatus = (orderId, status) => {
    /*
      Shortcut flow ke liye status ref immediately update hota hai.
      React re-render ka wait nahi karna padega.
    */
    keyboardStatusByOrderRef.current.set(Number(orderId), status);

    if (
      keyboardSelectedOrderRef.current &&
      Number(keyboardSelectedOrderRef.current.id) === Number(orderId)
    ) {
      keyboardSelectedOrderRef.current = {
        ...keyboardSelectedOrderRef.current,
        displayStatus: status,
        status: status === 'new' ? 'pending' : status,
        locked: ['done', 'cancelled'].includes(status),
      };
    }

    const updater = (order) =>
      Number(order.id) === Number(orderId)
        ? {
            ...order,
            display_status: status,
            displayStatus: status,
            order_status: status,
            status: status === 'new' ? 'pending' : status,
            cancelled_at:
              status === 'cancelled'
                ? order.cancelled_at || new Date().toISOString()
                : order.cancelled_at,
            locked: ['done', 'cancelled'].includes(status),
          }
        : order;

    setActiveOrders((prev) =>
      ['done', 'cancelled'].includes(status)
        ? prev.filter((order) => Number(order.id) !== Number(orderId))
        : prev.map(updater)
    );

    setHistoryOrders((prev) => {
      const oldOrder = [...activeOrders, ...historyOrders].find((order) => Number(order.id) === Number(orderId));
      if (!oldOrder) return prev.map(updater);

      const statusChangedAt = new Date().toISOString();

      const completedOrder = {
        ...oldOrder,
        display_status: status,
        displayStatus: status,
        order_status: status,
        displayStatusSource: 'local_status_update',
        status,
        locked: ['done', 'cancelled'].includes(status),
        completed_at:
          status === 'done'
            ? statusChangedAt
            : oldOrder.completed_at,
        cancelled_at:
          status === 'cancelled'
            ? statusChangedAt
            : oldOrder.cancelled_at,
      };

      if (['done', 'cancelled'].includes(status)) {
        return [completedOrder, ...prev.filter((order) => Number(order.id) !== Number(orderId))];
      }

      return prev.map(updater);
    });
  };

  const postStatus = async (orderId, status) => {
    const token = getToken();

    await axios.post(
      `${STANDARD_ORDERS_API}/update-status`,
      { orderId, status },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const openCancelOrderPopup = (order, event = null) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (!canAccess('cancelOrder')) {
      showMessage(getStaffDeniedMessage('Cancel Order'), 'error', 3600);
      return;
    }

    if (!order || order.locked) return;

    /*
      Requested UX:
      Cancellation icon sirf freshly received New orders par show hoga.
      Isse preparing / ready / delivery flow accidental cancel se safe rahega.
    */
    if (order.displayStatus !== 'new') {
      showMessage('Sirf New order ko queue card se cancel kar sakte hain.', 'error');
      return;
    }

    setCancelOrderTarget(order);
  };

  const closeCancelOrderPopup = () => {
    if (cancelOrderLoading) return;
    setCancelOrderTarget(null);
  };

  const confirmCancelOrder = async () => {
    const order = cancelOrderTarget;

    if (!order || cancelOrderLoading) return;

    try {
      setCancelOrderLoading(true);

      await postStatus(order.id, 'cancelled');
      updateLocalStatus(order.id, 'cancelled');

      setCancelOrderTarget(null);

      if (Number(selectedOrderId) === Number(order.id)) {
        setSelectedOrderId(null);
      }

      showMessage(`Order #${order.id} cancelled successfully.`);
      fetchOrders({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Order cancel nahi hua.',
        'error',
        4200
      );
    } finally {
      setCancelOrderLoading(false);
    }
  };

  const handleStepAction = async (order, forcedStatus = null) => {
    if (!order || actionLoading) return;

    const targetStatus = forcedStatus || STATUS_FLOW[order.displayStatus]?.next;

    if (targetStatus === 'accepted' && !canAccess('orderAccept')) {
      showMessage(getStaffDeniedMessage('Accept Order'), 'error', 3600);
      return;
    }

    if (targetStatus === 'preparing' && !canAccessAny(['sendToKitchen', 'markPreparing'])) {
      showMessage(getStaffDeniedMessage('Kitchen'), 'error', 3600);
      return;
    }

    if (targetStatus === 'ready' && !canAccess('markReady')) {
      showMessage(getStaffDeniedMessage('Mark Ready'), 'error', 3600);
      return;
    }

    if (order.locked) {
      showMessage('Completed orders are read-only.', 'error');
      return;
    }

    const current = order.displayStatus;
    const next = targetStatus;

    if (!next) {
      if (current === 'ready') {
        openDetail(order);
      }
      return;
    }

    try {
      setActionLoading(true);

      if (next === 'preparing') {
        await sendToKitchen(order, { quiet: true });
      } else {
        await postStatus(order.id, next);
      }

      updateLocalStatus(order.id, next);
      showMessage(`${STATUS_FLOW[next]?.longLabel || next} successful.`);

      if (next === 'ready') {
        openDetail({ ...order, displayStatus: next });
      }

      fetchOrders({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Action failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const sendToKitchen = async (order, { quiet = false } = {}) => {
    if (!order) return;

    if (!canAccessAny(['sendToKitchen', 'markPreparing'])) {
      showMessage(getStaffDeniedMessage('Send KOT to Kitchen'), 'error', 3600);
      return;
    }

    const token = getToken();

    await axios.post(
      `${STANDARD_ORDERS_API}/kot/send`,
      { orderId: order.id },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    updateLocalStatus(order.id, 'preparing');

    if (!quiet) {
      showMessage(`Order #${order.id} sent to kitchen.`);
      fetchOrders({ silent: true });
    }
  };

  const printKot = async (order) => {
    if (!order) return;

    if (!canAccess('printKot')) {
      showMessage(getStaffDeniedMessage('Print KOT'), 'error', 3600);
      return;
    }

    const token = getToken();

    try {
      await axios.post(
        `${STANDARD_ORDERS_API}/kot/print`,
        { orderId: order.id, printStatus: 'success' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // print should not fail for log issue
    }

    const html = buildKotHtml(order);
    printHtml(html, `KOT-${order.id}`);
    showMessage('KOT print opened successfully.');
  };

  const openDetail = (order) => {
    if (!order) return;

    if (!isOrderAllowedForCurrentStaff(order)) {
      showMessage(getStaffDeniedMessage('Order Detail'), 'error', 3600);
      return;
    }

    setSelectedOrderId(order.id);
    keyboardSelectedOrderRef.current = order;
    initInvoiceForOrder(order);

    /*
      Phone hardware / gesture Back handling:
      Detail open karte waqt same URL ki ek browser-history entry add karte hain.
      Isse phone Back press hone par vendor app se bahar ya home page par nahi jayega;
      pehle Orders list screen restore hogi.
    */
    if (isMobile && currentScreen !== 'detail') {
      try {
        window.history.pushState(
          {
            ...(window.history.state || {}),
            vakraFastFoodOrderDetail: true,
            orderId: order.id,
          },
          '',
          window.location.href
        );

        detailHistoryEntryRef.current = true;
      } catch {
        detailHistoryEntryRef.current = false;
      }
    }

    setCurrentScreen('detail');
  };

  const closeDetailScreen = ({ fromPopState = false } = {}) => {
    setActionDropdown(null);
    setCurrentScreen('list');

    /*
      Visible Back button press hone par artificial detail history entry consume karein.
      Phone hardware Back ke popstate event se call aaye to dobara history.back() mat karein.
    */
    if (!fromPopState && detailHistoryEntryRef.current) {
      detailHistoryEntryRef.current = false;

      try {
        window.history.back();
      } catch {
        // Browser history unavailable ho to list screen already restore ho chuki hai.
      }

      return;
    }

    detailHistoryEntryRef.current = false;
  };

  const addManualItem = (order) => {
    if (!order) return;

    if (!canAccess('invoiceEdit')) {
      showMessage(getStaffDeniedMessage('Invoice Edit'), 'error', 3600);
      return;
    }

    if (order.locked) {
      showMessage('Completed orders are read-only.', 'error');
      return;
    }

    const invoice = getInvoiceState(order.id);
    const name = String(invoice.manualItemName || '').trim();
    const qty = toNumber(invoice.manualItemQty, 0);
    const price = toNumber(invoice.manualItemPrice, 0);

    if (!name || qty <= 0 || price <= 0) {
      showMessage('Manual item name, qty and price required.', 'error');
      return;
    }

    setInvoiceStateForOrder(order.id, (prev) => ({
      ...prev,
      manualItems: [
        ...(prev.manualItems || []),
        {
          id: `manual_${Date.now()}`,
          item_name: name,
          name,
          quantity: qty,
          price,
          rate: price,
          total: qty * price,
          source_type: 'manual',
          manual_added_at: new Date().toISOString(),
        },
      ],
      manualItemName: '',
      manualItemQty: '1',
      manualItemPrice: '',
    }));

    showMessage('Manual item added successfully.');
  };

  const removeManualItem = (orderId, itemId) => {
    if (!canAccess('invoiceEdit')) {
      showMessage(getStaffDeniedMessage('Invoice Edit'), 'error', 3600);
      return;
    }

    if (!orderId || !itemId) return;

    setInvoiceStateForOrder(orderId, (prev) => ({
      ...prev,
      manualItems: (prev.manualItems || []).filter(
        (item) => String(item.id) !== String(itemId)
      ),
    }));

    showMessage('Manual item removed. Save Invoice karne par bill me update ho jayega.');
  };

  const buildBillPayload = (order, status) => {
    const invoice = getInvoiceState(order.id);
    const summary = calculateInvoice(order);

    return {
      orderId: order.id,
      customerName: order.customerName || 'Walk-in Customer',
      customerMobile: order.customerMobile || '',
      orderMode: order.displayMode,
      tableNumber: order.table_number || null,
      tokenNumber: order.redeem?.token || order.token || null,

      paymentStatus: summary.paymentStatus,
      paymentMethod: summary.paymentMethod,
      amountPaid: summary.amountPaid,

      originalTotal: summary.itemsTotal,
      manualItems: summary.manualItems,
      manualDiscount: summary.discount,
      manualDiscountType: summary.discountType,
      manualDiscountValue: summary.discountValue,
      discountType: summary.discountType,
      discountValue: summary.discountValue,
      offerDiscount: 0,
      coinsRedeemed: summary.coinsRedeemed,
      coinDiscountAmount: summary.coinDiscount,
      deliveryCharge: summary.deliveryCharge,
      packingCharge: summary.packingCharge,
      gstEnabled: summary.gstEnabled,
      gstPercent: summary.gstPercent,
      roundOff: summary.roundOff,

      notes: invoice.notes || '',
      status,
    };
  };

  const saveInvoice = async (order) => {
    if (!order || actionLoading) return;

    if (!canAccessAny(['invoiceEdit', 'paymentUpdate'])) {
      showMessage(getStaffDeniedMessage('Save Invoice'), 'error', 3600);
      return;
    }

    if (order.locked) {
      showMessage('Completed orders are read-only.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const token = getToken();
      const payload = buildBillPayload(order, 'saved');

      await axios.post(`${STANDARD_ORDERS_API}/save-bill`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showMessage('Invoice saved successfully.');
      fetchOrders({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Invoice save failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const savePrintComplete = async (order) => {
    if (!order || actionLoading) return;

    if (!canAccess('completeOrder')) {
      showMessage(getStaffDeniedMessage('Complete Order'), 'error', 3600);
      return;
    }

    if (order.locked) {
      showMessage('Completed orders are read-only.', 'error');
      return;
    }

    if (order.displayMode === 'delivery') {
      showMessage('Delivery order token verification ke baad hi complete hoga. Send to Delivery use karein.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const token = getToken();
      const payload = buildBillPayload(order, 'completed');

      await axios.post(`${STANDARD_ORDERS_API}/save-print-complete`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      printBill(order);
      updateLocalStatus(order.id, 'done');
      setCurrentScreen('list');
      showMessage('Order completed successfully.');
      fetchOrders({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Save + Print + Complete failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const sendDeliveryOrderToDispatch = async (order) => {
    if (!order || actionLoading) return;

    if (!canAccess('deliveryDispatch')) {
      showMessage(getStaffDeniedMessage('Delivery Dispatch'), 'error', 3600);
      return;
    }

    if (order.locked) {
      showMessage('Completed orders are read-only.', 'error');
      return;
    }

    if (order.displayMode !== 'delivery') {
      showMessage('Ye option sirf delivery orders ke liye hai.', 'error');
      return;
    }

    if (order.displayStatus !== 'ready') {
      showMessage('Delivery me bhejne se pehle order Ready mark hona chahiye.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const token = getToken();
      const payload = buildBillPayload(order, 'saved');

      await axios.post(`${STANDARD_ORDERS_API}/save-bill`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      printBill(order);

      setCurrentScreen('list');
      setSidePanel('delivery');
      setDeliveryTab('dispatch');

      showMessage('Delivery order invoice saved. Ab Delivery panel me rider assign karein.');

      fetchOrders({ silent: true });
      fetchDeliveryData({ silent: false });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Delivery dispatch me bhejne me problem aayi.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const printOnly = (order) => {
    if (!order) {
      showMessage('Print ke liye order select karein.', 'error');
      return;
    }

    try {
      printBill(order);
      showMessage('Print window open ho raha hai.');
    } catch (err) {
      console.error('printOnly error:', err);
      showMessage(err?.message || 'Print open nahi hua.', 'error');
    }
  };

  const isTypingElement = (target) => {
    if (!target) return false;

    const tagName = String(target.tagName || '').toLowerCase();

    return (
      ['input', 'textarea', 'select'].includes(tagName) ||
      target.isContentEditable === true
    );
  };

  const moveKeyboardSelection = (direction) => {
    if (!filteredOrders.length) {
      showMessage('Select karne ke liye order available nahi hai.', 'error');
      return;
    }

    const currentIndex = filteredOrders.findIndex(
      (order) => Number(order.id) === Number(selectedOrderId)
    );

    const startIndex =
      currentIndex >= 0
        ? currentIndex
        : direction > 0
          ? -1
          : 0;

    const nextIndex =
      (startIndex + direction + filteredOrders.length) %
      filteredOrders.length;

    const nextOrder = filteredOrders[nextIndex];

    if (!nextOrder) return;

    setSelectedOrderId(nextOrder.id);
    keyboardSelectedOrderRef.current = nextOrder;
    initInvoiceForOrder(nextOrder);
  };

  const getLatestKeyboardOrder = () => {
    const liveOrder = keyboardSelectedOrderRef.current || selectedOrder;

    if (!liveOrder) return null;

    const latestStatus =
      keyboardStatusByOrderRef.current.get(Number(liveOrder.id)) ||
      liveOrder.displayStatus;

    return {
      ...liveOrder,
      displayStatus: latestStatus,
      status: latestStatus === 'new' ? 'pending' : latestStatus,
      locked: ['done', 'cancelled'].includes(latestStatus),
    };
  };

  const runKeyboardOrderAction = async (actionName) => {
    const order = getLatestKeyboardOrder();

    if (!order) {
      showMessage('Pehle order select karein.', 'error');
      return;
    }

    if (actionName === 'open') {
      openDetail(order);
      return;
    }

    if (activeTab !== 'active' || order.locked) {
      showMessage('History / completed order read-only hai.', 'error');
      return;
    }

    if (actionName === 'accept') {
      if (order.displayStatus !== 'new') {
        showMessage('Accept shortcut sirf New order par chalega.', 'error');
        return;
      }

      /*
        Keyboard actions Promise queue me serially execute hote hain.
        Status ref successful API ke baad updateLocalStatus() me update hoga.
        Isse API fail hone par galat Accepted status UI me chipka nahi rahega.
      */
      await handleStepAction(order, 'accepted');
      return;
    }

    if (actionName === 'kitchen') {
      if (order.displayStatus !== 'accepted') {
        showMessage('Kitchen me bhejne se pehle order Accept karein.', 'error');
        return;
      }

      await handleStepAction(order, 'preparing');
      return;
    }

    if (actionName === 'print-kot') {
      if (!['accepted', 'preparing', 'ready'].includes(order.displayStatus)) {
        showMessage('KOT print ke liye order pehle Accept karein.', 'error');
        return;
      }

      await printKot(order);
      return;
    }

    if (actionName === 'ready') {
      if (order.displayStatus !== 'preparing') {
        showMessage('Ready mark karne se pehle order Kitchen me bhejein.', 'error');
        return;
      }

      await handleStepAction(order, 'ready');
      return;
    }

    if (actionName === 'invoice') {
      openDetail(order);
      return;
    }

    if (actionName === 'invoice-edit') {
      await openInvoiceEditMode(order);
      return;
    }

    if (actionName === 'save-invoice') {
      await saveInvoice(order);
      return;
    }

    if (actionName === 'print-bill') {
      printOnly(order);
      return;
    }

    if (actionName === 'final-billing') {
      if (order.displayStatus !== 'ready') {
        showMessage('Final billing se pehle order Ready mark karein.', 'error');
        return;
      }

      if (order.displayMode === 'delivery') {
        await sendDeliveryOrderToDispatch(order);
      } else {
        await savePrintComplete(order);
      }
    }
  };

  const queueKeyboardOrderAction = (actionName) => {
    /*
      A → K → R jaise fast shortcut presses same order me serially execute honge.
      Ek API complete hone se pehle next API start nahi hogi.
    */
    keyboardActionQueueRef.current = keyboardActionQueueRef.current
      .catch(() => undefined)
      .then(() => runKeyboardOrderAction(actionName));

    return keyboardActionQueueRef.current;
  };

  const closeTransientKeyboardPanels = () => {
    setActionDropdown(null);
    setHistoryFilterOpen(false);
    setShortcutHelpOpen(false);
    setCancelOrderTarget(null);
    releaseInvoiceKeyboardFocus({ clearSearch: true });

    if (counterOrderOpen && !counterSaving) {
      setCounterOrderOpen(false);
      resetCounterOrderForm();
    }
  };

  const openOrdersQueueShortcut = ({
    tab = 'active',
    refresh = false,
  } = {}) => {
    closeTransientKeyboardPanels();
    setSidePanel('orders');
    setCurrentScreen('list');
    setActiveTab(tab);
    setStatusFilter('all');

    if (refresh) {
      fetchOrders({ silent: false });
    }
  };

  const focusOrdersQueueSearch = ({
    selectAll = false,
  } = {}) => {
    setSidePanel('orders');
    setCurrentScreen('list');

    window.setTimeout(() => {
      const input = orderSearchInputRef.current;

      input?.focus?.();

      if (selectAll) {
        input?.select?.();
      }
    }, 0);
  };

  const releaseOrdersQueueSearchFocus = () => {
    const input = orderSearchInputRef.current;

    if (input && document.activeElement === input) {
      input.blur();
    }

    /*
      Hidden print iframe ya input focus ke baad keyboard shortcut keys
      immediately dashboard ko milni chahiye.
    */
    window.focus();
  };

  useEffect(() => {
    if (isMobile) return undefined;

    const handleKeyboardShortcut = (event) => {
      const key = String(event.key || '');
      const lowerKey = key.toLowerCase();
      const typing = isTypingElement(event.target);

      /*
        GLOBAL ORDERS QUEUE NAVIGATION
        Ye kisi bhi internal screen / popup se work karega.

        Alt + Q  Orders Queue / Active
        Alt + R  Orders Queue refresh
        Alt + A  Active Orders
        Alt + H  History
        Alt + D  Delivery page
        Alt + O  Order list
      */
      if (event.altKey && !event.ctrlKey && !event.metaKey) {
        if (lowerKey === 'q') {
          event.preventDefault();
          openOrdersQueueShortcut({ tab: 'active' });
          return;
        }

        if (lowerKey === 'r') {
          event.preventDefault();
          openOrdersQueueShortcut({
            tab: activeTab === 'history' ? 'history' : 'active',
            refresh: true,
          });
          return;
        }

        if (lowerKey === 'a') {
          event.preventDefault();
          openOrdersQueueShortcut({ tab: 'active' });
          return;
        }

        if (lowerKey === 'h') {
          event.preventDefault();
          openOrdersQueueShortcut({ tab: 'history' });
          return;
        }

        if (lowerKey === 'd') {
          event.preventDefault();
          closeTransientKeyboardPanels();
          setSidePanel('delivery');
          setCurrentScreen('list');
          setDeliveryTab('dispatch');
          fetchDeliveryData({ silent: false });
          showMessage('Delivery page open.');
          return;
        }

        if (lowerKey === 'o') {
          event.preventDefault();
          openOrdersQueueShortcut({ tab: 'active' });
          showMessage('Order list open.');
          return;
        }

        if (lowerKey === 'i') {
          event.preventDefault();
          queueKeyboardOrderAction('invoice-edit');
          return;
        }
      }

      /*
        Popup priority:
        - Counter drawer: Esc close, Ctrl+Enter create
        - Cancel popup: Esc keep, Shift+Delete final cancel
        - Shortcut help: Esc or ? close
      */
      if (counterOrderOpen) {
        const isCounterSearchFocused =
          event.target === counterSearchInputRef.current;

        /*
          Counter Order keyboard flow:
          Esc          Search / input focus release; second Esc closes drawer
          Ctrl+Enter   Create order
          D            Dine-in + Table focus
          T            Takeaway + Search focus
          /            Search focus; dobara / press karne par focus release
          Table digits Table number type; Enter confirm karke Search focus
          ↑ / ↓        Product highlight move
          Enter / +    Highlighted product add / qty increase
          -            Highlighted product qty decrease
          C            Customer name focus
          M            Customer mobile focus
          1 / 2 / 3    Cash / UPI / Credit

          Search input ke andar normal letters aur digits product search ke
          liye preserve rahenge. Isliye price search jaise 120 aur initials
          search jaise pmp bhi properly type ho sakte hain.
        */
        if (event.ctrlKey && key === 'Enter') {
          event.preventDefault();
          createCounterOrder();
          return;
        }

        /*
          Search focused ho to letters aur digits ko shortcut nahi banayenge.
          Warna d / c / m letters aur 100 / 250 jaise price searches break
          ho jayenge.
        */
        if (isCounterSearchFocused) {
          if (key === 'Escape') {
            event.preventDefault();
            releaseCounterFieldFocus(event.target);
            return;
          }

          if (key === '/') {
            event.preventDefault();
            releaseCounterFieldFocus(event.target);
            return;
          }

          if (key === 'ArrowDown') {
            event.preventDefault();
            moveCounterKeyboardProduct(1);
            return;
          }

          if (key === 'ArrowUp') {
            event.preventDefault();
            moveCounterKeyboardProduct(-1);
            return;
          }

          if (key === 'Enter' || key === '+' || key === '=') {
            event.preventDefault();
            addHighlightedCounterProduct();
            return;
          }

          if (key === '-') {
            event.preventDefault();
            decreaseHighlightedCounterProduct();
            return;
          }

          return;
        }

        const isCounterTableFocused =
          event.target === counterTableSelectRef.current;

        /*
          D press karne ke baad table dropdown focus me aayega.
          Vendor digits type karke Enter press kar sakta hai:
          D → 1 → 2 → Enter = Table 12 select + Search focus.
        */
        if (isCounterTableFocused) {
          if (key === 'Escape') {
            event.preventDefault();
            releaseCounterFieldFocus(event.target);
            return;
          }

          if (key === '/') {
            event.preventDefault();
            focusCounterSearch({ selectAll: false });
            return;
          }

          if (/^[0-9]$/.test(key)) {
            event.preventDefault();
            setCounterTableFromKeyboardDigit(key);
            return;
          }

          if (key === 'Backspace') {
            event.preventDefault();
            removeLastCounterTableKeyboardDigit();
            return;
          }

          if (key === 'Enter') {
            event.preventDefault();
            confirmCounterTableAndFocusSearch();
            return;
          }

          return;
        }

        /*
          Customer name aur mobile input me normal typing preserve rahegi.

          Esc pehle current field ka focus release karega. Uske baad D / T /
          C / M / 1 / 2 / 3 shortcuts phir se directly work karenge.
        */
        if (typing) {
          if (key === 'Escape') {
            event.preventDefault();
            releaseCounterFieldFocus(event.target);
          }

          return;
        }

        if (key === 'Escape') {
          event.preventDefault();
          closeCounterOrder();
          return;
        }

        if (lowerKey === 'd') {
          event.preventDefault();
          setCounterOrderMode('dine_in');
          focusCounterTable();
          return;
        }

        if (lowerKey === 't') {
          event.preventDefault();
          setCounterOrderMode('takeaway');
          setCounterTableNumber('');
          focusCounterSearch({ selectAll: true });
          return;
        }

        if (key === '/') {
          event.preventDefault();
          focusCounterSearch({ selectAll: true });
          return;
        }

        if (key === 'ArrowDown') {
          event.preventDefault();
          moveCounterKeyboardProduct(1);
          return;
        }

        if (key === 'ArrowUp') {
          event.preventDefault();
          moveCounterKeyboardProduct(-1);
          return;
        }

        if (key === 'Enter' || key === '+' || key === '=') {
          event.preventDefault();
          addHighlightedCounterProduct();
          return;
        }

        if (key === '-') {
          event.preventDefault();
          decreaseHighlightedCounterProduct();
          return;
        }

        if (lowerKey === 'c') {
          event.preventDefault();
          focusCounterCustomerName();
          return;
        }

        if (lowerKey === 'm') {
          event.preventDefault();
          focusCounterCustomerMobile();
          return;
        }

        if (key === '1') {
          event.preventDefault();
          setCounterPaymentMethod('cash');
          showMessage('Counter payment: Cash selected.');
          return;
        }

        if (key === '2') {
          event.preventDefault();
          setCounterPaymentMethod('upi');
          showMessage('Counter payment: UPI selected.');
          return;
        }

        if (key === '3') {
          event.preventDefault();
          setCounterPaymentMethod('credit');
          showMessage('Counter payment: Credit selected.');
          return;
        }

        return;
      }

      if (cancelOrderTarget) {
        if (key === 'Escape') {
          event.preventDefault();
          closeCancelOrderPopup();
          return;
        }

        if (event.shiftKey && key === 'Delete') {
          event.preventDefault();
          confirmCancelOrder();
        }

        return;
      }

      if (shortcutHelpOpen) {
        if (
          key === 'Escape' ||
          key === '?' ||
          (event.shiftKey && key === '/')
        ) {
          event.preventDefault();
          setShortcutHelpOpen(false);
        }

        return;
      }

      if (
        deliveryFormOpen ||
        resetPinRider ||
        deleteRiderTarget ||
        reassignOrder ||
        selectedDeliveryRiderRecord
      ) {
        return;
      }

      const isOrdersQueueSearchFocused =
        event.target === orderSearchInputRef.current;

      /*
        ORDERS QUEUE SEARCH KEYBOARD MODE

        /           Search focus release
        Esc         Search focus release
        ↑ / ↓       Filtered order select
        Enter       Selected filtered order detail open

        Baaki keys normal typing ke liye input me jayengi.
      */
      if (isOrdersQueueSearchFocused) {
        if (key === '/' || key === 'Escape') {
          event.preventDefault();
          releaseOrdersQueueSearchFocus();
          return;
        }

        if (key === 'ArrowUp') {
          event.preventDefault();
          moveKeyboardSelection(-1);
          return;
        }

        if (key === 'ArrowDown') {
          event.preventDefault();
          moveKeyboardSelection(1);
          return;
        }

        if (key === 'Enter') {
          event.preventDefault();

          const visibleSelectedOrder =
            filteredOrders.find(
              (order) => Number(order.id) === Number(selectedOrderId)
            ) ||
            filteredOrders[0] ||
            null;

          if (!visibleSelectedOrder) {
            showMessage('Matching order nahi mila.', 'error');
            return;
          }

          setSelectedOrderId(visibleSelectedOrder.id);
          keyboardSelectedOrderRef.current = visibleSelectedOrder;
          releaseOrdersQueueSearchFocus();
          openDetail(visibleSelectedOrder);
          return;
        }

        return;
      }

      const isInvoiceProductSearchFocused =
        event.target === invoiceProductSearchInputRef.current;

      if (isInvoiceProductSearchFocused) {
        if (key === '/' || key === 'Escape') {
          event.preventDefault();
          releaseInvoiceProductSearchFocus({ focusFirstControl: true });
          return;
        }

        if (key === 'ArrowDown' || key === 'ArrowRight') {
          event.preventDefault();
          moveInvoiceKeyboardProduct(1);
          return;
        }

        if (key === 'ArrowUp' || key === 'ArrowLeft') {
          event.preventDefault();
          moveInvoiceKeyboardProduct(-1);
          return;
        }

        if (key === 'Enter' || key === '+' || key === '=') {
          event.preventDefault();
          addHighlightedInvoiceProduct();
          return;
        }

        if (key === '-') {
          event.preventDefault();
          decreaseHighlightedInvoiceProduct();
          return;
        }

        return;
      }

      const isInvoiceEditControlFocused =
        currentScreen === 'detail' &&
        event.target?.dataset?.invoiceEditControl;

      if (isInvoiceEditControlFocused) {
        if (key === '/' && !event.shiftKey) {
          event.preventDefault();
          focusInvoiceProductSearch({ selectAll: true });
          return;
        }

        if (key === 'Escape') {
          event.preventDefault();
          event.target?.blur?.();
          window.focus();
          return;
        }

        if (['ArrowDown', 'ArrowRight'].includes(key)) {
          event.preventDefault();
          moveInvoiceEditControl(1, event.target);
          return;
        }

        if (['ArrowUp', 'ArrowLeft'].includes(key)) {
          event.preventDefault();
          moveInvoiceEditControl(-1, event.target);
          return;
        }

        if (
          key === 'Enter' &&
          String(event.target?.type || '').toLowerCase() === 'checkbox'
        ) {
          event.preventDefault();
          event.target.click?.();
          moveInvoiceEditControl(1, event.target);
          return;
        }
      }

      if (
        currentScreen === 'detail' &&
        ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(key)
      ) {
        event.preventDefault();
        focusInvoiceEditControl(
          ['ArrowUp', 'ArrowLeft'].includes(key) ? 'paymentMethod' : 'discount'
        );
        return;
      }

      if (key === 'Escape') {
        event.preventDefault();
        setActionDropdown(null);
        setHistoryFilterOpen(false);

        if (currentScreen === 'detail') {
          closeDetailScreen();
        }

        return;
      }

      if (typing) return;

      if (key === '?' || (event.shiftKey && key === '/')) {
        event.preventDefault();
        setShortcutHelpOpen(true);
        return;
      }

      if (key === '/' && !event.shiftKey) {
        event.preventDefault();
        focusOrdersQueueSearch({ selectAll: false });
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        moveKeyboardSelection(-1);
        return;
      }

      if (key === 'ArrowDown') {
        event.preventDefault();
        moveKeyboardSelection(1);
        return;
      }

      if (key === 'Enter') {
        event.preventDefault();
        queueKeyboardOrderAction('open');
        return;
      }

      if (key === 'Delete') {
        event.preventDefault();

        if (!selectedOrder) {
          showMessage('Cancel ke liye New order select karein.', 'error');
          return;
        }

        openCancelOrderPopup(selectedOrder);
        return;
      }

      if (lowerKey === 'n' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        event.preventDefault();
        openCounterOrder();
        return;
      }

      if (lowerKey === 'a' && !event.shiftKey) {
        event.preventDefault();
        queueKeyboardOrderAction('accept');
        return;
      }

      if (lowerKey === 'k' && event.shiftKey) {
        event.preventDefault();
        queueKeyboardOrderAction('print-kot');
        return;
      }

      if (lowerKey === 'k') {
        event.preventDefault();
        queueKeyboardOrderAction('kitchen');
        return;
      }

      if (lowerKey === 'r') {
        event.preventDefault();
        queueKeyboardOrderAction('ready');
        return;
      }

      if (lowerKey === 'i') {
        event.preventDefault();
        queueKeyboardOrderAction('invoice');
        return;
      }

      if (lowerKey === 's') {
        event.preventDefault();
        queueKeyboardOrderAction('save-invoice');
        return;
      }

      if (lowerKey === 'p' && event.shiftKey) {
        event.preventDefault();
        queueKeyboardOrderAction('final-billing');
        return;
      }

      if (lowerKey === 'p') {
        event.preventDefault();
        queueKeyboardOrderAction('print-bill');
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut, true);

    return () => {
      window.removeEventListener('keydown', handleKeyboardShortcut, true);
    };
  }, [
    isMobile,
    counterOrderOpen,
    cancelOrderTarget,
    shortcutHelpOpen,
    deliveryFormOpen,
    resetPinRider,
    deleteRiderTarget,
    reassignOrder,
    selectedDeliveryRiderRecord,
    currentScreen,
    filteredOrders,
    selectedOrder,
    selectedOrderId,
    activeTab,
    actionLoading,
    counterSaving,
    cancelOrderLoading,
  ]);

  const printBill = (order) => {
    if (!order) {
      showMessage('Print ke liye order select karein.', 'error');
      return;
    }

    const html = buildBillHtml(order);
    printHtml(html, `Bill-${order.id || 'order'}`);
  };

  const restoreDashboardKeyboardFocus = () => {
    if (!printKeyboardFocusRestorePendingRef.current) return;

    printKeyboardFocusRestorePendingRef.current = false;

    const restore = () => {
      try {
        const activeElement = document.activeElement;

        if (
          activeElement &&
          activeElement !== document.body &&
          typeof activeElement.blur === 'function'
        ) {
          activeElement.blur();
        }

        window.focus();

        if (document.body) {
          const previousTabIndex = document.body.getAttribute('tabindex');

          document.body.setAttribute('tabindex', '-1');
          document.body.focus({ preventScroll: true });

          if (previousTabIndex === null) {
            document.body.removeAttribute('tabindex');
          } else {
            document.body.setAttribute('tabindex', previousTabIndex);
          }
        }
      } catch {
        // Focus restore best-effort hai. Print flow ko block nahi karna.
      }
    };

    /*
      Kuch browsers print dialog close hote hi focus immediately return nahi karte.
      Isliye small repeated restore safe hai.
    */
    window.setTimeout(restore, 40);
    window.setTimeout(restore, 220);
  };

  const printHtml = (html, title) => {
    /*
      PERMANENT PRINT SOLUTION:
      - Bill/KOT HTML ko sessionStorage me save karke dedicated /print-preview route par bhejte hain.
      - Phone/PC dono me OrdersReceived UI print nahi hoga, kyunki print route par sirf bill content load hoga.
      - Print/cancel ke baad PrintPreview page /ordersReceived par return karega.
      - Backend me koi change nahi chahiye.
    */
    try {
      printKeyboardFocusRestorePendingRef.current = true;

      const safeTitle =
        String(title || 'Fast Food Print')
          .replace(/[<>]/g, '')
          .trim() || 'Fast Food Print';

      sessionStorage.setItem('vakra_print_html', String(html || ''));
      sessionStorage.setItem('vakra_print_title', safeTitle);
      sessionStorage.setItem('vakra_print_return_url', '/ordersReceived');
      sessionStorage.setItem('vakra_print_created_at', String(Date.now()));

      window.location.assign('/print-preview');
    } catch (err) {
      console.error('printHtml route preview error:', err);
      showMessage(
        err?.message || 'Print preview open nahi hua.',
        'error',
        4200
      );
      restoreDashboardKeyboardFocus();
    }
  };

  const buildKotHtml = (order) => {
    const products = order?.productsParsed || [];
    const invoice = calculateInvoice(order);
    const manualItems = invoice.manualItems || [];

    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const getShopLogo = () => {
      const logo =
        order?.shopLogo ||
        order?.shop_logo ||
        order?.vendor_logo ||
        order?.vendorLogo ||
        order?.shop_image ||
        order?.shopImage ||
        '';

      if (!logo) return '';
      if (/^https?:\/\//i.test(String(logo))) return String(logo);

      return `${BASE_URL}/${String(logo).replace(/^\/+/, '')}`;
    };

    const shopLogo = getShopLogo();

    const shopName =
      order?.shopName ||
      order?.shop_name ||
      order?.vendor_shop_name ||
      order?.vendorName ||
      order?.vendor_name ||
      uiSettings.shopName ||
      'Fast Food Store';

    const shopPhone =
      order?.shopPhone ||
      order?.shop_phone ||
      order?.vendor_phone ||
      order?.vendor_number ||
      order?.shop_number ||
      '';

    const shopAddress =
      order?.shopAddress ||
      order?.shop_address ||
      order?.liveLocation ||
      order?.vendor_address ||
      '';

    const kotNo =
      order?.kot?.kot_no ||
      order?.kot_no ||
      `KOT-${String(order?.id || '').padStart(5, '0')}`;

    const kotItems = [
      ...products.map((item) => ({
        name: item?.name || item?.item_name || 'Item',
        quantity: item?.quantity || item?.qty || 1,
        note: item?.note || item?.notes || item?.special_instruction || '',
        isManual: false,
      })),
      ...manualItems.map((item) => ({
        name: item?.name || item?.item_name || 'Manual Item',
        quantity: item?.quantity || item?.qty || 1,
        note: 'Manual Item',
        isManual: true,
      })),
    ];

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>KOT #${escapeHtml(order?.id || '')}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
            font-family: Arial, Helvetica, sans-serif;
          }

          body {
            width: 58mm;
            min-width: 58mm;
          }

          .kot {
            width: 58mm;
            padding: 2mm 1.6mm 2mm;
            margin: 0 auto;
            background: #ffffff;
          }

          .shop-logo {
            width: 16mm;
            height: 16mm;
            object-fit: contain;
            display: block;
            margin: 0 auto 1mm;
          }

          .shop-name {
            text-align: center;
            font-size: 15px;
            line-height: 1.05;
            font-weight: 900;
            margin: 0 0 0.7mm;
          }

          .shop-meta {
            text-align: center;
            font-size: 9px;
            line-height: 1.18;
            margin: 0;
            word-break: break-word;
          }

          .kot-title {
            text-align: center;
            font-size: 18px;
            line-height: 1;
            font-weight: 900;
            letter-spacing: 0.8px;
            margin: 1mm 0 0.6mm;
          }

          .kot-subtitle {
            text-align: center;
            font-size: 9.5px;
            font-weight: 800;
            margin: 0;
          }

          .dash {
            border-top: 1px dashed #000;
            margin: 2mm 0;
          }

          .meta-row {
            display: grid;
            grid-template-columns: 19mm 1fr;
            gap: 2mm;
            font-size: 10.5px;
            line-height: 1.25;
            margin: 0.7mm 0;
          }

          .meta-row span {
            font-weight: 700;
          }

          .meta-row b {
            text-align: right;
            font-weight: 800;
            word-break: break-word;
          }

          .items-table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          .items-table th {
            font-size: 10px;
            font-weight: 900;
            padding: 0 0 1mm;
            border-bottom: 1px dashed #000;
          }

          .items-table td {
            font-size: 12px;
            vertical-align: top;
            padding: 1.2mm 0.4mm;
          }

          .items-table th:nth-child(1),
          .items-table td:nth-child(1) {
            width: 12%;
            text-align: left;
          }

          .items-table th:nth-child(2),
          .items-table td:nth-child(2) {
            width: 68%;
            text-align: left;
          }

          .items-table th:nth-child(3),
          .items-table td:nth-child(3) {
            width: 20%;
            text-align: right;
          }

          .item-name {
            display: block;
            font-weight: 900;
            line-height: 1.15;
            word-break: break-word;
          }

          .item-note {
            display: block;
            font-size: 9px;
            line-height: 1.1;
            font-weight: 700;
            margin-top: 0.5mm;
            word-break: break-word;
          }

          .manual {
            font-weight: 900;
          }

          .kitchen-note {
            font-size: 10px;
            line-height: 1.25;
            font-weight: 800;
            word-break: break-word;
          }

          .footer {
            text-align: center;
            font-size: 10px;
            margin-top: 2mm;
            font-weight: 900;
          }

          .screen-actions {
            display: none;
          }

          @page {
            size: 58mm auto;
            margin: 0;
          }

          @media print {
            html,
            body {
              width: 58mm !important;
              min-width: 58mm !important;
              max-width: 58mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
            }

            .kot {
              width: 58mm !important;
              margin: 0 !important;
              padding: 2mm 1.6mm !important;
              box-shadow: none !important;
              border: none !important;
            }

            .screen-actions {
              display: none !important;
            }
          }

          @media screen {
            body {
              width: 100%;
              min-width: 0;
              background: #f3f4f6;
              padding: 10px 0;
            }

            .kot {
              width: min(94vw, 360px);
              background: #ffffff;
              box-shadow: 0 12px 35px rgba(15, 23, 42, 0.18);
            }

            .screen-actions {
              width: min(94vw, 360px);
              margin: 8px auto 0;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }

            .screen-actions button {
              min-height: 42px;
              border: none;
              border-radius: 7px;
              font-size: 17px;
              font-weight: 900;
              cursor: pointer;
            }

            .back-btn {
              background: #e5e7eb;
              color: #111827;
            }

            .print-btn {
              background: #2563eb;
              color: #ffffff;
            }
          }
        </style>
      </head>
      <body>
        <div class="kot">
          ${shopLogo ? `<img class="shop-logo" src="${escapeHtml(shopLogo)}" alt="Logo" />` : ''}
          <h1 class="shop-name">${escapeHtml(shopName)}</h1>
          ${shopAddress ? `<p class="shop-meta">${escapeHtml(shopAddress)}</p>` : ''}
          ${shopPhone ? `<p class="shop-meta">Ph: ${escapeHtml(shopPhone)}</p>` : ''}

          <div class="dash"></div>

          <div class="kot-title">KOT</div>
          <p class="kot-subtitle">Kitchen Order Ticket</p>

          <div class="dash"></div>

          <div class="meta-row">
            <span>KOT No</span>
            <b>${escapeHtml(kotNo)}</b>
          </div>
          <div class="meta-row">
            <span>Order</span>
            <b>#${escapeHtml(order?.id || '-')}</b>
          </div>
          <div class="meta-row">
            <span>Date</span>
            <b>${escapeHtml(formatDateTime(order?.created_at || new Date()))}</b>
          </div>
          <div class="meta-row">
            <span>Mode</span>
            <b>${escapeHtml(MODE_LABELS[order?.displayMode] || 'Order')}</b>
          </div>
          ${
            order?.table_number
              ? `
                <div class="meta-row">
                  <span>Table</span>
                  <b>${escapeHtml(order.table_number)}</b>
                </div>
              `
              : ''
          }
          <div class="meta-row">
            <span>Customer</span>
            <b>${escapeHtml(order?.customerName || 'Walk-in')}</b>
          </div>

          <div class="dash"></div>

          <table class="items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              ${
                kotItems.length
                  ? kotItems
                      .map(
                        (item, index) => `
                          <tr>
                            <td>${index + 1}</td>
                            <td>
                              <span class="item-name ${item.isManual ? 'manual' : ''}">${escapeHtml(item.name)}</span>
                              ${item.note ? `<span class="item-note">${escapeHtml(item.note)}</span>` : ''}
                            </td>
                            <td><b>x${escapeHtml(item.quantity)}</b></td>
                          </tr>
                        `
                      )
                      .join('')
                  : `
                    <tr>
                      <td colspan="3" style="text-align:center;padding:3mm 0;">No items</td>
                    </tr>
                  `
              }
            </tbody>
          </table>

          ${
            invoice.notes
              ? `
                <div class="dash"></div>
                <div class="kitchen-note">
                  Note: ${escapeHtml(invoice.notes)}
                </div>
              `
              : ''
          }

          ${manualItems.length ? '<div class="dash"></div><div class="footer">Manual items included in KOT</div>' : ''}

          <div class="dash"></div>
          <div class="footer">Send to Kitchen</div>
        </div>

        <div class="screen-actions">
          <button class="back-btn" onclick="window.close()">Back to Orders</button>
          <button class="print-btn" onclick="window.print()">Print Bill</button>
        </div>
      </body>
      </html>
    `;
  };

  const buildBillHtml = (order) => {
    const products = order?.productsParsed || [];
    const invoice = calculateInvoice(order);

    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const receiptLine = (label, value) => `
      <div class="meta-row">
        <span>${escapeHtml(label)}</span>
        <b>${escapeHtml(value)}</b>
      </div>
    `;

    const money = (value) => `₹${Math.round(toNumber(value, 0)).toLocaleString('en-IN')}`;

    const getShopLogo = () => {
      const logo =
        order?.shopLogo ||
        order?.shop_logo ||
        order?.vendor_logo ||
        order?.vendorLogo ||
        order?.shop_image ||
        order?.shopImage ||
        '';

      if (!logo) return '';
      if (/^https?:\/\//i.test(String(logo))) return String(logo);

      return `${BASE_URL}/${String(logo).replace(/^\/+/, '')}`;
    };

    const shopLogo = getShopLogo();

    const shopName =
      order?.shopName ||
      order?.shop_name ||
      order?.vendor_shop_name ||
      order?.vendorName ||
      order?.vendor_name ||
      uiSettings.shopName ||
      'Fast Food Store';

    const shopAddress =
      order?.shopAddress ||
      order?.shop_address ||
      order?.liveLocation ||
      order?.vendor_address ||
      '';

    const shopPhone =
      order?.shopPhone ||
      order?.shop_phone ||
      order?.vendor_phone ||
      order?.vendor_number ||
      order?.shop_number ||
      '';

    const billNo =
      order?.bill_no ||
      order?.invoice_no ||
      order?.bill?.bill_no ||
      `INV-${String(order?.id || '').padStart(5, '0')}`;

    const orderNo = order?.id || '-';
    const paymentLabel =
      invoice.paymentStatus === 'paid'
        ? 'Paid'
        : invoice.paymentStatus === 'partial'
          ? 'Partial'
          : invoice.paymentStatus === 'cod'
            ? 'COD'
            : 'Credit';

    const itemRows = [
      ...products.map((item) => {
        const qty = toNumber(item?.quantity || item?.qty, 1);
        const rate = toNumber(item?.price ?? item?.selling_price ?? item?.rate, 0);

        return {
          name: item?.name || item?.item_name || 'Item',
          desc: item?.unit || item?.size || item?.variant || item?.description || '',
          qty,
          rate,
          total: qty * rate,
          manual: false,
        };
      }),
      ...(invoice.manualItems || []).map((item) => {
        const qty = toNumber(item?.quantity || item?.qty, 1);
        const rate = toNumber(item?.price ?? item?.rate, 0);

        return {
          name: item?.name || item?.item_name || 'Manual Item',
          desc: 'Manual',
          qty,
          rate,
          total: qty * rate,
          manual: true,
        };
      }),
    ];

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill #${escapeHtml(orderNo)}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
            font-family: Arial, Helvetica, sans-serif;
          }

          body {
            width: 58mm;
            min-width: 58mm;
          }

          .receipt {
            width: 58mm;
            padding: 2mm 1.6mm 2mm;
            margin: 0 auto;
            background: #ffffff;
          }

          .shop-logo {
            width: 19mm;
            height: 19mm;
            object-fit: contain;
            display: block;
            margin: 0 auto 1mm;
          }

          .shop-name {
            text-align: center;
            font-size: 18px;
            line-height: 1.05;
            font-weight: 900;
            margin: 0 0 1mm;
          }

          .shop-meta {
            text-align: center;
            font-size: 10px;
            line-height: 1.25;
            margin: 0;
            word-break: break-word;
          }

          .dash {
            border-top: 1px dashed #000;
            margin: 2mm 0;
          }

          .meta-row {
            display: grid;
            grid-template-columns: 19mm 1fr;
            gap: 2mm;
            font-size: 10.5px;
            line-height: 1.25;
            margin: 0.6mm 0;
          }

          .meta-row span {
            font-weight: 700;
          }

          .meta-row b {
            text-align: right;
            font-weight: 700;
            word-break: break-word;
          }

          .items-table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          .items-table th {
            font-size: 10px;
            font-weight: 800;
            padding: 0 0 1mm;
            border-bottom: 1px dashed #000;
          }

          .items-table td {
            font-size: 10.5px;
            vertical-align: top;
            padding: 1mm 0.4mm;
          }

          .items-table th:nth-child(1),
          .items-table td:nth-child(1) {
            width: 50%;
          }

          .items-table th:nth-child(2),
          .items-table td:nth-child(2) {
            width: 14%;
            text-align: center;
          }

          .items-table th:nth-child(3),
          .items-table td:nth-child(3) {
            width: 16%;
            text-align: right;
          }

          .items-table th:nth-child(4),
          .items-table td:nth-child(4) {
            width: 20%;
            text-align: right;
          }

          .item-name {
            display: block;
            font-weight: 800;
            line-height: 1.15;
            word-break: break-word;
          }

          .item-desc {
            display: block;
            font-size: 9px;
            line-height: 1.1;
            color: #111;
            margin-top: 0.5mm;
            word-break: break-word;
          }

          .manual-tag {
            font-size: 8.5px;
            font-weight: 800;
          }

          .totals {
            margin-top: 1mm;
          }

          .total-row {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 2mm;
            font-size: 10.5px;
            line-height: 1.25;
            margin: 0.7mm 0;
          }

          .total-row span {
            font-weight: 700;
          }

          .total-row b {
            font-weight: 800;
            text-align: right;
          }

          .grand-total {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 2mm;
            align-items: baseline;
            font-size: 18px;
            font-weight: 900;
            margin-top: 1.6mm;
          }

          .grand-total b {
            font-size: 18px;
            text-align: right;
          }

          .footer {
            text-align: center;
            font-size: 10px;
            margin-top: 2mm;
            font-weight: 700;
          }

          .screen-actions {
            display: none;
          }

          @page {
            size: 58mm auto;
            margin: 0;
          }

          @media print {
            html,
            body {
              width: 58mm !important;
              min-width: 58mm !important;
              max-width: 58mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
            }

            .receipt {
              width: 58mm !important;
              margin: 0 !important;
              padding: 2mm 1.6mm !important;
              box-shadow: none !important;
              border: none !important;
            }

            .screen-actions {
              display: none !important;
            }
          }

          @media screen {
            body {
              width: 100%;
              min-width: 0;
              background: #f3f4f6;
              padding: 10px 0;
            }

            .receipt {
              width: min(94vw, 360px);
              min-height: auto;
              background: #ffffff;
              box-shadow: 0 12px 35px rgba(15, 23, 42, 0.18);
            }

            .screen-actions {
              width: min(94vw, 360px);
              margin: 8px auto 0;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }

            .screen-actions button {
              min-height: 42px;
              border: none;
              border-radius: 7px;
              font-size: 17px;
              font-weight: 900;
              cursor: pointer;
            }

            .back-btn {
              background: #e5e7eb;
              color: #111827;
            }

            .print-btn {
              background: #2563eb;
              color: #ffffff;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          ${shopLogo ? `<img class="shop-logo" src="${escapeHtml(shopLogo)}" alt="Logo" />` : ''}
          <h1 class="shop-name">${escapeHtml(shopName)}</h1>
          ${shopAddress ? `<p class="shop-meta">${escapeHtml(shopAddress)}</p>` : ''}
          ${shopPhone ? `<p class="shop-meta">Ph: ${escapeHtml(shopPhone)}</p>` : ''}

          <div class="dash"></div>

          ${receiptLine('Bill No', billNo)}
          ${receiptLine('Order', `#${orderNo}`)}
          ${receiptLine('Mode', MODE_LABELS[order?.displayMode] || 'Order')}
          ${order?.displayMode === 'dine_in' && order?.table_number ? receiptLine('Table No', order.table_number) : ''}
          ${receiptLine('Date', formatDateTime(order?.created_at || new Date()))}
          ${receiptLine('Customer', order?.customerName || 'Walk-in Customer')}
          ${order?.customerMobile ? receiptLine('Phone', order.customerMobile) : ''}
          ${receiptLine('Payment', paymentLabel)}

          <div class="dash"></div>

          <table class="items-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amt</th>
              </tr>
            </thead>
            <tbody>
              ${
                itemRows.length
                  ? itemRows
                      .map(
                        (item) => `
                          <tr>
                            <td>
                              <span class="item-name">${escapeHtml(item.name)}</span>
                              ${item.desc ? `<span class="item-desc">${escapeHtml(item.desc)}</span>` : ''}
                              ${item.manual ? `<span class="item-desc manual-tag">Manual Item</span>` : ''}
                            </td>
                            <td>${escapeHtml(item.qty)}</td>
                            <td>${escapeHtml(Math.round(item.rate))}</td>
                            <td>${escapeHtml(Math.round(item.total))}</td>
                          </tr>
                        `
                      )
                      .join('')
                  : `
                    <tr>
                      <td colspan="4" style="text-align:center;padding:3mm 0;">No items</td>
                    </tr>
                  `
              }
            </tbody>
          </table>

          <div class="dash"></div>

          <div class="totals">
            <div class="total-row">
              <span>Subtotal</span>
              <b>${money(invoice.subtotal)}</b>
            </div>
            ${
              invoice.discount > 0
                ? `
                  <div class="total-row">
                    <span>Discount${invoice.discountType === 'percentage' ? ` (${escapeHtml(invoice.discountValue)}%)` : ' (Amount)'}</span>
                    <b>-${money(invoice.discount)}</b>
                  </div>
                `
                : ''
            }
            ${
              invoice.coinDiscount > 0
                ? `
                  <div class="total-row">
                    <span>Coin Discount${invoice.coinsRedeemed > 0 ? ` (${escapeHtml(invoice.coinsRedeemed)} coins)` : ''}</span>
                    <b>-${money(invoice.coinDiscount)}</b>
                  </div>
                `
                : ''
            }
            <div class="total-row">
              <span>Taxable</span>
              <b>${money(invoice.taxable)}</b>
            </div>
            <div class="total-row">
              <span>GST (${escapeHtml(invoice.gstPercent)}%)</span>
              <b>${money(invoice.gstTotal)}</b>
            </div>
            <div class="total-row">
              <span>Delivery</span>
              <b>${money(invoice.deliveryCharge)}</b>
            </div>
            <div class="total-row">
              <span>Packing</span>
              <b>${money(invoice.packingCharge)}</b>
            </div>
            <div class="total-row">
              <span>Round Off</span>
              <b>${money(invoice.roundOff)}</b>
            </div>
          </div>

          <div class="dash"></div>

          <div class="grand-total">
            <span>Total</span>
            <b>${money(invoice.finalTotal)}</b>
          </div>

          ${
            invoice.paymentStatus === 'partial' || invoice.dueAmount > 0
              ? `
                <div class="dash"></div>
                <div class="total-row">
                  <span>Paid</span>
                  <b>${money(invoice.amountPaid)}</b>
                </div>
                <div class="total-row">
                  <span>Due</span>
                  <b>${money(invoice.dueAmount)}</b>
                </div>
              `
              : ''
          }

          <div class="dash"></div>

          <div class="footer">Thank you! Visit Again.</div>
        </div>

        <div class="screen-actions">
          <button class="back-btn" onclick="window.close()">Back to Orders</button>
          <button class="print-btn" onclick="window.print()">Print Bill</button>
        </div>
      </body>
      </html>
    `;
  };

  const getModeBadgeClass = (mode) => {
    if (mode === 'delivery') return 'delivery';
    if (mode === 'takeaway') return 'takeaway';
    return 'dine';
  };

  const getOrderStepIndex = (status) => {
    const keys = ['new', 'accepted', 'preparing', 'ready', 'done'];
    return Math.max(0, keys.indexOf(status));
  };

  const visibleStatusTabs = STATUS_TABS.filter((tab) => {
    if (!isStaffLogin) return true;
    if (canAccess('ordersView')) return true;
    if (canUseKitchenTools) return ['preparing', 'ready'].includes(tab.key);
    if (canUseBillingTools) return tab.key === 'ready';
    return tab.key === 'all';
  });

  const renderStatusPills = () => (
    <div className="ff-image-status-tabs">
      {visibleStatusTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={statusFilter === tab.key ? 'active' : ''}
          onClick={() => setStatusFilter(tab.key)}
        >
          {tab.label}
          {tab.key === 'new' && summary.newCount ? <span>{summary.newCount}</span> : null}
          {tab.key === 'preparing' && summary.kitchenCount ? <span>{summary.kitchenCount}</span> : null}
          {tab.key === 'ready' && summary.readyCount ? <span>{summary.readyCount}</span> : null}
        </button>
      ))}
    </div>
  );

  const renderSummaryCards = () => (
    <div className="ff-image-counts">
      <div className="ff-image-count-card orange">
        <span>📋</span>
        <strong>{summary.newCount}</strong>
        <small>New</small>
      </div>
      <div className="ff-image-count-card blue">
        <span>👨‍🍳</span>
        <strong>{summary.kitchenCount}</strong>
        <small>Kitchen</small>
      </div>
      <div className="ff-image-count-card green">
        <span>🍽️</span>
        <strong>{summary.readyCount}</strong>
        <small>Ready</small>
      </div>
      <div className="ff-image-count-card dark">
        <span>🧾</span>
        <strong>{summary.completedCount}</strong>
        <small>Completed</small>
      </div>
    </div>
  );

  const getQueueCardCurrentStatusLabel = (order) => {
    const status = String(order?.displayStatus || 'new').toLowerCase();

    /*
      Queue card par sirf CURRENT STATUS dikhana hai.
      Shortcut workflow same rahega:
      A -> Accepted, K -> Kitchen, R -> Ready.
      Button click se bhi next workflow action execute hoga, lekin label
      current stage hi rahega taaki vendor confuse na ho.
    */
    if (status === 'new') return 'New';
    if (status === 'accepted') return 'Accepted';
    if (status === 'preparing') return 'Kitchen';
    if (status === 'ready') return 'Ready';

    return STATUS_FLOW[status]?.longLabel || 'Open';
  };

  const renderOrderCard = (order) => {
    const products = order.productsParsed || [];
    const queueCardCurrentStatusLabel = getQueueCardCurrentStatusLabel(order);
    const amount = order.displayTotal;
    const isFreshOrder =
      activeTab === 'active' && order.displayStatus === 'new' && !order.kotSent;
    const isKotRunning = activeTab === 'active' && order.kotSent && !order.locked;
    const priorityClass = isFreshOrder
      ? 'priority-new-order'
      : isKotRunning
        ? 'priority-kot-running'
        : order.runningTable && activeTab === 'active' && !order.locked
          ? 'priority-running-table'
          : '';
    const canCancelFromQueue =
      canAccess('cancelOrder') &&
      activeTab === 'active' &&
      !order.locked &&
      order.displayStatus === 'new';

    const openCard = () => {
      setSelectedOrderId(order.id);

      if (isMobile) {
        openDetail(order);
      }
    };

    return (
      <article
        key={order.id}
        data-keyboard-order-id={order.id}
        className={`ff-image-order-card ${selectedOrder?.id === order.id ? 'selected' : ''} ${order.displayStatus} ${priorityClass} ${order.counterCreated ? 'counter-created-order' : ''}`}
        role="button"
        tabIndex={0}
        onClick={openCard}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openCard();
          }
        }}
      >
        <div className="ff-image-order-top">
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '7px',
              }}
            >
              <strong>#ORD-{order.id}</strong>

              {isFreshOrder ? (
                <span className="ff-order-priority-badge new-order">NEW</span>
              ) : null}

              {order.counterCreated ? (
                <span className="ff-order-priority-badge counter-order">COUNTER</span>
              ) : null}

              {isKotRunning ? (
                <span className="ff-order-priority-badge kot-running">KOT PRINTED</span>
              ) : null}

              {order.runningTable && !isFreshOrder && !isKotRunning && activeTab === 'active' ? (
                <span className="ff-order-priority-badge running-table">TABLE RUNNING</span>
              ) : null}

              {order.displayStatus === 'cancelled' ? (
                <span
                  className="ff-history-order-status cancelled"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '3px 8px',
                    borderRadius: '999px',
                    background: '#fee2e2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca',
                    fontSize: '11px',
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  ✕ Cancelled
                </span>
              ) : activeTab === 'history' ? (
                <span
                  className={`ff-history-order-status ${order.displayStatus}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '3px 8px',
                    borderRadius: '999px',
                    background: '#dcfce7',
                    color: '#166534',
                    border: '1px solid #bbf7d0',
                    fontSize: '11px',
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  {STATUS_FLOW[order.displayStatus]?.longLabel || 'Completed'}
                </span>
              ) : null}
            </div>

            <h4>{order.customerName}</h4>
          </div>

          <div className="ff-order-card-top-actions">
            <span className="ff-order-time-highlight">{formatTime(order.created_at)}</span>

            {canCancelFromQueue ? (
              <button
                type="button"
                className="ff-order-cancel-icon"
                title={`Cancel order #${order.id}`}
                aria-label={`Cancel order ${order.id}`}
                onClick={(event) => openCancelOrderPopup(order, event)}
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>

        <div className="ff-image-order-mid">
          <span className={`ff-mode-badge ${getModeBadgeClass(order.displayMode)}`}>
            {order.displayMode === 'dine_in' ? '🍽️' : order.displayMode === 'delivery' ? '🛵' : '🛍️'} {MODE_LABELS[order.displayMode]}
          </span>
          {order.displayMode === 'dine_in' && order.table_number ? (
            <span className="ff-table-highlight">Table {order.table_number}</span>
          ) : null}
          <span className="ff-items-dot">• {(products.length || order.itemCount) + ((getInvoiceState(order.id)?.manualItems || []).reduce((sum, item) => sum + toNumber(item?.quantity || item?.qty, 1), 0))} Items</span>
          <b>{formatMoney(amount)}</b>
        </div>

        <div className="ff-image-order-bottom">
          <span className={`ff-payment-mini ${order.displayPaymentStatus}`}>
            {order.displayPaymentStatus === 'paid'
              ? 'Paid'
              : order.displayPaymentStatus === 'cod'
                ? 'COD'
                : order.displayPaymentStatus === 'partial'
                  ? 'Partial'
                  : 'Credit'}
          </span>

          {activeTab === 'active' && !order.locked ? (
            <button
              type="button"
              className={`ff-quick-accept ${order.displayStatus || 'new'}`}
              onClick={(e) => {
                e.stopPropagation();
                if (order.displayStatus === 'ready') {
                  openDetail(order);
                } else {
                  handleStepAction(order);
                }
              }}
            >
              {queueCardCurrentStatusLabel}
            </button>
          ) : (
            <button
              type="button"
              className={`ff-quick-open ${
                order.displayStatus === 'cancelled' ? 'cancelled' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                openDetail(order);
              }}
              style={
                order.displayStatus === 'cancelled'
                  ? {
                      background: '#fee2e2',
                      color: '#b91c1c',
                      borderColor: '#fecaca',
                      fontWeight: 800,
                    }
                  : undefined
              }
            >
              {order.displayStatus === 'cancelled' ? 'Cancelled' : 'Open'}
            </button>
          )}
        </div>
      </article>
    );
  };

  const renderOrderQueue = () => (
    <section className="ff-image-queue">
      <div className="ff-image-queue-head">
        <div>
          <h3 title="Orders Queue (Alt + Q)">Orders Queue</h3>
          <p>Fast, simple order flow</p>
        </div>

        <button
          type="button"
          className="ff-refresh-btn"
          title="Refresh Orders (Alt + R)"
          onClick={() => fetchOrders({ silent: false })}
        >
          ↻
        </button>
      </div>

      <div className="ff-image-main-tabs ff-history-main-tabs">
        <button
          type="button"
          className={activeTab === 'active' ? 'active' : ''}
          title="Active Orders (Alt + A)"
          onClick={() => {
            setActiveTab('active');
            setHistoryFilterOpen(false);
          }}
        >
          Active
        </button>

        {canAccess('recordsView') ? (
        <div className="ff-history-tab-wrap">
          <button
            type="button"
            className={activeTab === 'history' ? 'active' : ''}
            title="History (Alt + H)"
            onClick={() => setActiveTab('history')}
          >
            History
          </button>

          <button
            type="button"
            className={`ff-history-filter-icon ${historyFilterOpen ? 'open' : ''}`}
            title="History date filter"
            onClick={(e) => {
              e.stopPropagation();
              setActiveTab('history');
              setHistoryFilterOpen((prev) => !prev);
            }}
          >
            📅
          </button>
        </div>
        ) : null}
      </div>

      {activeTab === 'history' && historyFilterOpen ? (
        <div className="ff-history-filter-box professional" ref={historyFilterRef}>
          <div className="ff-history-filter-head">
            <div>
              <strong>Filter History</strong>
              <span>{getHistoryFilterLabel()}</span>
            </div>

            <button
              type="button"
              className="ff-history-close"
              onClick={() => setHistoryFilterOpen(false)}
              aria-label="Close history filter"
            >
              ×
            </button>
          </div>

          <label className="ff-history-select-label">
            Period
            <select
              value={historyDateFilter.type}
              onChange={(e) =>
                setHistoryDateFilter({
                  type: e.target.value,
                  customFrom:
                    e.target.value === 'custom' ? historyDateFilter.customFrom || '' : '',
                  customTo:
                    e.target.value === 'custom' ? historyDateFilter.customTo || '' : '',
                })
              }
            >
              <option value="all">All completed orders</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7">Last 7 days</option>
              <option value="custom">Custom date range</option>
            </select>
          </label>

          {historyDateFilter.type === 'custom' ? (
            <div className="ff-history-range-row">
              <label>
                From
                <input
                  type="date"
                  value={historyDateFilter.customFrom}
                  onChange={(e) =>
                    setHistoryDateFilter((prev) => ({
                      ...prev,
                      type: 'custom',
                      customFrom: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                To
                <input
                  type="date"
                  value={historyDateFilter.customTo}
                  onChange={(e) =>
                    setHistoryDateFilter((prev) => ({
                      ...prev,
                      type: 'custom',
                      customTo: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
          ) : null}

          <div className="ff-history-filter-actions">
            <button
              type="button"
              className="secondary"
              onClick={() =>
                setHistoryDateFilter({
                  type: 'all',
                  customFrom: '',
                  customTo: '',
                })
              }
            >
              Reset
            </button>

            <button
              type="button"
              className="primary"
              onClick={() => setHistoryFilterOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}

      {renderStatusPills()}

      <div className="ff-image-search">
        <span>🔎</span>
        <input
          ref={orderSearchInputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search table, amount, name, mobile, product, order ID..."
          title="/ Search focus · / again release · ↑↓ Select · Enter Open"
        />
      </div>

      {renderSummaryCards()}

      <div className="ff-image-order-list">
        {loading ? (
          <div className="ff-image-empty">Loading orders...</div>
        ) : filteredOrders.length ? (
          filteredOrders.map(renderOrderCard)
        ) : (
          <div className="ff-image-empty">No orders found.</div>
        )}
      </div>
    </section>
  );


  const renderCounterOrderModal = () => {
    if (!counterOrderOpen) return null;

    const normalizedCustomerSearch = String(counterCustomerSearch || '')
      .trim()
      .toLowerCase();
    const visibleCounterCustomers = counterCustomers.filter((customer) => {
      if (!normalizedCustomerSearch) return true;

      return [customer.customer_name, customer.customer_mobile]
        .some((value) =>
          String(value || '').toLowerCase().includes(normalizedCustomerSearch)
        );
    });

    return (
      <div
        className="ff-counter-order-overlay"
        role="presentation"
        onClick={closeCounterOrder}
      >
        <section
          ref={counterOrderDrawerRef}
          className="ff-counter-order-drawer"
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          aria-labelledby="ff-counter-order-title"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="ff-counter-order-head">
            <div>
              <span>Walk-in customer</span>
              <h2 id="ff-counter-order-title">Create Counter Order</h2>
            </div>

            <button
              type="button"
              className="ff-counter-close"
              onClick={closeCounterOrder}
              disabled={counterSaving}
              aria-label="Close counter order"
            >
              ×
            </button>
          </header>

          <button
            type="button"
            className="ff-counter-category-fab"
            onClick={() => setCounterCategorySidebarOpen(true)}
            aria-label="Open counter product categories"
            title="Open categories"
          >
            <span aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </button>

          <div
            className={`ff-counter-category-backdrop ${counterCategorySidebarOpen ? 'open' : ''}`}
            role="presentation"
            onClick={() => setCounterCategorySidebarOpen(false)}
          />

          <aside className={`ff-counter-category-panel ${counterCategorySidebarOpen ? 'open' : ''}`}>
            <div className="ff-counter-category-panel-head">
              <div>
                <span>Product</span>
                <h3>Categories</h3>
              </div>
              <button
                type="button"
                onClick={() => setCounterCategorySidebarOpen(false)}
                aria-label="Close category"
              >
                ×
              </button>
            </div>

            <div className="ff-counter-category-list">
              {counterCategories.map((categoryName) => (
                <button
                  key={categoryName}
                  type="button"
                  className={counterCategoryFilter === categoryName ? 'active' : ''}
                  onClick={() => selectCounterCategory(categoryName)}
                >
                  <span>{categoryName}</span>
                  <b>{getCounterCategoryCount(categoryName)}</b>
                </button>
              ))}
            </div>
          </aside>

          <div className="ff-counter-scroll">
            <section className="ff-counter-section ff-counter-mode-section">
              <div className="ff-counter-section-title">
                <b>1</b>
                <strong>Order Mode</strong>
              </div>

              <div className="ff-counter-mode-grid">
                <button
                  type="button"
                  className={counterOrderMode === 'dine_in' ? 'active' : ''}
                  onClick={() => {
                    setCounterOrderMode('dine_in');
                    focusCounterTable();
                  }}
                >
                  🍽️ Dine-in
                </button>

                <button
                  type="button"
                  className={counterOrderMode === 'takeaway' ? 'active' : ''}
                  onClick={() => {
                    setCounterOrderMode('takeaway');
                    setCounterTableNumber('');
                  }}
                >
                  🛍️ Takeaway
                </button>
              </div>
            </section>

            {counterOrderMode === 'dine_in' ? (
              <section className="ff-counter-section ff-counter-table-section">
                <div className="ff-counter-section-title">
                  <b>2</b>
                  <strong>Table Number *</strong>
                </div>

                <select
                  ref={counterTableSelectRef}
                  className="ff-counter-input"
                  value={counterTableNumber}
                  onChange={(event) => {
                    const nextTable = event.target.value;
                    setCounterTableNumber(nextTable);
                    counterTableKeyboardBufferRef.current = '';
                    window.clearTimeout(counterTableKeyboardResetTimerRef.current);

                    if (nextTable) {
                      focusCounterSearch({ selectAll: false });
                    }
                  }}
                >
                  <option value="">Select Table</option>
                  {Array.from({ length: 30 }, (_, index) => String(index + 1)).map(
                    (table) => (
                      <option key={table} value={table}>
                        Table {table}
                      </option>
                    )
                  )}
                </select>
              </section>
            ) : null}

            <section className="ff-counter-section ff-counter-cart-section">
              <div className="ff-counter-section-title">
                <b>{counterOrderMode === 'dine_in' ? '3' : '2'}</b>
                <strong>Cart / Selected Items</strong>
              </div>

              <div className="ff-counter-cart">
                {counterCart.length ? (
                  counterCart.map((item) => (
                    <article key={item.id} className="ff-counter-cart-row">
                      <div>
                        <strong>{item.name}</strong>
                        <span>
                          {formatMoney(item.selling_price || item.price)} × {item.quantity}
                        </span>
                      </div>

                      <b>
                        {formatMoney(
                          Number(item.selling_price || item.price || 0) *
                            Number(item.quantity || 0)
                        )}
                      </b>

                      <button
                        type="button"
                        onClick={() => removeCounterProduct(item.id)}
                        aria-label={`Remove ${item.name}`}
                      >
                        ×
                      </button>
                    </article>
                  ))
                ) : (
                  <div className="ff-counter-empty">Abhi koi item add nahi hua.</div>
                )}
              </div>

              <div className="ff-counter-subtotal">
                <span>Subtotal</span>
                <strong>{formatMoney(counterCartSubtotal)}</strong>
              </div>
            </section>

            <section className="ff-counter-section ff-counter-menu-section">
              <div className="ff-counter-section-title">
                <b>{counterOrderMode === 'dine_in' ? '4' : '3'}</b>
                <strong>Search Items</strong>
                <small className="ff-counter-active-category">{counterCategoryFilter}</small>
              </div>

              <div className="ff-counter-product-topbar">
                <button
                  type="button"
                  className="ff-counter-category-open-btn"
                  onClick={() => setCounterCategorySidebarOpen(true)}
                >
                  <span aria-hidden="true">»</span>
                  Category
                </button>

                <button
                  type="button"
                  className="ff-counter-category-clear-btn"
                  onClick={() => selectCounterCategory('All')}
                >
                  All
                </button>
              </div>

              <div className="ff-counter-search">
                <span>⌕</span>
                <input
                  ref={counterSearchInputRef}
                  value={counterSearch}
                  onChange={(event) => {
                    setCounterSearch(event.target.value);
                    counterKeyboardProductIndexRef.current = -1;
                    setCounterKeyboardProductIndex(-1);
                  }}
                  placeholder="Search item, price or initials... e.g. pmp, 120"
                />
              </div>

              <div className="ff-counter-products">
                {counterProductsLoading ? (
                  <div className="ff-counter-empty">Products loading...</div>
                ) : counterFilteredProducts.length ? (
                  counterFilteredProducts.map((product, productIndex) => {
                    const cartItem = counterCart.find(
                      (item) => Number(item.id) === Number(product.id)
                    );
                    const qty = Number(cartItem?.quantity || 0);

                    return (
                      <article
                        key={product.id}
                        data-counter-keyboard-product-index={productIndex}
                        className="ff-counter-product-card"
                        onMouseEnter={() => {
                          counterKeyboardProductIndexRef.current = productIndex;
                          setCounterKeyboardProductIndex(productIndex);
                        }}
                        onClick={() =>
                          setCounterKeyboardIndexSafe(productIndex, { scroll: false })
                        }
                        style={
                          counterKeyboardProductIndex === productIndex
                            ? {
                                borderColor: '#2563eb',
                                background: '#eff6ff',
                                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
                              }
                            : undefined
                        }
                      >
                        <div className="ff-counter-product-icon">
                          {(product.name || 'F').slice(0, 1).toUpperCase()}
                        </div>

                        <div>
                          <strong>{product.name}</strong>
                          <span>{product.category || 'Others'} · {formatMoney(product.selling_price || product.price)}</span>
                        </div>

                        {qty > 0 ? (
                          <div className="ff-counter-qty">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                decreaseCounterProduct(product.id);
                              }}
                            >
                              −
                            </button>
                            <b>{qty}</b>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                addCounterProduct(product);
                              }}
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="ff-counter-add"
                            onClick={(event) => {
                              event.stopPropagation();
                              addCounterProduct(product);
                            }}
                          >
                            Add
                          </button>
                        )}
                      </article>
                    );
                  })
                ) : (
                  <div className="ff-counter-empty">Is category/search me matching item nahi mila.</div>
                )}
              </div>
            </section>

            <section className="ff-counter-section ff-counter-customer-section">
              <div className="ff-counter-section-title">
                <b>{counterOrderMode === 'dine_in' ? '5' : '4'}</b>
                <strong>Customer <small>(Optional)</small></strong>
              </div>

              <div className="ff-counter-customer-actions">
                <button
                  type="button"
                  className={counterCustomerPanel === 'add' ? 'primary active' : 'secondary'}
                  onClick={() => {
                    const willOpen = counterCustomerPanel !== 'add';
                    setCounterCustomerPanel(willOpen ? 'add' : '');

                    if (willOpen) {
                      setCounterSelectedCustomerId('');
                      setCounterCustomerName('');
                      setCounterCustomerMobile('');
                      window.setTimeout(() => counterCustomerNameInputRef.current?.focus?.(), 0);
                    }
                  }}
                >
                  + Add Customer
                </button>

                <button
                  type="button"
                  className={counterCustomerPanel === 'show' ? 'primary active' : 'secondary'}
                  onClick={() => {
                    const willOpen = counterCustomerPanel !== 'show';
                    setCounterCustomerPanel(willOpen ? 'show' : '');
                    if (willOpen) fetchCounterCustomers();
                  }}
                >
                  Show Customers
                </button>
              </div>

              {counterSelectedCustomerId && counterCustomerPanel !== 'add' ? (
                <div className="ff-counter-selected-customer">
                  <strong>{counterCustomerName}</strong>
                  {counterCustomerMobile ? <span>{counterCustomerMobile}</span> : null}
                  <button
                    type="button"
                    onClick={() => selectCounterCustomer('')}
                    aria-label="Remove selected customer"
                  >
                    ×
                  </button>
                </div>
              ) : null}

              {counterCustomerPanel === 'add' ? (
                <div className="ff-counter-customer-add-panel">
                  <div className="ff-counter-two-inputs">
                    <input
                      ref={counterCustomerNameInputRef}
                      className="ff-counter-input"
                      value={counterCustomerName}
                      onChange={(event) => setCounterCustomerName(event.target.value)}
                      placeholder="Customer Name"
                    />

                    <input
                      ref={counterCustomerMobileInputRef}
                      className="ff-counter-input"
                      value={counterCustomerMobile}
                      onChange={(event) =>
                        setCounterCustomerMobile(
                          String(event.target.value || '')
                            .replace(/\D/g, '')
                            .slice(0, 10)
                        )
                      }
                      inputMode="numeric"
                      placeholder="Mobile Number (Optional)"
                    />
                  </div>

                  <button
                    type="button"
                    className="primary ff-counter-save-customer-btn"
                    onClick={saveCounterCustomer}
                    disabled={counterCustomerSaving || !String(counterCustomerName || '').trim()}
                  >
                    {counterCustomerSaving ? 'Saving...' : 'Save Customer'}
                  </button>
                </div>
              ) : null}

              {counterCustomerPanel === 'show' ? (
                <div className="ff-counter-customer-list-panel">
                  <input
                    className="ff-counter-input"
                    value={counterCustomerSearch}
                    onChange={(event) => setCounterCustomerSearch(event.target.value)}
                    placeholder="Name ya mobile search karein"
                  />

                  <select
                    className="ff-counter-input"
                    value={counterSelectedCustomerId}
                    onChange={(event) => selectCounterCustomer(event.target.value)}
                    disabled={counterCustomersLoading}
                    aria-label="Select saved customer"
                  >
                    <option value="">
                      {counterCustomersLoading ? 'Loading...' : 'Customer select karein'}
                    </option>
                    {visibleCounterCustomers.map((customer) => (
                      <option key={customer.id} value={String(customer.id)}>
                        {customer.customer_name}
                        {customer.customer_mobile ? ` • ${customer.customer_mobile}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </section>

            <section className="ff-counter-section ff-counter-payment-section">
              <div className="ff-counter-section-title">
                <b>{counterOrderMode === 'dine_in' ? '6' : '5'}</b>
                <strong>Payment</strong>
              </div>

              <div className="ff-counter-payment-grid">
                {[
                  ['cash', '💵 Cash'],
                  ['upi', '📲 UPI'],
                  ['credit', '📝 Credit'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={counterPaymentMethod === value ? 'active' : ''}
                    onClick={() => setCounterPaymentMethod(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </section>
          </div>

          <footer className="ff-counter-footer">
            <button
              type="button"
              className="secondary"
              onClick={closeCounterOrder}
              disabled={counterSaving}
            >
              Cancel
            </button>

            <button
              type="button"
              className="primary"
              onClick={createCounterOrder}
              disabled={counterSaving}
            >
              {counterSaving ? 'Creating...' : 'Create Order'}
            </button>
          </footer>
        </section>
      </div>
    );
  };

  const renderInvoiceProductPicker = () => {
    if (!invoiceProductPickerOpen) return null;

    const pickerOrder = getInvoicePickerOrder();
    const locked = pickerOrder?.locked;
    const pickerSummary = pickerOrder ? calculateInvoice(pickerOrder) : { manualItems: [] };
    const orderTitle = pickerOrder
      ? `#${pickerOrder.id} · ${MODE_LABELS[pickerOrder.displayMode] || pickerOrder.displayMode || 'Order'}`
      : 'Selected Order';

    return (
      <div
        className="ff-counter-order-overlay ff-invoice-picker-overlay"
        role="presentation"
        onClick={closeInvoiceProductPicker}
      >
        <section
          className="ff-counter-order-drawer ff-invoice-picker-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ff-invoice-picker-title"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="ff-counter-order-head ff-invoice-picker-head">
            <div>
              <span>Running Order</span>
              <h2 id="ff-invoice-picker-title">Add Item to Invoice</h2>
              <p>{orderTitle} me product add hoga. Save Invoice / Complete par bill me save rahega.</p>
            </div>

            <button
              type="button"
              className="ff-counter-close"
              onClick={closeInvoiceProductPicker}
              aria-label="Close add item"
            >
              ×
            </button>
          </header>

          <button
            type="button"
            className="ff-counter-category-fab ff-invoice-category-fab"
            onClick={() => setInvoiceCategorySidebarOpen(true)}
            aria-label="Open invoice product categories"
            title="Open categories"
          >
            <span aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </button>

          <div
            className={`ff-counter-category-backdrop ${invoiceCategorySidebarOpen ? 'open' : ''}`}
            role="presentation"
            onClick={() => setInvoiceCategorySidebarOpen(false)}
          />

          <aside className={`ff-counter-category-panel ${invoiceCategorySidebarOpen ? 'open' : ''}`}>
            <div className="ff-counter-category-panel-head">
              <div>
                <span>Product</span>
                <h3>Categories</h3>
              </div>
              <button
                type="button"
                onClick={() => setInvoiceCategorySidebarOpen(false)}
                aria-label="Close category"
              >
                ×
              </button>
            </div>

            <div className="ff-counter-category-list">
              {invoiceCategories.map((categoryName) => (
                <button
                  key={`invoice-category-${categoryName}`}
                  type="button"
                  className={invoiceCategoryFilter === categoryName ? 'active' : ''}
                  onClick={() => selectInvoiceCategory(categoryName)}
                >
                  <span>{categoryName}</span>
                  <b>{getInvoiceCategoryCount(categoryName)}</b>
                </button>
              ))}
            </div>
          </aside>

          <div className="ff-counter-scroll ff-invoice-picker-scroll">
            <section className="ff-counter-section ff-counter-menu-section ff-invoice-picker-menu-section">
              <div className="ff-counter-section-title">
                <b>＋</b>
                <strong>Select Product</strong>
                <span className="ff-counter-active-category">{invoiceCategoryFilter}</span>
              </div>

              <div className="ff-counter-product-topbar">
                <button
                  type="button"
                  className="ff-counter-category-open-btn"
                  onClick={() => setInvoiceCategorySidebarOpen(true)}
                >
                  <span>»»</span>
                  Category
                </button>

                {invoiceCategoryFilter !== 'All' ? (
                  <button
                    type="button"
                    className="ff-counter-category-clear-btn"
                    onClick={() => selectInvoiceCategory('All')}
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              <div className="ff-counter-search ff-invoice-product-search">
                <span>⌕</span>
                <input
                  ref={invoiceProductSearchInputRef}
                  value={invoiceProductSearch}
                  disabled={locked}
                  placeholder="Product name, short form ya price search"
                  onChange={(event) => setInvoiceProductSearch(event.target.value)}
                  onFocus={() => {
                    if (!counterProducts.length) {
                      fetchCounterProducts();
                    }
                  }}
                />
              </div>

              <div className="ff-counter-products ff-invoice-product-results">
                {counterProductsLoading ? (
                  <div className="ff-counter-empty">Products loading...</div>
                ) : invoiceFilteredProducts.length ? (
                  invoiceFilteredProducts.map((product, index) => {
                    const existing = pickerSummary.manualItems.find(
                      (item) => item.id === getInvoiceProductManualItemId(product.id)
                    );
                    const qty = Number(existing?.quantity || 0);
                    const selected = index === invoiceKeyboardProductIndex;

                    return (
                      <div
                        key={`invoice-product-${product.id}`}
                        className={`ff-counter-product-card ${selected ? 'keyboard-selected' : ''}`}
                        data-invoice-keyboard-product-index={index}
                        onMouseEnter={() => setInvoiceKeyboardIndexSafe(index, { scroll: false })}
                      >
                        <span className="ff-counter-product-icon">
                          {String(product.name || 'I').slice(0, 1).toUpperCase()}
                        </span>

                        <div>
                          <strong>{product.name}</strong>
                          <span>
                            {product.category || 'Item'} · {formatMoney(product.selling_price || product.price)}
                          </span>
                        </div>

                        {qty > 0 ? (
                          <div className="ff-counter-qty">
                            <button
                              type="button"
                              disabled={locked}
                              onClick={() => decreaseInvoiceProductFromManualItems(pickerOrder, product)}
                            >
                              −
                            </button>
                            <b>{qty}</b>
                            <button
                              type="button"
                              disabled={locked}
                              onClick={() => addInvoiceProductToManualItems(pickerOrder, product)}
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="ff-counter-add"
                            disabled={locked}
                            onClick={() => addInvoiceProductToManualItems(pickerOrder, product)}
                          >
                            Add
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="ff-counter-empty">Matching product nahi mila.</div>
                )}
              </div>
            </section>

            <section className="ff-counter-section ff-counter-cart-section ff-invoice-picker-cart-section">
              <div className="ff-counter-section-title">
                <b>🧾</b>
                <strong>Added Items</strong>
              </div>

              <div className="ff-counter-cart">
                {pickerSummary.manualItems.length ? (
                  pickerSummary.manualItems.map((item) => (
                    <div key={item.id} className="ff-counter-cart-row">
                      <div>
                        <strong>{item.name || item.item_name}</strong>
                        <span>{formatMoney(item.price)} × {item.quantity}</span>
                      </div>

                      <div className="ff-counter-cart-actions">
                        <b>{formatMoney(Number(item.price || 0) * Number(item.quantity || 0))}</b>
                        <button
                          type="button"
                          disabled={locked}
                          onClick={() => removeManualItem(pickerOrder.id, item.id)}
                          aria-label={`Remove ${item.name || item.item_name}`}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="ff-counter-empty">Abhi koi extra item add nahi hua.</div>
                )}
              </div>
            </section>
          </div>

          <footer className="ff-counter-footer ff-invoice-picker-footer">
            <button
              type="button"
              className="secondary"
              onClick={closeInvoiceProductPicker}
            >
              Done
            </button>

            <button
              type="button"
              className="primary"
              onClick={() => {
                closeInvoiceProductPicker();
                showMessage('Item invoice me add ho gaya. Save Invoice / Complete karna na bhoolein.');
              }}
            >
              Continue Billing
            </button>
          </footer>
        </section>
      </div>
    );
  };


  const renderShortcutHelpPopup = () => {
    if (!shortcutHelpOpen) return null;

    const shortcutGroups = [
      {
        title: 'Navigation',
        items: [
          ['Alt + Q', 'Orders Queue / Active open'],
          ['Alt + R', 'Orders refresh'],
          ['Alt + A', 'Active Orders open'],
          ['Alt + H', 'History open'],
          ['Alt + D', 'Delivery page open'],
          ['Alt + O', 'Order list open'],
          ['↑ / ↓', 'Previous / Next order select'],
          ['Enter', 'Selected order detail open'],
          ['Esc', 'Back / Close popup'],
          ['/', 'Search focus / press again to release'],
          ['↑ / ↓ in Search', 'Filtered order select'],
          ['Enter in Search', 'Selected filtered order open'],
          ['?', 'Shortcut help close'],
        ],
      },
      {
        title: 'Order Process',
        items: [
          ['A', 'Accept New order'],
          ['K', 'Send accepted order to Kitchen'],
          ['Shift + K', 'Print KOT copy'],
          ['R', 'Mark Kitchen order Ready'],
          ['I', 'Open Invoice detail'],
          ['Alt + I', 'Open Invoice edit mode'],
        ],
      },
      {
        title: 'Billing',
        items: [
          ['S', 'Save Invoice only'],
          ['P', 'Print Bill only'],
          ['Shift + P', 'Ready order final action'],
          ['Alt + I, type, ↑↓, Enter', 'Invoice product search add'],
          ['+ / - in Invoice search', 'Invoice highlighted item add / reduce'],
        ],
      },
      {
        title: 'Counter & Safety',
        items: [
          ['N', 'New Counter Order'],
          ['D / T', 'Counter: Dine-in / Takeaway'],
          ['Digits + Enter after D', 'Counter: Table number select, then Search focus'],
          ['Esc in Counter field', 'Release focus; press again to close'],
          ['/ · / again', 'Counter: Search focus / release focus'],
          ['↑↓ · Enter', 'Counter: Search result select and add'],
          ['+ / -', 'Counter: Add / reduce highlighted item'],
          ['C / M', 'Counter: Customer name / mobile focus'],
          ['1 / 2 / 3', 'Counter: Cash / UPI / Credit'],
          ['Ctrl + Enter', 'Create Counter Order'],
          ['Delete', 'Open cancel confirmation'],
          ['Shift + Delete', 'Confirm cancel popup'],
        ],
      },
    ];

    return (
      <div
        className="ff-cancel-order-overlay"
        role="presentation"
        onClick={() => setShortcutHelpOpen(false)}
      >
        <section
          className="ff-cancel-order-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ff-shortcut-help-title"
          onClick={(event) => event.stopPropagation()}
          style={{ width: 'min(760px, 100%)' }}
        >
          <div className="ff-cancel-order-modal-copy" style={{ marginTop: 0 }}>
            <span>Desktop / Laptop</span>
            <h3 id="ff-shortcut-help-title">⌨ Keyboard Shortcuts</h3>
            <p>
              Mouse ke bina fast order handling. Input box me typing karte waqt
              shortcuts trigger nahi honge.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '10px',
              marginTop: '14px',
              maxHeight: '62vh',
              overflowY: 'auto',
            }}
          >
            {shortcutGroups.map((group) => (
              <section
                key={group.title}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '10px',
                  background: '#f8fafc',
                }}
              >
                <strong
                  style={{
                    display: 'block',
                    color: '#0f172a',
                    fontSize: '0.78rem',
                    marginBottom: '7px',
                  }}
                >
                  {group.title}
                </strong>

                {group.items.map(([shortcut, description]) => (
                  <div
                    key={`${group.title}-${shortcut}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '86px minmax(0, 1fr)',
                      gap: '7px',
                      alignItems: 'center',
                      padding: '6px 0',
                      borderTop: '1px dashed #e2e8f0',
                    }}
                  >
                    <kbd
                      style={{
                        border: '1px solid #cbd5e1',
                        borderRadius: '7px',
                        background: '#ffffff',
                        padding: '4px 6px',
                        color: '#0f172a',
                        fontSize: '0.7rem',
                        fontWeight: 900,
                        textAlign: 'center',
                        boxShadow: '0 2px 0 #cbd5e1',
                      }}
                    >
                      {shortcut}
                    </kbd>

                    <span
                      style={{
                        color: '#475569',
                        fontSize: '0.72rem',
                        fontWeight: 750,
                      }}
                    >
                      {description}
                    </span>
                  </div>
                ))}
              </section>
            ))}
          </div>

          <div className="ff-cancel-order-modal-actions">
            <button
              type="button"
              className="keep"
              onClick={() => setShortcutHelpOpen(false)}
            >
              Close (Esc)
            </button>
          </div>
        </section>
      </div>
    );
  };

  const renderCancelOrderPopup = () => {
    if (!cancelOrderTarget) return null;

    return (
      <div
        className="ff-cancel-order-overlay"
        role="presentation"
        onClick={closeCancelOrderPopup}
      >
        <section
          className="ff-cancel-order-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ff-cancel-order-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="ff-cancel-order-modal-icon">!</div>

          <div className="ff-cancel-order-modal-copy">
            <span>Cancel confirmation</span>
            <h3 id="ff-cancel-order-title">
              Order #{cancelOrderTarget.id} cancel karna hai?
            </h3>
            <p>
              Cancel hone ke baad order active queue se remove ho jayega aur History
              me Cancelled status ke saath safe rahega.
            </p>
          </div>

          <div className="ff-cancel-order-modal-meta">
            <div>
              <span>Customer</span>
              <strong>{cancelOrderTarget.customerName || 'Walk-in Customer'}</strong>
            </div>

            <div>
              <span>Total</span>
              <strong>{formatMoney(cancelOrderTarget.displayTotal)}</strong>
            </div>
          </div>

          <div className="ff-cancel-order-modal-actions">
            <button
              type="button"
              className="keep"
              onClick={closeCancelOrderPopup}
              disabled={cancelOrderLoading}
            >
              Keep Order
            </button>

            <button
              type="button"
              className="confirm"
              onClick={confirmCancelOrder}
              disabled={cancelOrderLoading}
            >
              {cancelOrderLoading ? 'Cancelling...' : 'Confirm Cancel'}
            </button>
          </div>
        </section>
      </div>
    );
  };

  const renderProgress = (order) => {
    const currentIndex = getOrderStepIndex(order?.displayStatus);

    const steps = [
      { key: 'new', label: 'New' },
      { key: 'accepted', label: 'Accepted' },
      { key: 'preparing', label: 'Kitchen' },
      { key: 'ready', label: 'Ready' },
      { key: 'done', label: 'Completed' },
    ];

    return (
      <div className="ff-image-progress-card">
        <div className="ff-image-progress">
          {steps.map((step, index) => (
            <div
              key={step.key}
              className={`ff-image-step ${index <= currentIndex ? 'active' : ''} ${step.key}`}
            >
              <span>{index + 1}</span>
              <small>{step.label}</small>
            </div>
          ))}
        </div>

        <div className={`ff-current-status ${order?.displayStatus || 'new'}`}>
          🔔 Current Status: {STATUS_FLOW[order?.displayStatus || 'new']?.longLabel || 'New'}
        </div>
      </div>
    );
  };

  const renderItemsCard = (order) => {
    const invoice = getInvoiceState(order?.id);
    const itemsSummary = calculateInvoice(order);
    const manualItems = invoice?.manualItems || [];
    const displayItems = [
      ...(order?.productsParsed || []).map((item, index) => {
        const qty = toNumber(item?.quantity || item?.qty, 1);
        const rate = toNumber(item?.price ?? item?.selling_price ?? item?.rate, 0);

        return {
          key: `ordered-${item?.id || item?.productId || item?.name || index}-${index}`,
          qty,
          name: item?.name || item?.item_name || 'Item',
          amount: qty * rate,
          manual: false,
        };
      }),
      ...manualItems.map((item, index) => {
        const qty = toNumber(item?.quantity || item?.qty, 1);
        const rate = toNumber(item?.price ?? item?.rate, 0);

        return {
          key: `manual-${item?.id || item?.saved_item_id || item?.name || index}-${index}`,
          itemId: item?.id || item?.saved_item_id || `manual-${index}`,
          qty,
          name: item?.name || item?.item_name || 'Manual Item',
          amount: qty * rate,
          manual: true,
        };
      }),
    ];

    return (
      <section className={`ff-image-panel-card ff-items-panel status-${order?.displayStatus || 'new'}`}>
        <h3>Items</h3>

        <div className="ff-image-items">
          {displayItems.map((item) => (
            <div
              className={`ff-image-item-row ${item.manual ? 'manual' : ''}`}
              key={item.key}
            >
              <span>{item.qty}</span>
              <strong>
                {item.name}
                {item.manual ? <small className="ff-readonly-pill">Manual</small> : null}
              </strong>
              <b>{formatMoney(item.amount)}</b>
              {item.manual && !order?.locked ? (
                <button
                  type="button"
                  className="ff-mini-danger"
                  title="Remove manual item"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => removeManualItem(order.id, item.itemId)}
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}

          {!displayItems.length ? (
            <div className="ff-image-empty small">No items</div>
          ) : null}
        </div>

        <div
          className="ff-invoice-line total"
          style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #e5eaf1',
          }}
        >
          <span>Items Total</span>
          <strong>{formatMoney(itemsSummary.subtotal)}</strong>
        </div>
      </section>
    );
  };

  const renderInvoiceCard = (order) => {
    const invoice = getInvoiceState(order?.id);
    const summary = calculateInvoice(order);
    const locked = order?.locked;

    return (
      <section className="ff-image-panel-card ff-invoice-panel">
        <div className="ff-invoice-title-row">
          <h3>Invoice / Bill</h3>
          {locked ? <span className="ff-readonly-pill">Read-only</span> : null}
        </div>

        <div className="ff-invoice-lines">
          <div className="ff-invoice-input-line">
            <label>Custom Manual Item</label>
            <div className="ff-manual-inline">
              <input
                value={invoice.manualItemName}
                disabled={locked}
                placeholder="Item"
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    manualItemName: e.target.value,
                  }))
                }
              />
              <input
                value={invoice.manualItemQty}
                disabled={locked}
                type="number"
                min="1"
                placeholder="Qty"
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    manualItemQty: e.target.value,
                  }))
                }
              />
              <input
                value={invoice.manualItemPrice}
                disabled={locked}
                type="number"
                min="0"
                placeholder="₹"
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    manualItemPrice: e.target.value,
                  }))
                }
              />
              <button type="button" disabled={locked} onClick={() => addManualItem(order)}>
                +
              </button>
            </div>
          </div>

          {summary.manualItems.length ? (
            <div className="ff-manual-list">
              {summary.manualItems.map((item) => (
                <div key={item.id} className="ff-manual-list-row">
                  <span>{item.name || item.item_name}</span>
                  <b>
                    {item.quantity} × {formatMoney(item.price)}
                  </b>
                  {!locked ? (
                    <button type="button" onClick={() => removeManualItem(order.id, item.id)}>
                      ×
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          <div className="ff-invoice-input-line two">
            <label>
              Discount
              <div className="ff-invoice-discount-control">
                <select
                  value={invoice.discountType || 'amount'}
                  disabled={locked}
                  onChange={(e) =>
                    setInvoiceStateForOrder(order.id, (prev) => ({
                      ...prev,
                      discountType: e.target.value === 'percentage' ? 'percentage' : 'amount',
                      discount: '',
                    }))
                  }
                  aria-label="Discount type"
                >
                  <option value="amount">₹ Amount</option>
                  <option value="percentage">% Percentage</option>
                </select>

                <input
                  ref={(node) => {
                    invoiceEditControlRefs.current.discount = node;
                  }}
                  data-invoice-edit-control="discount"
                  value={invoice.discount}
                  disabled={locked}
                  type="number"
                  min="0"
                  max={invoice.discountType === 'percentage' ? '100' : undefined}
                  step={invoice.discountType === 'percentage' ? '0.01' : '1'}
                  placeholder={invoice.discountType === 'percentage' ? '0%' : '₹0'}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const nextValue = invoice.discountType === 'percentage' && rawValue !== ''
                      ? String(Math.min(100, Math.max(0, toNumber(rawValue, 0))))
                      : rawValue;

                    setInvoiceStateForOrder(order.id, (prev) => ({
                      ...prev,
                      discount: nextValue,
                    }));
                  }}
                />
              </div>
              {summary.discount > 0 ? (
                <small className="ff-invoice-discount-result">
                  Discount: {summary.discountType === 'percentage' ? `${summary.discountValue}% = ` : ''}{formatMoney(summary.discount)}
                </small>
              ) : null}
            </label>
            <div className="ff-invoice-gst-control">
              <label className="ff-invoice-gst-toggle">
                <input
                  ref={(node) => {
                    invoiceEditControlRefs.current.gstToggle = node;
                  }}
                  data-invoice-edit-control="gstToggle"
                  type="checkbox"
                  checked={Boolean(invoice.gstEnabled)}
                  disabled={locked}
                  onChange={(e) =>
                    setInvoiceStateForOrder(order.id, (prev) => ({
                      ...prev,
                      gstEnabled: e.target.checked,
                      gstPercent: e.target.checked
                        ? String(
                            toNumber(
                              prev.gstPercent ||
                                billingSettingsRef.current?.defaultGstPercent,
                              5
                            )
                          )
                        : '',
                      gstTouched: true,
                    }))
                  }
                />
                <span>Apply GST on this bill</span>
              </label>

              <label>
                GST (%)
                <input
                  ref={(node) => {
                    invoiceEditControlRefs.current.gstPercent = node;
                  }}
                  data-invoice-edit-control="gstPercent"
                  value={invoice.gstPercent}
                  disabled={locked}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="Type GST %"
                  onChange={(e) => {
                    const nextValue = sanitizeGstInput(e.target.value);

                    setInvoiceStateForOrder(order.id, (prev) => ({
                      ...prev,
                      gstPercent: nextValue,
                      gstEnabled: toNumber(nextValue, 0) > 0,
                      gstTouched: true,
                    }));
                  }}
                />
              </label>
            </div>
          </div>

          {summary.coinDiscount > 0 ? (
            <div className="ff-invoice-line">
              <span>
                Coin Discount
                {summary.coinsRedeemed > 0
                  ? ` (${summary.coinsRedeemed} coins)`
                  : ''}
              </span>
              <strong>-{formatMoney(summary.coinDiscount)}</strong>
            </div>
          ) : null}

          <div className="ff-invoice-input-line three">
            <label>
              Delivery
              <input
                ref={(node) => {
                  invoiceEditControlRefs.current.deliveryCharge = node;
                }}
                data-invoice-edit-control="deliveryCharge"
                value={invoice.deliveryCharge}
                disabled={locked}
                type="number"
                placeholder="0"
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    deliveryCharge: e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Packing
              <input
                ref={(node) => {
                  invoiceEditControlRefs.current.packingCharge = node;
                }}
                data-invoice-edit-control="packingCharge"
                value={invoice.packingCharge}
                disabled={locked}
                type="number"
                placeholder="0"
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    packingCharge: e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Round
              <input
                ref={(node) => {
                  invoiceEditControlRefs.current.roundOff = node;
                }}
                data-invoice-edit-control="roundOff"
                value={invoice.roundOff}
                disabled={locked}
                type="number"
                placeholder="0"
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    roundOff: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="ff-invoice-line total">
            <span>Final Total</span>
            <strong>{formatMoney(summary.finalTotal)}</strong>
          </div>

          <div className="ff-invoice-input-line two">
            <label>
              Payment Status
              <select
                ref={(node) => {
                  invoiceEditControlRefs.current.paymentStatus = node;
                }}
                data-invoice-edit-control="paymentStatus"
                value={invoice.paymentStatus}
                disabled={locked}
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    paymentStatus: e.target.value,
                  }))
                }
              >
                {PAYMENT_STATUS_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Method
              <select
                ref={(node) => {
                  invoiceEditControlRefs.current.paymentMethod = node;
                }}
                data-invoice-edit-control="paymentMethod"
                value={invoice.paymentMethod}
                disabled={locked}
                onChange={(e) =>
                  setInvoiceStateForOrder(order.id, (prev) => ({
                    ...prev,
                    paymentMethod: e.target.value,
                  }))
                }
              >
                {PAYMENT_METHOD_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {invoice.paymentStatus === 'partial' ? (
            <div className="ff-invoice-input-line">
              <label>
                Amount Paid
                <input
                  ref={(node) => {
                    invoiceEditControlRefs.current.amountPaid = node;
                  }}
                  data-invoice-edit-control="amountPaid"
                  value={invoice.amountPaid}
                  disabled={locked}
                  type="number"
                  placeholder="Enter paid amount"
                  onChange={(e) =>
                    setInvoiceStateForOrder(order.id, (prev) => ({
                      ...prev,
                      amountPaid: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
          ) : null}

          <div className="ff-invoice-line">
            <span>Amount Paid</span>
            <strong>{formatMoney(summary.amountPaid)}</strong>
          </div>

          <div className="ff-invoice-line due">
            <span>Due Amount</span>
            <strong>{formatMoney(summary.dueAmount)}</strong>
          </div>
        </div>
      </section>
    );
  };

  const renderCustomerCard = (order) => (
    <section className="ff-image-customer-card">
      <div className="ff-customer-avatar">👤</div>

      <div className="ff-customer-info">
        <h3>{order.customerName}</h3>
        <p>Phone: {order.customerMobile || 'N/A'}</p>
      </div>

      <div className="ff-customer-meta">
        <span className={`ff-mode-badge ${getModeBadgeClass(order.displayMode)}`}>
          {order.displayMode === 'dine_in' ? '🍽️' : order.displayMode === 'delivery' ? '🛵' : '🛍️'} {MODE_LABELS[order.displayMode]}
          {order.displayMode === 'dine_in' && order.table_number ? ` • Table ${order.table_number}` : ''}
        </span>

        <span className="ff-items-mini">📄 {(order.productsParsed.length || order.itemCount) + ((getInvoiceState(order.id)?.manualItems || []).reduce((sum, item) => sum + toNumber(item?.quantity || item?.qty, 1), 0))} Items</span>
      </div>
    </section>
  );

  const renderQuickPaymentMode = (order) => {
    const invoice = getInvoiceState(order?.id);
    const currentMethod = String(invoice?.paymentMethod || '').toLowerCase();
    const currentStatus = String(invoice?.paymentStatus || '').toLowerCase();
    const selectedMode =
      currentStatus === 'cod' || currentMethod === 'cod'
        ? 'cod'
        : ['online', 'razorpay', 'upi', 'card', 'stripe'].includes(currentMethod)
          ? 'online'
          : '';

    const selectPaymentMode = (mode) => {
      if (!order?.id || order?.locked || actionLoading) return;

      setInvoiceStateForOrder(order.id, (prev) => ({
        ...prev,
        paymentStatus: mode === 'cod' ? 'cod' : 'paid',
        paymentMethod: mode === 'cod' ? 'cod' : 'online',
        amountPaid: mode === 'cod' ? '' : prev.amountPaid,
      }));
    };

    return (
      <section className="ff-quick-payment-card" aria-label="Payment mode">
        <div className="ff-quick-payment-head">
          <div>
            <span>Payment Mode</span>
            <strong>Select for this order</strong>
          </div>
          <small>{selectedMode === 'online' ? 'ONLINE' : selectedMode === 'cod' ? 'COD' : 'SELECT'}</small>
        </div>

        <div className="ff-quick-payment-options">
          <button
            type="button"
            className={selectedMode === 'online' ? 'active online' : 'online'}
            disabled={order?.locked || actionLoading}
            onClick={() => selectPaymentMode('online')}
          >
            <span>📲</span>
            <b>ONLINE</b>
          </button>

          <button
            type="button"
            className={selectedMode === 'cod' ? 'active cod' : 'cod'}
            disabled={order?.locked || actionLoading}
            onClick={() => selectPaymentMode('cod')}
          >
            <span>💵</span>
            <b>COD</b>
          </button>
        </div>

        <p>Selected mode Save Invoice / Complete ke saath save hoga.</p>
      </section>
    );
  };


  const runMenuAction = (event, disabled, callback) => {
    if (disabled) return;

    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    callback();
  };

  const renderActionTextItem = ({
    title,
    helper,
    tone = 'default',
    disabled = false,
    onSelect,
  }) => (
    <div
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      className={`ff-action-text-item ${tone} ${disabled ? 'disabled' : ''}`}
      onClick={(event) => runMenuAction(event, disabled, onSelect)}
      onKeyDown={(event) => runMenuAction(event, disabled, onSelect)}
    >
      <span className="ff-action-text-main">{title}</span>
      {helper ? <span className="ff-action-text-help">{helper}</span> : null}
    </div>
  );

  const renderActionDropdowns = (order, locked) => {
    const isDeliveryOrder = order?.displayMode === 'delivery';
    const isDeliveryReady = isDeliveryOrder && order?.displayStatus === 'ready';

    const canShowOrderActions = canAccessAny([
      'orderAccept',
      'sendToKitchen',
      'markPreparing',
      'markReady',
      'printKot',
    ]);

    const canShowBillingActions = canAccessAny([
      'invoiceEdit',
      'billPrint',
      'paymentUpdate',
      'completeOrder',
      'deliveryDispatch',
    ]);

    const disableAccept = actionLoading || locked || order.displayStatus !== 'new';
    const disableKitchen =
      actionLoading ||
      locked ||
      ['preparing', 'ready', 'done'].includes(order.displayStatus);
    const disableReady = actionLoading || locked || order.displayStatus !== 'preparing';

    if (!canShowOrderActions && !canShowBillingActions) return null;

    return (
      <div className="ff-two-action-menu-row compact" ref={actionDropdownRef}>
        {canShowOrderActions ? (
          <div className="ff-action-dropdown-wrap">
            <button
              type="button"
              className="ff-main-action-btn order"
              disabled={actionLoading || locked}
              onClick={() =>
                setActionDropdown((prev) => (prev === 'order' ? null : 'order'))
              }
            >
              Order Actions <span className="ff-main-action-arrow">⌄</span>
            </button>

            {actionDropdown === 'order' ? (
              <div className="ff-action-dropdown-menu order text-only" role="menu">
                {canAccess('orderAccept') ? renderActionTextItem({
                  title: 'Accept Order',
                  helper: disableAccept ? 'Already accepted or not available' : 'Confirm this order',
                  tone: 'orange',
                  disabled: disableAccept,
                  onSelect: () => {
                    setActionDropdown(null);
                    handleStepAction(order, 'accepted');
                  },
                }) : null}

                {canAccessAny(['sendToKitchen', 'markPreparing']) ? renderActionTextItem({
                  title: 'Send KOT to Kitchen',
                  helper: disableKitchen
                    ? 'KOT already sent or order is ready'
                    : 'Manual items will be included',
                  tone: 'blue',
                  disabled: disableKitchen,
                  onSelect: () => {
                    setActionDropdown(null);
                    sendToKitchen(order);
                  },
                }) : null}

                {canAccess('markReady') ? renderActionTextItem({
                  title: 'Mark Ready',
                  helper: disableReady ? 'Available after kitchen step' : 'Order is ready to serve',
                  tone: 'green',
                  disabled: disableReady,
                  onSelect: () => {
                    setActionDropdown(null);
                    handleStepAction(order, 'ready');
                  },
                }) : null}

                {canAccess('printKot') ? renderActionTextItem({
                  title: 'Print KOT Copy',
                  helper: 'Kitchen copy with manual items',
                  tone: 'neutral',
                  disabled: actionLoading,
                  onSelect: () => {
                    setActionDropdown(null);
                    printKot(order);
                  },
                }) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {canShowBillingActions ? (
          <div className="ff-action-dropdown-wrap">
            <button
              type="button"
              className="ff-main-action-btn billing"
              disabled={actionLoading}
              onClick={() =>
                setActionDropdown((prev) => (prev === 'billing' ? null : 'billing'))
              }
            >
              Billing Actions <span className="ff-main-action-arrow">⌄</span>
            </button>

            {actionDropdown === 'billing' ? (
              <div className="ff-action-dropdown-menu billing text-only" role="menu">
                {canAccessAny(['invoiceEdit', 'paymentUpdate']) ? renderActionTextItem({
                  title: 'Save Invoice',
                  helper: 'Save bill details only',
                  tone: 'blue',
                  disabled: actionLoading,
                  onSelect: () => {
                    setActionDropdown(null);
                    saveInvoice(order);
                  },
                }) : null}

                {canAccess('billPrint') ? renderActionTextItem({
                  title: 'Print Bill',
                  helper: 'Print without completing order',
                  tone: 'neutral',
                  disabled: actionLoading,
                  onSelect: () => {
                    setActionDropdown(null);
                    printOnly(order);
                  },
                }) : null}

                {isDeliveryOrder
                  ? canAccess('deliveryDispatch') ? renderActionTextItem({
                      title: 'Send to Delivery',
                      helper: isDeliveryReady
                        ? 'Save invoice, print bill and assign rider'
                        : 'Available only after Ready status',
                      tone: 'green',
                      disabled: actionLoading || locked || !isDeliveryReady,
                      onSelect: () => {
                        setActionDropdown(null);
                        sendDeliveryOrderToDispatch(order);
                      },
                    }) : null
                  : canAccess('completeOrder') ? renderActionTextItem({
                      title: 'Complete Order',
                      helper: 'Save invoice, print bill and move to history',
                      tone: 'green',
                      disabled: actionLoading || locked,
                      onSelect: () => {
                        setActionDropdown(null);
                        savePrintComplete(order);
                      },
                    }) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };



  const renderDetailWorkspace = () => {
    if (!selectedOrder) {
      return (
        <section className="ff-image-detail">
          <div className="ff-image-empty">Select an order.</div>
        </section>
      );
    }

    const order = selectedOrder;
    const locked = order.locked;
    const invoiceDetailsOpen =
      String(invoiceDetailsOpenOrderId || '') === String(order.id || '');

    return (
      <section
        className={`ff-image-detail status-${order.displayStatus || 'new'} ${
          order.displayStatus === 'new' && !order.kotSent
            ? 'priority-new-order-detail'
            : order.kotSent && !order.locked
              ? 'priority-kot-running-detail'
              : order.runningTable && !order.locked
                ? 'priority-running-table-detail'
                : ''
        }`}
      >
        <div className="ff-image-detail-head">
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '9px',
              }}
            >
              <h2 style={{ margin: 0 }}>#ORD-{order.id}</h2>

              {order.displayStatus === 'cancelled' ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '5px 10px',
                    borderRadius: '999px',
                    background: '#fee2e2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca',
                    fontSize: '12px',
                    fontWeight: 800,
                  }}
                >
                  ✕ Cancelled
                </span>
              ) : null}
            </div>
            <div className="ff-order-detail-meta-row">
              <span className="ff-order-time-highlight detail-time">
                {formatTime(order.created_at)}
              </span>
              <span className="ff-order-date-text">
                {formatDateTime(order.created_at)}
              </span>
              {order.displayMode === 'dine_in' && order.table_number ? (
                <span className="ff-table-highlight detail-table">
                  🍽️ Table {order.table_number}
                </span>
              ) : null}
            </div>
          </div>

          {isMobile ? (
            <button type="button" className="ff-back-mobile" onClick={() => closeDetailScreen()}>
              ← Back
            </button>
          ) : null}
        </div>

        {renderProgress(order)}

        {locked ? (
          <div
            className={`ff-readonly-banner ${
              order.displayStatus === 'cancelled' ? 'cancelled' : ''
            }`}
          >
            {order.displayStatus === 'cancelled'
              ? '✕ This order was cancelled. History me cancelled record safe rahega.'
              : '🔒 Completed orders are read-only. History me record safe rahega.'}
          </div>
        ) : null}

        {!locked && order.displayMode === 'delivery' ? (
          <div className="ff-readonly-banner delivery-info">
            🛵 Delivery order direct complete nahi hoga. Ready ke baad Billing Actions → Send to Delivery karein. Final complete customer token number verify hone ke baad hoga.
          </div>
        ) : null}

        {renderActionDropdowns(order, locked)}

        <div className="ff-customer-payment-row">
          {canAccessAny(['ordersView', 'invoiceView', 'deliveryDispatch']) ? renderCustomerCard(order) : <div />}
          {canUseBillingTools ? renderQuickPaymentMode(order) : null}
        </div>

        {order.customerNote ? (
          <section className="ff-customer-order-note-card" aria-label="Customer order note">
            <div className="ff-customer-order-note-icon" aria-hidden="true">📝</div>
            <div>
              <strong>Customer Note</strong>
              <p>{order.customerNote}</p>
            </div>
          </section>
        ) : null}

        <div
          className="ff-image-detail-grid"
          style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}
        >
          {renderItemsCard(order)}
        </div>

        {canUseBillingTools ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              margin: '12px 0',
            }}
          >
            {!locked && canAccess('invoiceEdit') ? (
              <button
                type="button"
                className="ff-invoice-add-item-btn"
                style={{ flex: '1 1 220px', margin: 0 }}
                disabled={actionLoading}
                onClick={() => openInvoiceProductPicker(order)}
              >
                <span>＋</span>
                Add Item
              </button>
            ) : null}

            <button
              type="button"
              className="ff-invoice-add-item-btn"
              style={{
                flex: '1 1 220px',
                margin: 0,
                background: invoiceDetailsOpen ? '#24324a' : '#ffffff',
                color: invoiceDetailsOpen ? '#ffffff' : '#24324a',
                border: '1px solid #d8e0ec',
              }}
              aria-expanded={invoiceDetailsOpen}
              onClick={() =>
                setInvoiceDetailsOpenOrderId((currentOrderId) =>
                  String(currentOrderId || '') === String(order.id || '')
                    ? null
                    : order.id
                )
              }
            >
              <span>{invoiceDetailsOpen ? '−' : '▾'}</span>
              {invoiceDetailsOpen ? 'Hide Invoice / Bill' : 'Show Invoice / Bill'}
            </button>
          </div>
        ) : null}

        {canUseBillingTools && invoiceDetailsOpen ? (
          <div
            className="ff-image-detail-grid"
            style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}
          >
            {renderInvoiceCard(order)}
          </div>
        ) : null}
      </section>
    );
  };



  const fetchCustomerDues = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setPanelLoading(true);

      const res = await axios.get(`${STANDARD_ORDERS_API}/customer-dues`, {
        params: customerSearch ? { q: customerSearch } : {},
        headers: { Authorization: `Bearer ${token}` },
      });

      setCustomerDues(Array.isArray(res.data?.customers) ? res.data.customers : []);
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Customer dues load failed.',
        'error'
      );
    } finally {
      setPanelLoading(false);
    }
  };

  const fetchReadyReport = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setPanelLoading(true);

      const res = await axios.get(`${STANDARD_ORDERS_API}/ready-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setReportData(res.data?.report || {});
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Report load failed.',
        'error'
      );
    } finally {
      setPanelLoading(false);
    }
  };


  const normalizeDeliveryRider = (rider) => ({
    ...rider,
    id: rider?.id || rider?.rider_id,
    name: rider?.name || rider?.rider_name || 'Delivery Boy',
    mobile: rider?.mobile || rider?.rider_mobile || '',
    vehicleType: rider?.vehicle_type || rider?.vehicleType || 'bike',
    vehicleNumber: rider?.vehicle_number || rider?.vehicleNumber || '',
    serviceArea: rider?.service_area || rider?.serviceArea || '',
    status: String(rider?.status || rider?.rider_status || 'available').toLowerCase(),
    activeOrders: toNumber(rider?.active_orders || rider?.activeOrders, 0),
    deliveredToday: toNumber(rider?.delivered_today || rider?.deliveredToday, 0),
    deliveredTotal: toNumber(rider?.delivered_total || rider?.deliveredTotal, 0),
    totalTrips: toNumber(rider?.total_trips || rider?.totalTrips, 0),
  });

  const normalizeDeliveryReadyOrder = (order) => {
    const products = safeJsonParse(order?.productsParsed || order?.products, []);
    const area =
      order?.areaKey ||
      order?.area ||
      order?.delivery_area ||
      order?.landmark ||
      order?.city ||
      order?.pincode ||
      'Unknown Area';

    return {
      ...order,
      id: order?.id || order?.order_id,
      /*
        Active trip API delivery_trip_orders rows return karti hai.
        Us row ka `id` trip-order ID hai aur `order_id` original order ID.
        Reassign API ko delivery_trip_orders.id chahiye.
      */
      tripOrderId:
        order?.trip_order_id ||
        order?.tripOrderId ||
        order?.delivery_trip_order_id ||
        (order?.trip_id ? order?.id : null),
      orderId: order?.order_id || order?.orderId || order?.id || null,
      riderId: order?.rider_id || order?.riderId || null,
      customerName:
        order?.customerName ||
        order?.customer_name ||
        order?.delivery_details?.customer_name ||
        order?.name ||
        'Customer',
      customerMobile:
        order?.customerMobile ||
        order?.customer_mobile ||
        order?.customer_number ||
        order?.delivery_details?.customer_mobile ||
        '',
      fullAddress: order?.fullAddress || order?.full_address || getFullAddress(order),
      areaKey: String(area || 'Unknown Area').trim(),
      productsParsed: products,
      itemCount: products.reduce((sum, item) => sum + toNumber(item?.quantity || item?.qty, 1), 0),
      displayTotal: toNumber(
        order?.final_total || order?.display_total || order?.bill_total || order?.total_price,
        0
      ),
      tokenNumber:
        order?.tokenNumber ||
        order?.token_number ||
        order?.token ||
        order?.redeem_token ||
        order?.redeem_code ||
        order?.redeem?.token ||
        '',
      paymentStatus: String(order?.payment_status || order?.paymentStatus || 'unpaid').toLowerCase(),
      deliveryDistanceKm: toNumber(order?.delivery_distance_km || order?.distance_km, 0),
    };
  };

  const normalizeDeliveryTrip = (trip) => ({
    ...trip,
    id: trip?.id || trip?.trip_id,
    tripNo: trip?.trip_no || trip?.tripNo || `TRP-${trip?.id || ''}`,
    riderId: trip?.rider_id || trip?.riderId,
    riderName: trip?.rider_name || trip?.riderName || trip?.rider?.name || 'Rider',
    riderMobile: trip?.rider_mobile || trip?.riderMobile || trip?.rider?.mobile || '',
    status: String(trip?.status || trip?.trip_status || 'assigned').toLowerCase(),
    orders: Array.isArray(trip?.orders) ? trip.orders.map(normalizeDeliveryReadyOrder) : [],
    totalOrders: toNumber(trip?.total_orders || trip?.totalOrders || trip?.orders?.length, 0),
    totalDistanceKm: toNumber(trip?.total_distance_km || trip?.totalDistanceKm, 0),
    estimatedMinutes: toNumber(trip?.estimated_minutes || trip?.estimatedMinutes, 0),
    createdAt: trip?.created_at || trip?.createdAt,
  });

  const deliverySelectedRider = useMemo(
    () =>
      deliveryRiders.find(
        (rider) => String(rider.id) === String(deliverySelectedRiderId)
      ) || null,
    [deliveryRiders, deliverySelectedRiderId]
  );

  const deliverySelectedOrders = useMemo(
    () =>
      deliveryReadyOrders.filter((order) =>
        deliverySelectedOrderIds.includes(Number(order.id))
      ),
    [deliveryReadyOrders, deliverySelectedOrderIds]
  );

  const deliveryAreaGroups = useMemo(() => {
    const groups = {};

    deliveryReadyOrders.forEach((order) => {
      const key = order.areaKey || 'Unknown Area';

      if (!groups[key]) {
        groups[key] = {
          area: key,
          orders: [],
          total: 0,
        };
      }

      groups[key].orders.push(order);
      groups[key].total += toNumber(order.displayTotal, 0);
    });

    return Object.values(groups).sort((a, b) => b.orders.length - a.orders.length);
  }, [deliveryReadyOrders]);

  const filteredDeliveryReadyOrders = useMemo(() => {
    const q = deliverySearch.trim().toLowerCase();

    return deliveryReadyOrders.filter((order) => {
      const areaOk = deliverySelectedArea === 'all' || order.areaKey === deliverySelectedArea;
      const text = [
        order.id,
        order.customerName,
        order.customerMobile,
        order.fullAddress,
        order.areaKey,
        order.tokenNumber,
        ...(order.productsParsed || []).map((item) => item?.name || item?.item_name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return areaOk && (!q || text.includes(q));
    });
  }, [deliveryReadyOrders, deliverySelectedArea, deliverySearch]);

  const deliveryActiveTrackingTrip = useMemo(() => {
    if (deliveryTrackingTripId) {
      return (
        deliveryTrips.find(
          (trip) => String(trip.id) === String(deliveryTrackingTripId)
        ) || null
      );
    }

    return deliveryTrips[0] || null;
  }, [deliveryTrips, deliveryTrackingTripId]);

  const fetchDeliveryData = async ({ silent = false } = {}) => {
    const token = getToken();

    if (!token || !vendorId) return;

    try {
      if (!silent) setPanelLoading(true);

      const [ordersRes, ridersRes, tripsRes, historyRes] = await Promise.allSettled([
        axios.get(`${DELIVERY_API}/ready-orders`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${DELIVERY_API}/riders`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${DELIVERY_API}/trips/active`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${DELIVERY_API}/trips/history?limit=300`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (ordersRes.status === 'fulfilled') {
        const orders = Array.isArray(ordersRes.value?.data?.orders)
          ? ordersRes.value.data.orders
          : [];
        setDeliveryReadyOrders(orders.map(normalizeDeliveryReadyOrder));
      } else {
        const queryParam = getDbQueryParam();
        const fallbackRes = await axios.get(
          `${STANDARD_ORDERS_API}/today-orders?${queryParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const fallbackOrders = Array.isArray(fallbackRes?.data?.orders)
          ? fallbackRes.data.orders
          : [];

        setDeliveryReadyOrders(
          fallbackOrders
            .map(normalizeDeliveryReadyOrder)
            .filter(
              (order) =>
                normalizeOrderMode(order) === 'delivery' &&
                normalizeStatus(order) === 'ready'
            )
        );
      }

      if (ridersRes.status === 'fulfilled') {
        const riders = Array.isArray(ridersRes.value?.data?.riders)
          ? ridersRes.value.data.riders
          : [];
        setDeliveryRiders(riders.map(normalizeDeliveryRider));
      }

      if (tripsRes.status === 'fulfilled') {
        const trips = Array.isArray(tripsRes.value?.data?.trips)
          ? tripsRes.value.data.trips
          : [];
        setDeliveryTrips(trips.map(normalizeDeliveryTrip));
      }

      if (historyRes.status === 'fulfilled') {
        const history = Array.isArray(historyRes.value?.data?.trips)
          ? historyRes.value.data.trips
          : [];
        setDeliveryTripHistory(history.map(normalizeDeliveryTrip));
      }
    } catch (err) {
      if (!silent) {
        showMessage(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Delivery data load failed.',
          'error'
        );
      }
    } finally {
      if (!silent) setPanelLoading(false);
    }
  };

  const toggleDeliveryOrderSelect = (orderId) => {
    const id = Number(orderId);

    setDeliverySelectedOrderIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectDeliveryAreaOrders = (area) => {
    const ids = deliveryReadyOrders
      .filter((order) => order.areaKey === area)
      .map((order) => Number(order.id));

    const allSelected = ids.length && ids.every((id) => deliverySelectedOrderIds.includes(id));

    setDeliverySelectedOrderIds((prev) =>
      allSelected
        ? prev.filter((id) => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  const clearDeliverySelection = () => {
    setDeliverySelectedOrderIds([]);
    setDeliverySelectedRiderId('');
  };

  const createDeliveryRider = async (event) => {
    event.preventDefault();

    const token = getToken();
    const name = deliveryRiderForm.name.trim();
    const mobile = deliveryRiderForm.mobile.replace(/\D/g, '').slice(0, 10);
    const pin = deliveryRiderForm.pin.trim();

    if (!name || mobile.length !== 10 || !pin) {
      showMessage('Delivery boy name, 10 digit mobile and PIN required.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const payload = {
        name,
        mobile,
        pin,
        vehicleType: deliveryRiderForm.vehicleType,
        vehicleNumber: deliveryRiderForm.vehicleNumber.trim(),
        serviceArea: deliveryRiderForm.serviceArea.trim(),
        status: deliveryRiderForm.status,
        notes: deliveryRiderForm.notes.trim(),
      };

      const res = await axios.post(`${DELIVERY_API}/riders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newRider = normalizeDeliveryRider(res.data?.rider || payload);
      setDeliveryRiders((prev) => [newRider, ...prev]);
      setDeliveryRiderForm(emptyDeliveryRiderForm);
      setDeliveryFormOpen(false);
      showMessage('Delivery boy account created. Login link copy/share karke rider ko bhejein.');
      fetchDeliveryData({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Delivery boy create failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const assignDeliveryTrip = async () => {
    const token = getToken();

    if (!deliverySelectedOrderIds.length) {
      showMessage('Assign karne ke liye delivery orders select karein.', 'error');
      return;
    }

    if (!deliverySelectedRiderId) {
      showMessage('Delivery boy select karein.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const payload = {
        riderId: deliverySelectedRiderId,
        orderIds: deliverySelectedOrderIds,
        tokenVerificationRequired: true,
      };

      const res = await axios.post(`${DELIVERY_API}/assign-trip`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const trip = normalizeDeliveryTrip(
        res.data?.trip || {
          id: Date.now(),
          trip_no: `TRP-${Date.now()}`,
          rider_id: deliverySelectedRiderId,
          rider_name: deliverySelectedRider?.name,
          rider_mobile: deliverySelectedRider?.mobile,
          status: 'assigned',
          orders: deliverySelectedOrders,
          total_orders: deliverySelectedOrders.length,
        }
      );

      setDeliveryTrips((prev) => [trip, ...prev]);
      setDeliveryReadyOrders((prev) =>
        prev.filter((order) => !deliverySelectedOrderIds.includes(Number(order.id)))
      );
      setDeliveryTrackingTripId(trip.id);
      setDeliveryTab('dispatch');
      clearDeliverySelection();
      showMessage(`${trip.totalOrders || deliverySelectedOrderIds.length} orders assigned successfully.`);
      fetchDeliveryData({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Trip assign failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const updateDeliveryRiderStatus = async (riderId, status) => {
    const token = getToken();

    try {
      setActionLoading(true);

      await axios.patch(
        `${DELIVERY_API}/riders/${riderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDeliveryRiders((prev) =>
        prev.map((rider) =>
          String(rider.id) === String(riderId) ? { ...rider, status } : rider
        )
      );
      showMessage('Delivery boy status updated.');
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Delivery boy status update failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };



  const openResetPinModal = (rider) => {
    setResetPinRider(rider);
    setResetPinValue('');
  };

  const closeResetPinModal = () => {
    setResetPinRider(null);
    setResetPinValue('');
  };

  const resetRiderPin = async (event) => {
    event.preventDefault();

    if (!resetPinRider?.id) {
      showMessage('Delivery boy select nahi hua.', 'error');
      return;
    }

    if (!/^\d{4,6}$/.test(resetPinValue)) {
      showMessage('New PIN 4 se 6 digits ka hona chahiye.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const res = await axios.patch(
        `${DELIVERY_API}/riders/${resetPinRider.id}/reset-pin`,
        { newPin: resetPinValue },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      showMessage(res?.data?.message || 'Delivery boy PIN reset successful.');
      closeResetPinModal();
      await fetchDeliveryData({ silent: true });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'PIN reset failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const deleteDeliveryRider = async () => {
    if (!deleteRiderTarget?.id) {
      showMessage('Delivery boy select nahi hua.', 'error');
      return;
    }

    try {
      setActionLoading(true);

      const res = await axios.delete(
        `${DELIVERY_API}/riders/${deleteRiderTarget.id}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      showMessage(res?.data?.message || 'Delivery boy account deleted.');
      setDeleteRiderTarget(null);
      await fetchDeliveryData({ silent: false });
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Delivery boy delete failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openReassignModal = (order, trip) => {
    setReassignOrder({
      ...order,
      tripId: trip?.id || order?.trip_id || order?.tripId || null,
      tripNo: trip?.tripNo || trip?.trip_no || '',
      oldRiderId:
        order?.riderId ||
        order?.rider_id ||
        trip?.riderId ||
        trip?.rider_id ||
        null,
      oldRiderName:
        trip?.riderName ||
        trip?.rider_name ||
        'Current Rider',
    });

    setReassignRiderId('');
    setReassignReason('');
    setReassignError('');
  };

  const closeReassignModal = () => {
    setReassignOrder(null);
    setReassignRiderId('');
    setReassignReason('');
    setReassignError('');
  };

  const reassignTripOrder = async (event) => {
    event.preventDefault();
    setReassignError('');

    if (!reassignOrder?.tripOrderId) {
      const errorText = 'Trip order ID missing hai. Refresh karke dobara try karein.';
      setReassignError(errorText);
      showMessage(errorText, 'error');
      return;
    }

    if (!reassignRiderId) {
      const errorText = 'New delivery boy select karein.';
      setReassignError(errorText);
      showMessage(errorText, 'error');
      return;
    }

    try {
      setActionLoading(true);

      const res = await axios.patch(
        `${DELIVERY_API}/trip-orders/${reassignOrder.tripOrderId}/reassign`,
        {
          newRiderId: reassignRiderId,
          reason: reassignReason.trim() || 'Vendor reassigned delivery order',
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      showMessage(res?.data?.message || 'Delivery order reassigned successfully.');
      closeReassignModal();
      await fetchDeliveryData({ silent: false });
    } catch (err) {
      const errorText =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Order reassign failed.';

      setReassignError(errorText);
      showMessage(errorText, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const getReassignableDeliveryRiders = () =>
    deliveryRiders.filter(
      (rider) =>
        String(rider.status || '').toLowerCase() !== 'inactive' &&
        String(rider.id) !== String(reassignOrder?.oldRiderId || '')
    );


  const openSidePanel = (panel) => {
    const panelAllowed = {
      orders: canViewOrdersPanel,
      onlineOrders: canViewOrdersPanel,
      kitchen: canUseKitchenTools,
      invoice: canUseBillingTools,
      customers: canAccess('recordsView'),
      reports: canAccessAny(['reportsView', 'expenseManage']),
      delivery: canAccessAny(['deliveryDispatch', 'assignRider', 'reassignRider', 'deliveryRecords', 'riderManage']),
      staff: canAccess('staffManagement'),
      settings: canAccess('settingsManage'),
    };

    if (!panelAllowed[panel]) {
      showMessage(getStaffDeniedMessage(panel), 'error', 3600);
      return;
    }

    setSidePanel(panel);

    if (panel === 'orders') {
      setActiveTab('active');
      setStatusFilter(canAccess('ordersView') ? 'all' : canUseKitchenTools ? 'preparing' : 'ready');
      setCurrentScreen('list');
      return;
    }

    if (panel === 'onlineOrders') {
      setOnlineOrderFilter('all');
      setActiveTab('active');
      setStatusFilter('all');
      setCurrentScreen('list');
      fetchOnlineOrders({ silent: false });
      return;
    }

    if (panel === 'kitchen') {
      setActiveTab('active');
      setStatusFilter('preparing');
      setCurrentScreen('list');
      return;
    }

    if (panel === 'invoice') {
      if (selectedOrder && isOrderAllowedForCurrentStaff(selectedOrder)) {
        openDetail(selectedOrder);
      } else {
        setActiveTab('active');
        setStatusFilter('ready');
        setCurrentScreen('list');
        showMessage('Invoice ke liye pehle Ready order select karein.', 'error');
      }
      return;
    }

    if (panel === 'customers') {
      fetchCustomerDues();
      return;
    }

    if (panel === 'reports') {
      fetchReadyReport();
      return;
    }

    if (panel === 'delivery') {
      setDeliveryTab('dispatch');
      fetchDeliveryData({ silent: false });
      return;
    }
  };


  const renderOnlineOrdersPanel = () => {
    if (!canViewOrdersPanel) {
      return renderStaffAccessDenied('Online Orders access nahi hai');
    }

    const allOnlineOrders = onlineOrders.filter((order) =>
      ['swiggy', 'zomato'].includes(normalizeOrderSource(order))
    );

    const activeOnlineOrders = allOnlineOrders.filter((order) => {
      const source = normalizeOrderSource(order);

      if (onlineOrderFilter === 'all') return true;
      return source === onlineOrderFilter;
    });

    const swiggyCount = allOnlineOrders.filter(
      (order) => normalizeOrderSource(order) === 'swiggy'
    ).length;

    const zomatoCount = allOnlineOrders.filter(
      (order) => normalizeOrderSource(order) === 'zomato'
    ).length;

    const pendingCount = allOnlineOrders.filter((order) =>
      ['new', 'accepted', 'preparing'].includes(
        String(order.displayStatus || '').toLowerCase()
      )
    ).length;

    const openOnlineOrder = (order) => {
      setSelectedOrderId(order.id);
      keyboardSelectedOrderRef.current = order;
      initInvoiceForOrder(order);
      openDetail(order);
    };

    const renderSourceBadge = (source) => (
      <span className={`ff-online-source-badge ${source}`}>
        {source === 'swiggy' ? 'SWIGGY' : 'ZOMATO'}
      </span>
    );

    return (
      <section className="ff-side-workspace ff-online-orders-workspace">
        <div className="ff-side-workspace-head ff-online-orders-head">
          <div>
            <h2>Online Orders</h2>
            <p>Swiggy aur Zomato se aaye orders yahan alag dikhte hain.</p>
          </div>

          <button
            type="button"
            onClick={async () => {
              await fetchOrders({ silent: true });
              await fetchOnlineOrders({ silent: false });
            }}
            disabled={loading || onlineOrdersLoading}
          >
            ↻ Refresh
          </button>
        </div>

        <div className="ff-online-tabs">
          {[
            ['all', 'All'],
            ['swiggy', 'Swiggy'],
            ['zomato', 'Zomato'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={onlineOrderFilter === key ? 'active' : ''}
              onClick={() => setOnlineOrderFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ff-online-stats">
          <div className="ff-online-stat-card total">
            <span>🛍️</span>
            <div>
              <small>Total Online Orders</small>
              <strong>{allOnlineOrders.length}</strong>
            </div>
          </div>

          <div className="ff-online-stat-card swiggy">
            <span>S</span>
            <div>
              <small>Swiggy</small>
              <strong>{swiggyCount}</strong>
            </div>
          </div>

          <div className="ff-online-stat-card zomato">
            <span>Z</span>
            <div>
              <small>Zomato</small>
              <strong>{zomatoCount}</strong>
            </div>
          </div>

          <div className="ff-online-stat-card pending">
            <span>◷</span>
            <div>
              <small>Pending</small>
              <strong>{pendingCount}</strong>
            </div>
          </div>
        </div>

        <div className="ff-online-order-list">
          {onlineOrdersLoading ? (
            <div className="ff-online-empty">
              <span>⏳</span>
              <strong>Online orders load ho rahe hain...</strong>
            </div>
          ) : activeOnlineOrders.length ? (
            activeOnlineOrders.map((order) => {
              const source = normalizeOrderSource(order);
              const status = String(order.displayStatus || 'new').toLowerCase();
              const products = Array.isArray(order.productsParsed)
                ? order.productsParsed
                : [];

              const previewItems = products
                .slice(0, 2)
                .map(
                  (item) =>
                    `${Number(item.quantity || item.qty || 1)} x ${
                      item.name || item.item_name || 'Item'
                    }`
                )
                .join(', ');

              const orderNo =
                order.external_order_id ||
                order.externalOrderId ||
                order.platform_order_id ||
                order.platformOrderId ||
                order.order_number ||
                order.id;

              const customerName =
                order.customer_name ||
                order.external_customer_name ||
                order.customerName ||
                order.name ||
                'Customer';

              const total = toNumber(
                order.displayTotal ??
                  order.total_price ??
                  order.external_total_amount ??
                  order.total_amount ??
                  order.total ??
                  order.grand_total,
                0
              );

              const payment =
                order.payment_method ||
                order.external_payment_method ||
                order.paymentMethod ||
                order.displayPaymentMethod ||
                order.external_payment_status ||
                order.payment_status ||
                'online';

              return (
                <article
                  key={order.id}
                  className={`ff-online-order-card ${source} ${status}`}
                  onClick={() => openOnlineOrder(order)}
                >
                  <div className="ff-online-order-card-top">
                    <div className="ff-online-order-source-wrap">
                      {renderSourceBadge(source)}
                      <strong>Order #{orderNo}</strong>
                    </div>

                    <span className="ff-online-order-time">
                      {formatDateTime(order.created_at || order.createdAt)}
                    </span>
                  </div>

                  <div className="ff-online-order-customer">
                    <span>👤</span>
                    <b>{customerName}</b>
                  </div>

                  <p className="ff-online-order-items">
                    {previewItems || 'Items loading...'}
                    {products.length > 2 ? ` +${products.length - 2} more` : ''}
                  </p>

                  <div className="ff-online-order-card-bottom">
                    <strong>{formatMoney(total)}</strong>

                    <span className={`ff-online-status-pill ${status}`}>
                      {STATUS_FLOW[status]?.label || status.toUpperCase()}
                    </span>

                    <span className="ff-online-payment-pill">
                      {String(payment || 'online').toUpperCase()}
                    </span>

                    {status === 'new' ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedOrderId(order.id);
                          keyboardSelectedOrderRef.current = order;
                          initInvoiceForOrder(order);
                          openDetail(order);
                        }}
                      >
                        Accept
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          openOnlineOrder(order);
                        }}
                      >
                        Open
                      </button>
                    )}
                  </div>
                </article>
              );
            })
          ) : (
            <div className="ff-online-empty">
              <span>🌐</span>
              <strong>Abhi koi online order nahi hai.</strong>
              <p>
                Swiggy/Zomato integration se order aate hi yahan show hoga.
              </p>
            </div>
          )}
        </div>
      </section>
    );
  };


  const renderDeliveryPanel = () => {
    const selectedTotal = deliverySelectedOrders.reduce(
      (sum, order) => sum + toNumber(order.displayTotal, 0),
      0
    );

    const stats = {
      readyOrders: deliveryReadyOrders.length,
      availableRiders: deliveryRiders.filter((rider) => rider.status === 'available').length,
      onDelivery: deliveryRiders.filter((rider) =>
        ['on_delivery', 'busy'].includes(rider.status)
      ).length,
      activeTrips: deliveryTrips.filter((trip) => trip.status !== 'delivered').length,
    };

    const renderDeliveryOrders = () => (
      <section className="dd-panel dd-area-panel">
        <div className="dd-panel-head">
          <div>
            <h2>Ready Delivery Orders</h2>
            <p>Area wise orders select karke ek delivery boy ko assign karein.</p>
          </div>
          <button type="button" onClick={clearDeliverySelection} disabled={!deliverySelectedOrderIds.length}>
            Clear
          </button>
        </div>

        <div className="dd-search-row">
          <input
            value={deliverySearch}
            onChange={(e) => setDeliverySearch(e.target.value)}
            placeholder="Search order, customer, token, area"
          />

          <select
            value={deliverySelectedArea}
            onChange={(e) => setDeliverySelectedArea(e.target.value)}
          >
            <option value="all">All Areas</option>
            {deliveryAreaGroups.map((group) => (
              <option key={group.area} value={group.area}>
                {group.area} ({group.orders.length})
              </option>
            ))}
          </select>
        </div>

        <div className="dd-area-list">
          {deliveryAreaGroups.length ? (
            deliveryAreaGroups.map((group) => (
              <div className="dd-area-group" key={group.area}>
                <div className="dd-area-title-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={
                        group.orders.length > 0 &&
                        group.orders.every((order) =>
                          deliverySelectedOrderIds.includes(Number(order.id))
                        )
                      }
                      onChange={() => selectDeliveryAreaOrders(group.area)}
                    />
                    <span>{group.area}</span>
                  </label>
                  <b>{group.orders.length} Orders</b>
                </div>

                <div className="dd-order-list">
                  {group.orders
                    .filter((order) =>
                      filteredDeliveryReadyOrders.some(
                        (item) => Number(item.id) === Number(order.id)
                      )
                    )
                    .map((order) => (
                      <label className="dd-ready-order" key={order.id}>
                        <input
                          type="checkbox"
                          checked={deliverySelectedOrderIds.includes(Number(order.id))}
                          onChange={() => toggleDeliveryOrderSelect(order.id)}
                        />

                        <div>
                          <strong>#ORD-{order.id}</strong>
                          <span>{formatDateTime(order.created_at)}</span>
                        </div>

                        <div>
                          <b>{order.customerName}</b>
                          <span>{order.fullAddress || 'Address missing'}</span>
                        </div>

                        <div>
                          <small className={`dd-payment ${order.paymentStatus}`}>
                            {order.paymentStatus === 'paid'
                              ? 'Paid'
                              : order.paymentStatus === 'cod'
                                ? 'COD'
                                : order.paymentStatus === 'partial'
                                  ? 'Partial'
                                  : 'Credit'}
                          </small>
                          <span>{formatMoney(order.displayTotal)}</span>
                        </div>

                        <div>
                          <small className="dd-token">Token: {order.tokenNumber || 'N/A'}</small>
                          <em>Delivery Ready</em>
                        </div>
                      </label>
                    ))}
                </div>
              </div>
            ))
          ) : (
            <div className="dd-empty">
              <strong>No ready delivery orders</strong>
              <p>Delivery order Ready mark hote hi yahan show hoga.</p>
            </div>
          )}
        </div>
      </section>
    );

    const renderAssignRider = () => (
      <section className="dd-panel dd-assign-panel">
        <div className="dd-panel-head">
          <div>
            <h2>Assign Delivery Boy</h2>
            <p>Available rider choose karein.</p>
          </div>
          <button type="button" onClick={() => setDeliveryFormOpen(true)}>
            + Add
          </button>
        </div>

        <div className="dd-rider-select-list">
          {deliveryRiders.length ? (
            deliveryRiders.map((rider) => (
              <label
                className={`dd-rider-option ${
                  String(deliverySelectedRiderId) === String(rider.id) ? 'selected' : ''
                }`}
                key={rider.id}
              >
                <input
                  type="radio"
                  name="deliverySelectedRider"
                  value={rider.id}
                  checked={String(deliverySelectedRiderId) === String(rider.id)}
                  onChange={(e) => setDeliverySelectedRiderId(e.target.value)}
                  disabled={rider.status !== 'available'}
                />

                <div className="dd-rider-avatar">🛵</div>

                <div>
                  <strong>{rider.name}</strong>
                  <span>{rider.mobile}</span>
                  <small>
                    {rider.vehicleType} {rider.vehicleNumber ? `• ${rider.vehicleNumber}` : ''}
                  </small>
                  {renderDeliveryLoginLinkActions(rider)}
                </div>

                <em className={rider.status}>
                  {DELIVERY_RIDER_STATUS_LABELS[rider.status] || rider.status}
                </em>
              </label>
            ))
          ) : (
            <div className="dd-empty small">
              <strong>No delivery boys</strong>
              <p>Add button se delivery boy account banayein.</p>
            </div>
          )}
        </div>

        <button
          type="button"
          className="dd-assign-btn"
          disabled={actionLoading || !deliverySelectedOrderIds.length || !deliverySelectedRiderId}
          onClick={assignDeliveryTrip}
        >
          Assign {deliverySelectedOrderIds.length || 0} Orders
          {deliverySelectedRider ? ` to ${deliverySelectedRider.name}` : ''}
        </button>

        <div className="dd-safe-note">
          🔐 Delivery boy customer se token number lega. Token match hone ke baad hi delivery confirm hogi.
        </div>
      </section>
    );

    const renderTripPreview = () => (
      <section className="dd-panel dd-trip-preview">
        <div className="dd-panel-head">
          <div>
            <h2>Trip Preview</h2>
            <p>Selected orders ka short route preview.</p>
          </div>
        </div>

        {deliverySelectedOrders.length ? (
          <>
            <div className="dd-trip-summary">
              <div>
                <small>Orders</small>
                <strong>{deliverySelectedOrders.length}</strong>
              </div>
              <div>
                <small>Total Amount</small>
                <strong>{formatMoney(selectedTotal)}</strong>
              </div>
              <div>
                <small>Area</small>
                <strong>{deliverySelectedOrders[0]?.areaKey || '-'}</strong>
              </div>
            </div>

            <div className="dd-route-list">
              <div className="dd-route-stop shop">
                <span>🏪</span>
                <div>
                  <strong>Shop Pickup</strong>
                  <small>Delivery boy order pickup karega</small>
                </div>
              </div>

              {deliverySelectedOrders.map((order, index) => (
                <div className="dd-route-stop" key={order.id}>
                  <span>{index + 1}</span>
                  <div>
                    <strong>{order.customerName}</strong>
                    <small>{order.areaKey} • Token {order.tokenNumber || 'N/A'}</small>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="dd-empty small">
            <strong>Select orders</strong>
            <p>Area panel se orders select karne ke baad preview dikhega.</p>
          </div>
        )}
      </section>
    );

    const openDeliveryRiderMetric = (rider, metric) => {
      setSelectedDeliveryRiderRecord(rider);
      setSelectedDeliveryRecordMetric(metric);
    };

    const renderDeliveryRiderRecords = () => (
      <section className="dd-rider-record-panel">
        <div className="dd-panel-head">
          <div>
            <h2>Delivery Boy Records</h2>
            <p>Today, Total, Active ya Trips par click karke individual records dekhein.</p>
          </div>
        </div>

        <div className="dd-rider-record-grid">
          {deliveryRiders.length ? (
            deliveryRiders.map((rider) => (
              <article className="dd-rider-record-card metric-card" key={`record_${rider.id}`}>
                <div className="dd-rider-record-card-top">
                  <div>
                    <strong>{rider.name}</strong>
                    <span>{rider.mobile || '-'}</span>
                  </div>

                  <button
                    type="button"
                    className="dd-rider-profile-btn"
                    onClick={() => openDeliveryRiderMetric(rider, 'trips')}
                  >
                    View Trips →
                  </button>
                </div>

                <div className="dd-rider-record-stats individual-actions">
                  <button
                    type="button"
                    className="today"
                    onClick={() => openDeliveryRiderMetric(rider, 'today')}
                  >
                    <small>Today</small>
                    <b>{toNumber(rider.deliveredToday, 0)}</b>
                    <span>View Records</span>
                  </button>

                  <button
                    type="button"
                    className="total"
                    onClick={() => openDeliveryRiderMetric(rider, 'total')}
                  >
                    <small>Total</small>
                    <b>{toNumber(rider.deliveredTotal, 0)}</b>
                    <span>View Records</span>
                  </button>

                  <button
                    type="button"
                    className="active"
                    onClick={() => openDeliveryRiderMetric(rider, 'active')}
                  >
                    <small>Active</small>
                    <b>{toNumber(rider.activeOrders, 0)}</b>
                    <span>View Records</span>
                  </button>

                  <button
                    type="button"
                    className="trips"
                    onClick={() => openDeliveryRiderMetric(rider, 'trips')}
                  >
                    <small>Trips</small>
                    <b>{toNumber(rider.totalTrips, 0)}</b>
                    <span>View Records</span>
                  </button>
                </div>
              </article>
            ))
          ) : (
            <div className="dd-empty small">
              <strong>No delivery boy record</strong>
              <p>Delivery boy add karne ke baad uska record yahan show hoga.</p>
            </div>
          )}
        </div>
      </section>
    );

    const renderDeliveryRiderDetailModal = () => {
      if (!selectedDeliveryRiderRecord) return null;

      const rider = selectedDeliveryRiderRecord;
      const metric = selectedDeliveryRecordMetric || 'trips';

      const riderTrips = deliveryTripHistory
        .filter(
          (trip) =>
            String(trip.riderId || trip.rider_id || '') ===
            String(rider.id)
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt || b.created_at || 0).getTime() -
            new Date(a.createdAt || a.created_at || 0).getTime()
        );

      const isDelivered = (order) =>
        String(order?.status || '').toLowerCase() === 'delivered';

      const isActiveDelivery = (order) =>
        !['delivered', 'cancelled', 'failed'].includes(
          String(order?.status || '').toLowerCase()
        );

      const isDeliveredToday = (order) => {
        if (!isDelivered(order)) return false;

        const deliveredValue =
          order?.delivered_at ||
          order?.deliveredAt ||
          order?.token_verified_at ||
          order?.tokenVerifiedAt ||
          '';

        if (!deliveredValue) return false;

        const deliveredDate = new Date(deliveredValue);
        if (Number.isNaN(deliveredDate.getTime())) return false;

        const now = new Date();

        return (
          deliveredDate.getFullYear() === now.getFullYear() &&
          deliveredDate.getMonth() === now.getMonth() &&
          deliveredDate.getDate() === now.getDate()
        );
      };

      const filterOrdersByMetric = (orders = []) => {
        if (metric === 'today') return orders.filter(isDeliveredToday);
        if (metric === 'total') return orders.filter(isDelivered);
        if (metric === 'active') return orders.filter(isActiveDelivery);
        return orders;
      };

      const visibleTrips = riderTrips
        .map((trip) => ({
          ...trip,
          orders: filterOrdersByMetric(trip.orders || []),
        }))
        .filter((trip) => metric === 'trips' || (trip.orders || []).length > 0);

      const visibleOrderCount = visibleTrips.reduce(
        (sum, trip) => sum + (trip.orders || []).length,
        0
      );

      const metricConfig = {
        today: {
          title: 'Today Delivered Orders',
          description: 'Aaj complete hue delivery orders ka individual record.',
          emptyTitle: 'Aaj koi delivered order nahi hai',
          emptyText: 'Aaj delivered hua order yahan show hoga.',
        },
        total: {
          title: 'All Delivered Orders',
          description: 'Is rider ke total completed delivery orders ka record.',
          emptyTitle: 'Delivered order record nahi mila',
          emptyText: 'Delivery complete hone ke baad record yahan show hoga.',
        },
        active: {
          title: 'Active Delivery Orders',
          description: 'Abhi rider ke paas active ya pending delivery orders.',
          emptyTitle: 'Koi active delivery order nahi hai',
          emptyText: 'Rider ko order assign karne ke baad record yahan show hoga.',
        },
        trips: {
          title: 'Trip Records',
          description: 'Is rider ke trip-wise orders aur status details.',
          emptyTitle: 'Trip record nahi mila',
          emptyText: 'Trip assign hone ke baad record yahan show hoga.',
        },
      };

      const currentMetric = metricConfig[metric] || metricConfig.trips;

      return (
        <div
          className="dd-rider-detail-backdrop"
          onClick={() => setSelectedDeliveryRiderRecord(null)}
        >
          <section
            className="dd-rider-detail-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="dd-rider-detail-head">
              <div>
                <small>Delivery Boy Record</small>
                <h2>{rider.name}</h2>
                <p>{rider.mobile || '-'}</p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedDeliveryRiderRecord(null)}
              >
                ×
              </button>
            </div>

            <div className="dd-rider-metric-tabs">
              <button
                type="button"
                className={metric === 'today' ? 'active today' : 'today'}
                onClick={() => setSelectedDeliveryRecordMetric('today')}
              >
                <small>Today</small>
                <b>{toNumber(rider.deliveredToday, 0)}</b>
              </button>

              <button
                type="button"
                className={metric === 'total' ? 'active total' : 'total'}
                onClick={() => setSelectedDeliveryRecordMetric('total')}
              >
                <small>Total</small>
                <b>{toNumber(rider.deliveredTotal, 0)}</b>
              </button>

              <button
                type="button"
                className={metric === 'active' ? 'active metric-active' : 'metric-active'}
                onClick={() => setSelectedDeliveryRecordMetric('active')}
              >
                <small>Active</small>
                <b>{toNumber(rider.activeOrders, 0)}</b>
              </button>

              <button
                type="button"
                className={metric === 'trips' ? 'active trips' : 'trips'}
                onClick={() => setSelectedDeliveryRecordMetric('trips')}
              >
                <small>Trips</small>
                <b>{toNumber(rider.totalTrips, 0)}</b>
              </button>
            </div>

            <div className="dd-rider-detail-profile-grid">
              <div>
                <small>Status</small>
                <strong>
                  {DELIVERY_RIDER_STATUS_LABELS[rider.status] ||
                    rider.status ||
                    '-'}
                </strong>
              </div>
              <div>
                <small>Vehicle</small>
                <strong>
                  {rider.vehicleType || '-'}
                  {rider.vehicleNumber ? ` • ${rider.vehicleNumber}` : ''}
                </strong>
              </div>
              <div>
                <small>Service Area</small>
                <strong>{rider.serviceArea || '-'}</strong>
              </div>
              <div>
                <small>Selected Record Count</small>
                <strong>{metric === 'trips' ? visibleTrips.length : visibleOrderCount}</strong>
              </div>
            </div>

            <div className="dd-rider-detail-trip-section">
              <div className="dd-panel-head">
                <div>
                  <h2>{currentMetric.title}</h2>
                  <p>{currentMetric.description}</p>
                </div>
              </div>

              <div className="dd-rider-detail-trip-list">
                {visibleTrips.length ? (
                  visibleTrips.map((trip) => (
                    <article className="dd-rider-detail-trip-card" key={`${metric}_${trip.id}`}>
                      <div className="dd-rider-detail-trip-head">
                        <div>
                          <strong>{trip.tripNo}</strong>
                          <span>{formatDateTime(trip.createdAt || trip.created_at)}</span>
                        </div>

                        <em className={`dd-status-pill ${trip.status}`}>
                          {DELIVERY_TRIP_STATUS_LABELS[trip.status] || trip.status}
                        </em>
                      </div>

                      <div className="dd-rider-detail-trip-meta">
                        <span>{toNumber(trip.orders?.length, 0)} Records</span>
                        <span>{formatMoney(trip.total_amount || trip.totalAmount || 0)}</span>
                        <span>{toNumber(trip.totalDistanceKm || trip.total_distance_km, 0).toFixed(2)} KM</span>
                      </div>

                      <div className="dd-rider-detail-order-list">
                        {(trip.orders || []).length ? (
                          trip.orders.map((order, index) => (
                            <div
                              className="dd-rider-detail-order-row"
                              key={order.id || `${trip.id}_${index}`}
                            >
                              <span>{index + 1}</span>

                              <div>
                                <strong>Order #{order.order_id || order.id || '-'}</strong>
                                <small>
                                  {order.customerName || order.customer_name || 'Customer'}
                                  {' • '}
                                  {order.customerMobile || order.customer_mobile || '-'}
                                </small>
                                <small>
                                  {order.fullAddress || order.full_address || order.address || order.areaKey || '-'}
                                </small>
                                {order.delivered_at || order.deliveredAt ? (
                                  <small>
                                    Delivered: {formatDateTime(order.delivered_at || order.deliveredAt)}
                                  </small>
                                ) : null}
                              </div>

                              <div>
                                <b>{formatMoney(order.amount || order.displayTotal || 0)}</b>
                                <em className={`dd-status-pill ${order.status || 'assigned'}`}>
                                  {order.status || 'assigned'}
                                </em>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="dd-empty small">
                            <strong>Order details not available</strong>
                            <p>Is trip ke order records backend response me nahi mile.</p>
                          </div>
                        )}
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="dd-empty small">
                    <strong>{currentMetric.emptyTitle}</strong>
                    <p>{currentMetric.emptyText}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      );
    };

    const renderDeliveryRiders = () => (
      <section className="dd-panel dd-riders-table-panel">
        <div className="dd-panel-head">
          <div>
            <h2>Delivery Boys</h2>
            <p>Rider account, delivery record, status, area aur vehicle manage karein.</p>
          </div>
          <button type="button" onClick={() => setDeliveryFormOpen(true)}>
            + Add Delivery Boy
          </button>
        </div>

        {renderDeliveryRiderRecords()}

        <div className="dd-riders-table">
          <div className="dd-table-head">
            <span>Name</span>
            <span>Mobile</span>
            <span>Vehicle</span>
            <span>Area</span>
            <span>Status</span>
            <span>Login Link</span>
            <span>Action</span>
          </div>

          {deliveryRiders.length ? (
            deliveryRiders.map((rider) => (
              <div className="dd-table-row" key={rider.id}>
                <span>{rider.name}</span>
                <span>{rider.mobile}</span>
                <span>{rider.vehicleType} {rider.vehicleNumber || '-'}</span>
                <span>{rider.serviceArea || '-'}</span>
                <span>
                  <b className={`dd-status-pill ${rider.status}`}>
                    {DELIVERY_RIDER_STATUS_LABELS[rider.status] || rider.status}
                  </b>
                </span>
                <span>{renderDeliveryLoginLinkActions(rider)}</span>
                <span className="dd-rider-admin-actions">
                  <select
                    value={rider.status}
                    onChange={(e) => updateDeliveryRiderStatus(rider.id, e.target.value)}
                  >
                    <option value="available">Available</option>
                    <option value="on_delivery">On Delivery</option>
                    <option value="inactive">Inactive</option>
                  </select>

                  <button
                    type="button"
                    className="reset-pin"
                    onClick={() => openResetPinModal(rider)}
                  >
                    Reset PIN
                  </button>

                  <button
                    type="button"
                    className="delete"
                    onClick={() => setDeleteRiderTarget(rider)}
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))
          ) : (
            <div className="dd-empty">
              <strong>No delivery boys</strong>
              <p>Rider account banane ke baad yahan show hoga.</p>
            </div>
          )}
        </div>
      </section>
    );


    const renderTracking = () => (
      <section className="dd-panel dd-live-track-panel">
        <div className="dd-empty">
          <strong>Tracking Facility Not Available</strong>
          <p>
            Live rider tracking facility abhi temporarily available nahi hai.
            Delivery dispatch, rider assignment, delivery boy records aur trip records normally kaam karenge.
          </p>
        </div>
      </section>
    );


    const renderTrips = () => (
      <section className="dd-panel dd-trips-history-panel">
        <div className="dd-panel-head">
          <div>
            <h2>Trip Records</h2>
            <p>Assigned aur delivered trips ka record. Active order ko new rider ko reassign kar sakte hain.</p>
          </div>
        </div>

        <div className="dd-trips-history-list">
          {deliveryTrips.length ? (
            deliveryTrips.map((trip) => (
              <article className="dd-history-trip-card" key={trip.id}>
                <div className="dd-history-trip-row">
                  <div>
                    <strong>{trip.tripNo}</strong>
                    <span>{formatDateTime(trip.createdAt)}</span>
                  </div>
                  <div>
                    <b>{trip.riderName}</b>
                    <span>{trip.totalOrders} orders</span>
                  </div>
                  <div>
                    <small className={`dd-status-pill ${trip.status}`}>
                      {DELIVERY_TRIP_STATUS_LABELS[trip.status] || trip.status}
                    </small>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDeliveryTrackingTripId(trip.id);
                      setDeliveryTab('tracking');
                    }}
                  >
                    Track
                  </button>
                </div>

                <div className="dd-route-list dd-trip-order-management-list">
                  {(trip.orders || []).length ? (
                    trip.orders.map((order, index) => {
                      const orderStatus = String(order?.status || '').toLowerCase();
                      const canReassign = !['delivered', 'cancelled', 'failed'].includes(orderStatus);

                      return (
                        <div
                          className="dd-route-stop dd-trip-order-management-row"
                          key={order.tripOrderId || order.id || `${trip.id}_${index}`}
                        >
                          <span>{index + 1}</span>

                          <div>
                            <strong>Order #{order.orderId || order.order_id || order.id || '-'}</strong>
                            <small>
                              {order.customerName || order.customer_name || 'Customer'}
                              {' • '}
                              {order.customerMobile || order.customer_mobile || '-'}
                            </small>
                            <small>{order.fullAddress || order.full_address || order.areaKey || '-'}</small>
                            <small className="dd-token">
                              Token: {order.tokenNumber || 'N/A'}
                            </small>
                          </div>

                          <div className="dd-trip-order-actions">
                            <small className={`dd-status-pill ${orderStatus || 'assigned'}`}>
                              {DELIVERY_TRIP_STATUS_LABELS[orderStatus] || orderStatus || 'Assigned'}
                            </small>

                            {canReassign ? (
                              <button
                                type="button"
                                className="dd-reassign-btn"
                                onClick={() => openReassignModal(order, trip)}
                              >
                                Reassign Rider
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dd-empty small">
                      <strong>No order details</strong>
                      <p>Trip order records available nahi hain.</p>
                    </div>
                  )}
                </div>
              </article>
            ))
          ) : (
            <div className="dd-empty">
              <strong>No trips found</strong>
              <p>Assign hone ke baad trips yahan show honge.</p>
            </div>
          )}
        </div>
      </section>
    );

    const renderResetPinModal = () =>
      resetPinRider ? (
        <div className="dd-modal-backdrop">
          <form className="dd-rider-form-modal dd-compact-admin-modal" onSubmit={resetRiderPin}>
            <div className="dd-modal-head">
              <div>
                <h2>Reset Delivery Boy PIN</h2>
                <p>{resetPinRider.name} • {resetPinRider.mobile}</p>
              </div>
              <button type="button" onClick={closeResetPinModal}>×</button>
            </div>

            <label>
              New Login PIN
              <input
                autoFocus
                value={resetPinValue}
                onChange={(event) =>
                  setResetPinValue(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="4-6 digit PIN"
              />
            </label>

            <div className="dd-modal-action-row">
              <button type="button" className="secondary" onClick={closeResetPinModal}>
                Cancel
              </button>
              <button type="submit" className="primary" disabled={actionLoading}>
                Save New PIN
              </button>
            </div>
          </form>
        </div>
      ) : null;

    const renderDeleteRiderModal = () =>
      deleteRiderTarget ? (
        <div className="dd-modal-backdrop">
          <section className="dd-rider-form-modal dd-compact-admin-modal">
            <div className="dd-modal-head">
              <div>
                <h2>Delete Delivery Boy?</h2>
                <p>{deleteRiderTarget.name} • {deleteRiderTarget.mobile}</p>
              </div>
              <button type="button" onClick={() => setDeleteRiderTarget(null)}>×</button>
            </div>

            <div className="dd-danger-note">
              Active delivery assigned hai to account delete nahi hoga. Pehle order reassign ya complete karein.
            </div>

            <div className="dd-modal-action-row">
              <button type="button" className="secondary" onClick={() => setDeleteRiderTarget(null)}>
                Cancel
              </button>
              <button type="button" className="danger" onClick={deleteDeliveryRider} disabled={actionLoading}>
                Delete Account
              </button>
            </div>
          </section>
        </div>
      ) : null;

    const renderReassignModal = () =>
      reassignOrder ? (
        <div className="dd-modal-backdrop">
          <form className="dd-rider-form-modal dd-compact-admin-modal" onSubmit={reassignTripOrder}>
            <div className="dd-modal-head">
              <div>
                <h2>Reassign Delivery Order</h2>
                <p>Order #{reassignOrder.orderId || reassignOrder.order_id || reassignOrder.id || '-'}</p>
              </div>
              <button type="button" onClick={closeReassignModal}>×</button>
            </div>

            <div className="dd-reassign-summary">
              <div>
                <small>Current Rider</small>
                <strong>{reassignOrder.oldRiderName || '-'}</strong>
              </div>
              <div>
                <small>Customer</small>
                <strong>{reassignOrder.customerName || reassignOrder.customer_name || 'Customer'}</strong>
              </div>
              <div>
                <small>Token Number</small>
                <strong>{reassignOrder.tokenNumber || 'N/A'}</strong>
              </div>
            </div>

            <label>
              New Delivery Boy
              <select
                value={reassignRiderId}
                onChange={(event) => {
                  setReassignRiderId(event.target.value);
                  setReassignError('');
                }}
              >
                <option value="">Select rider</option>
                {getReassignableDeliveryRiders().map((rider) => (
                  <option key={rider.id} value={rider.id}>
                    {rider.name} • {rider.mobile} • {DELIVERY_RIDER_STATUS_LABELS[rider.status] || rider.status}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Reason
              <textarea
                value={reassignReason}
                onChange={(event) => setReassignReason(event.target.value)}
                placeholder="Example: Current rider delivery complete nahi kar pa raha hai"
              />
            </label>

            <div className="dd-safe-note">
              Token number same rahega. Customer ko naya token nahi dena padega.
            </div>

            {reassignError ? (
              <div className="dd-danger-note">
                {reassignError}
              </div>
            ) : null}

            <div className="dd-modal-action-row">
              <button type="button" className="secondary" onClick={closeReassignModal}>
                Cancel
              </button>
              <button type="submit" className="primary" disabled={actionLoading || !reassignRiderId}>
                {actionLoading ? 'Reassigning...' : 'Confirm Reassign'}
              </button>
            </div>
          </form>
        </div>
      ) : null;

    const renderRiderForm = () =>
      deliveryFormOpen ? (
        <div className="dd-modal-backdrop">
          <form className="dd-rider-form-modal" onSubmit={createDeliveryRider}>
            <div className="dd-modal-head">
              <div>
                <h2>Create Delivery Boy Account</h2>
                <p>Mobile + PIN se rider login karega.</p>
              </div>
              <button type="button" onClick={() => setDeliveryFormOpen(false)}>
                ×
              </button>
            </div>

            <label>
              Full Name
              <input
                value={deliveryRiderForm.name}
                onChange={(e) =>
                  setDeliveryRiderForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Rider name"
              />
            </label>

            <label>
              Mobile Number
              <input
                value={deliveryRiderForm.mobile}
                onChange={(e) =>
                  setDeliveryRiderForm((prev) => ({
                    ...prev,
                    mobile: e.target.value.replace(/\D/g, '').slice(0, 10),
                  }))
                }
                placeholder="10 digit mobile"
              />
            </label>

            <label>
              Login PIN
              <input
                value={deliveryRiderForm.pin}
                onChange={(e) =>
                  setDeliveryRiderForm((prev) => ({
                    ...prev,
                    pin: e.target.value.replace(/\D/g, '').slice(0, 6),
                  }))
                }
                placeholder="4-6 digit PIN"
              />
            </label>

            <div className="dd-form-grid">
              <label>
                Vehicle Type
                <select
                  value={deliveryRiderForm.vehicleType}
                  onChange={(e) =>
                    setDeliveryRiderForm((prev) => ({
                      ...prev,
                      vehicleType: e.target.value,
                    }))
                  }
                >
                  <option value="bike">Bike</option>
                  <option value="scooter">Scooter</option>
                  <option value="cycle">Cycle</option>
                  <option value="walking">Walking</option>
                </select>
              </label>

              <label>
                Vehicle Number
                <input
                  value={deliveryRiderForm.vehicleNumber}
                  onChange={(e) =>
                    setDeliveryRiderForm((prev) => ({
                      ...prev,
                      vehicleNumber: e.target.value,
                    }))
                  }
                  placeholder="MH 04 AB 1234"
                />
              </label>
            </div>

            <label>
              Service Area
              <input
                value={deliveryRiderForm.serviceArea}
                onChange={(e) =>
                  setDeliveryRiderForm((prev) => ({
                    ...prev,
                    serviceArea: e.target.value,
                  }))
                }
                placeholder="Mira Road East"
              />
            </label>

            <label>
              Notes
              <textarea
                value={deliveryRiderForm.notes}
                onChange={(e) =>
                  setDeliveryRiderForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Optional notes"
              />
            </label>

            <button type="submit" className="dd-save-rider-btn" disabled={actionLoading}>
              Save Delivery Boy
            </button>
          </form>
        </div>
      ) : null;

    return (
      <section className="ff-side-workspace ff-delivery-mounted">
        <div className="ff-side-workspace-head">
          <div>
            <h2>Delivery Management</h2>
            <p>Ready delivery orders assign, delivery boy manage aur trip tracking.</p>
          </div>

          <div className="dd-top-actions">
            <button type="button" className="dd-secondary-mini" onClick={copyDeliveryBoyLoginLink}>
              🔗 Copy Link
            </button>
            <button type="button" className="dd-secondary-mini whatsapp" onClick={() => shareDeliveryBoyOnWhatsApp()}>
              WhatsApp
            </button>
            <button type="button" onClick={() => openSidePanel('orders')}>
              ← Orders
            </button>
          </div>
        </div>

        <section className="dd-login-link-card">
          <div>
            <strong>Delivery Boy Login Link</strong>
            <span>{getDeliveryBoyLoginLink()}</span>
          </div>

          <div>
            <button type="button" onClick={copyDeliveryBoyLoginLink}>
              Copy
            </button>
            <button type="button" className="whatsapp" onClick={() => shareDeliveryBoyOnWhatsApp()}>
              Share
            </button>
          </div>
        </section>

        <div className="dd-stats">
          <div className="dd-stat-card blue">
            <span>📦</span>
            <div>
              <strong>{stats.readyOrders}</strong>
              <small>Ready Delivery</small>
            </div>
          </div>
          <div className="dd-stat-card green">
            <span>🛵</span>
            <div>
              <strong>{stats.availableRiders}</strong>
              <small>Available Riders</small>
            </div>
          </div>
          <div className="dd-stat-card orange">
            <span>🚚</span>
            <div>
              <strong>{stats.onDelivery}</strong>
              <small>On Delivery</small>
            </div>
          </div>
          <div className="dd-stat-card purple">
            <span>🗺️</span>
            <div>
              <strong>{stats.activeTrips}</strong>
              <small>Active Trips</small>
            </div>
          </div>
        </div>

        <nav className="dd-tabs">
          <button
            type="button"
            className={deliveryTab === 'dispatch' ? 'active' : ''}
            onClick={() => setDeliveryTab('dispatch')}
          >
            Dispatch
          </button>
          <button
            type="button"
            className={deliveryTab === 'riders' ? 'active' : ''}
            onClick={() => setDeliveryTab('riders')}
          >
            Riders
          </button>
          <button
            type="button"
            className={deliveryTab === 'tracking' ? 'active' : ''}
            onClick={() => setDeliveryTab('tracking')}
          >
            Tracking
          </button>
          <button
            type="button"
            className={deliveryTab === 'trips' ? 'active' : ''}
            onClick={() => setDeliveryTab('trips')}
          >
            Trips
          </button>
        </nav>

        {panelLoading ? (
          <section className="dd-loading-panel">
            <div className="dd-loader"></div>
            <strong>Loading delivery dispatch...</strong>
          </section>
        ) : deliveryTab === 'riders' ? (
          renderDeliveryRiders()
        ) : deliveryTab === 'tracking' ? (
          renderTracking()
        ) : deliveryTab === 'trips' ? (
          renderTrips()
        ) : (
          <div className="dd-dispatch-grid">
            {renderDeliveryOrders()}
            <div className="dd-right-stack">
              {renderAssignRider()}
              {renderTripPreview()}
            </div>
          </div>
        )}

        {renderRiderForm()}
        {renderResetPinModal()}
        {renderDeleteRiderModal()}
        {renderReassignModal()}
        {renderDeliveryRiderDetailModal()}
      </section>
    );
  };


  const renderCustomersPanel = () => (
    <section className="ff-side-workspace">
      <div className="ff-side-workspace-head">
        <div>
          <h2>Customers / Udhari</h2>
          <p>Partial aur credit payment customers yahan dikhenge.</p>
        </div>
        <button type="button" onClick={() => openSidePanel('orders')}>← Orders</button>
      </div>

      <div className="ff-panel-search-row">
        <input
          value={customerSearch}
          onChange={(e) => setCustomerSearch(e.target.value)}
          placeholder="Search customer name / mobile"
        />
        <button type="button" onClick={fetchCustomerDues}>Search</button>
      </div>

      <div className="ff-due-list">
        {panelLoading ? (
          <div className="ff-image-empty">Loading customers...</div>
        ) : customerDues.length ? (
          customerDues.map((customer, index) => (
            <div className="ff-due-card" key={`${customer.customer_mobile}-${index}`}>
              <div>
                <strong>{customer.customer_name || 'Customer'}</strong>
                <span>{customer.customer_mobile}</span>
              </div>
              <div>
                <small>Total Due</small>
                <b>{formatMoney(customer.total_due)}</b>
              </div>
              <div>
                <small>Total Paid</small>
                <b>{formatMoney(customer.total_credit || customer.total_paid || 0)}</b>
              </div>
            </div>
          ))
        ) : (
          <div className="ff-image-empty">No due customers found.</div>
        )}
      </div>
    </section>
  );

  const renderReportsPanel = () => {
    const report = reportData || {};

    return (
      <section className="ff-side-workspace">
        <div className="ff-side-workspace-head">
          <div>
            <h2>Reports</h2>
            <p>Today order, ready, completed, paid aur due summary.</p>
          </div>
          <button type="button" onClick={() => openSidePanel('orders')}>← Orders</button>
        </div>

        <div className="ff-report-grid">
          <div className="ff-report-card">
            <span>Total Orders</span>
            <strong>{toNumber(report.total_orders, 0)}</strong>
          </div>
          <div className="ff-report-card">
            <span>Completed</span>
            <strong>{toNumber(report.completed_orders, 0)}</strong>
          </div>
          <div className="ff-report-card">
            <span>Ready</span>
            <strong>{toNumber(report.ready_orders, 0)}</strong>
          </div>
          <div className="ff-report-card">
            <span>Kitchen</span>
            <strong>{toNumber(report.preparing_orders, 0)}</strong>
          </div>
          <div className="ff-report-card green">
            <span>Total Sales</span>
            <strong>{formatMoney(report.total_sales)}</strong>
          </div>
          <div className="ff-report-card blue">
            <span>Total Paid</span>
            <strong>{formatMoney(report.total_paid)}</strong>
          </div>
          <div className="ff-report-card red">
            <span>Total Due</span>
            <strong>{formatMoney(report.total_due)}</strong>
          </div>
        </div>

        <button type="button" className="ff-panel-primary" onClick={fetchReadyReport}>
          ↻ Refresh Report
        </button>
      </section>
    );
  };

  const renderSettingsPanel = () => (
    <section className="ff-side-workspace">
      <div className="ff-side-workspace-head">
        <div>
          <h2>Settings</h2>
          <p>Ye setting page vendor ki order screen preferences ke liye hai.</p>
        </div>
        <button type="button" onClick={() => openSidePanel('orders')}>← Orders</button>
      </div>

      <div className="ff-settings-grid">
        <label>
          Shop / Brand Name
          <input
            value={uiSettings.shopName}
            onChange={(e) =>
              setUiSettings((prev) => ({
                ...prev,
                shopName: e.target.value,
              }))
            }
            placeholder="QuickBite"
          />
        </label>

        <label>
          Bill Paper Size
          <select
            value={uiSettings.paperSize}
            onChange={(e) =>
              setUiSettings((prev) => ({
                ...prev,
                paperSize: e.target.value,
              }))
            }
          >
            <option value="58mm">58mm Thermal</option>
            <option value="80mm">80mm Thermal</option>
            <option value="A4">A4</option>
          </select>
        </label>

        <label className="ff-check-setting">
          <input
            type="checkbox"
            checked={uiSettings.compactOrders}
            onChange={(e) =>
              setUiSettings((prev) => ({
                ...prev,
                compactOrders: e.target.checked,
              }))
            }
          />
          Compact order cards
        </label>

        <label className="ff-check-setting">
          <input
            type="checkbox"
            checked={uiSettings.autoRefresh}
            onChange={(e) =>
              setUiSettings((prev) => ({
                ...prev,
                autoRefresh: e.target.checked,
              }))
            }
          />
          Auto refresh orders
        </label>
      </div>

      <section className="ff-billing-settings-card">
        <div className="ff-billing-settings-head">
          <div className="ff-billing-settings-icon">%</div>

          <div>
            <h3>Billing & GST Settings</h3>
            <p>
              GST ko optional rakhein. Enable karne par default percentage new bills me automatically apply hoga.
            </p>
          </div>
        </div>

        <div className="ff-billing-settings-body">
          <label className="ff-billing-gst-switch">
            <input
              type="checkbox"
              checked={Boolean(billingSettings.gstEnabled)}
              disabled={billingSettingsLoading || billingSettingsSaving}
              onChange={(event) =>
                setBillingSettings((prev) => ({
                  ...prev,
                  gstEnabled: event.target.checked,
                }))
              }
            />
            <span>
              <strong>Apply GST by default on new bills</strong>
              <small>
                OFF rahega to GST jabardasti add nahi hoga. Invoice open karke individual bill par manually enable kar sakte hain.
              </small>
            </span>
          </label>

          <label className="ff-default-gst-field">
            Default GST Percentage
            <div>
              <input
                value={billingSettings.defaultGstPercent}
                disabled={
                  billingSettingsLoading ||
                  billingSettingsSaving
                }
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="Type GST %"
                onChange={(event) => {
                  const nextValue = sanitizeGstInput(event.target.value);

                  setBillingSettings((prev) => ({
                    ...prev,
                    defaultGstPercent: nextValue,
                    gstEnabled: toNumber(nextValue, 0) > 0,
                  }));
                }}
              />
              <span>%</span>
            </div>
          </label>
        </div>

        <p className="ff-gst-keyboard-helper">
          GST percentage direct keyboard se type karein. Example: 5, 12, 18 ya 2.5. Value 0 ya blank karne par GST OFF ho jayega.
        </p>

        <button
          type="button"
          className="ff-save-billing-settings-btn"
          onClick={saveBillingSettings}
          disabled={billingSettingsLoading || billingSettingsSaving}
        >
          {billingSettingsSaving
            ? 'Saving GST Settings...'
            : 'Save GST Settings'}
        </button>

        <div className="ff-billing-settings-note">
          {billingSettings.gstEnabled
            ? `New invoice par default ${billingSettings.defaultGstPercent || 0}% GST apply hoga. Vendor har bill me GST ON/OFF ya percentage manually change kar sakta hai.`
            : 'GST default OFF hai. New invoice par GST amount ₹0 rahega jab tak vendor manually GST enable na kare.'}
        </div>
      </section>

      <section className="ff-notification-settings-card">
        <div className="ff-notification-settings-head">
          <div className="ff-notification-settings-icon">🔔</div>

          <div>
            <h3>New Order Phone Notifications</h3>
            <p>
              Customer order place kare to vendor phone par instant alert receive karein.
            </p>
          </div>
        </div>

        <div className="ff-notification-overview-grid">
          <div className="ff-notification-overview-item">
            <span>Permission</span>
            <strong className={`ff-permission-value ${notificationPermission}`}>
              {notificationPermission}
            </strong>
          </div>

          <div className="ff-notification-overview-item">
            <span>Registered Devices</span>
            <strong>{notificationDevices.length} / 3</strong>
          </div>
        </div>

        <div className="ff-notification-primary-actions">
          <button
            type="button"
            className="ff-notification-refresh-btn"
            onClick={enablePhoneNotifications}
            disabled={notificationSaving}
          >
            {notificationSaving
              ? 'Enabling Notifications...'
              : notificationPermission === 'granted'
                ? '↻ Refresh Current Device Token'
                : '🔔 Enable Notifications'}
          </button>
        </div>

        {notificationPermission === 'denied' ? (
          <div className="ff-notification-warning">
            Chrome me website ke Site settings → Notifications → Allow karke phir button dabayein.
          </div>
        ) : null}

        <div className="ff-notification-device-list">
          {notificationDevicesLoading ? (
            <p className="ff-notification-empty">
              Notification devices load ho rahe hain...
            </p>
          ) : null}

          {!notificationDevicesLoading && !notificationDevices.length ? (
            <p className="ff-notification-empty">
              Abhi koi notification device registered nahi hai.
            </p>
          ) : null}

          {notificationDevices.map((device) => (
            <article
              key={device.deviceId}
              className={`ff-notification-device-card ${
                device.isCurrent ? 'current' : ''
              } ${device.isCompulsory ? 'compulsory' : ''}`}
            >
              <div className="ff-notification-device-head">
                <div>
                  <strong>
                    {device.isCurrent ? '📱 Current Device' : '📱 Registered Device'}
                  </strong>
                  <p>{device.deviceName || 'Browser Device'}</p>
                </div>

                <span className={`ff-device-status ${device.isCompulsory ? 'locked' : ''}`}>
                  {device.isCompulsory ? 'Protected' : 'Active'}
                </span>
              </div>

              {device.isCurrent ? (
                <label className="ff-device-compulsory-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(device.isCompulsory)}
                    onChange={(event) =>
                      toggleCurrentDeviceCompulsory(event.target.checked)
                    }
                    disabled={notificationSaving}
                  />
                  <span>
                    Is device par notification compulsory rakhein
                  </span>
                </label>
              ) : (
                <p className="ff-device-helper">
                  {device.isCompulsory
                    ? '🔒 Compulsory device: auto-remove nahi hoga.'
                    : 'Oldest non-compulsory device hone par fourth login me auto-remove ho sakta hai.'}
                </p>
              )}

              <button
                type="button"
                className="ff-device-remove-btn"
                onClick={() => removeNotificationDevice(device.deviceId)}
                disabled={notificationSaving}
              >
                Remove Device
              </button>
            </article>
          ))}
        </div>

        <div className="ff-notification-limit-note">
          <strong>Maximum 3 active devices</strong>
          <span>
            Fourth device enable hone par oldest non-compulsory device automatically remove ho jayega.
          </span>
        </div>
      </section>

      <div className="ff-settings-note">
        ⚙️ Setting icon ka use order screen preference ke liye hai. Real printer add browser/system print dialog se hota hai; yahan paper size/preference save hota hai.
      </div>
    </section>
  );

  const renderMainWorkspace = () => {
    if (sidePanel === 'customers') {
      return canAccess('recordsView') ? renderCustomersPanel() : renderStaffAccessDenied('Records access nahi hai');
    }

    if (sidePanel === 'reports') {
      return canAccessAny(['reportsView', 'expenseManage']) ? renderReportsPanel() : renderStaffAccessDenied('Reports access nahi hai');
    }

    if (sidePanel === 'onlineOrders') {
      return renderOnlineOrdersPanel();
    }

    if (sidePanel === 'delivery') {
      return canAccessAny(['deliveryDispatch', 'assignRider', 'reassignRider', 'deliveryRecords', 'riderManage'])
        ? renderDeliveryPanel()
        : renderStaffAccessDenied('Delivery access nahi hai');
    }

    if (sidePanel === 'staff') {
      return canAccess('staffManagement') ? (
        <StaffManagement
          vendorId={vendorId}
          vendorType={vendorType}
          getToken={getToken}
          showMessage={showMessage}
        />
      ) : renderStaffAccessDenied('Staff Management access nahi hai');
    }

    if (sidePanel === 'settings') {
      return canAccess('settingsManage') ? renderSettingsPanel() : renderStaffAccessDenied('Settings access nahi hai');
    }

    if (!canViewOrdersPanel) {
      return renderStaffAccessDenied('Orders access nahi hai');
    }

    return (
      <div className={`ff-image-shell ${uiSettings.compactOrders ? 'compact' : ''}`}>
        {(!isMobile || currentScreen === 'list') ? renderOrderQueue() : null}
        {(!isMobile || currentScreen === 'detail') ? renderDetailWorkspace() : null}
      </div>
    );
  };


  const renderDesktopSidebar = () => (
    <aside className="ff-app-sidebar" aria-label="Fast food navigation">
      <div className="ff-brand">
        <span className="ff-brand-icon">🍔</span>
        <strong>{uiSettings.shopName || 'QuickBite'}</strong>
        {isStaffLogin ? <small className="ff-staff-role-badge">{staffName}</small> : null}
      </div>

      <nav className="ff-side-nav">
        {!isStaffLogin && (
          <button
            type="button"
            className={sidePanel === 'orders' && activeTab === 'active' ? 'active' : ''}
            onClick={() => openSidePanel('orders')}
          >
            <span>⌂</span>
            Dashboard
          </button>
        )}

        {canViewOrdersPanel && (
          <button
            type="button"
            className={sidePanel === 'orders' ? 'active' : ''}
            onClick={() => openSidePanel('orders')}
          >
            <span>🧾</span>
            Orders
          </button>
        )}

        {canViewOrdersPanel && (
          <button
            type="button"
            className={sidePanel === 'onlineOrders' ? 'active' : ''}
            onClick={() => openSidePanel('onlineOrders')}
          >
            <span>🌐</span>
            Online Orders
          </button>
        )}

        {canUseKitchenTools && (
          <button
            type="button"
            className={statusFilter === 'preparing' ? 'active' : ''}
            onClick={() => openSidePanel('kitchen')}
          >
            <span>👨‍🍳</span>
            Kitchen
          </button>
        )}

        {canUseBillingTools && (
          <button
            type="button"
            className={currentScreen === 'detail' || sidePanel === 'invoice' ? 'active' : ''}
            onClick={() => openSidePanel('invoice')}
          >
            <span>📄</span>
            Invoice
          </button>
        )}

        {canAccess('recordsView') && (
          <button
            type="button"
            className={sidePanel === 'customers' ? 'active' : ''}
            onClick={() => openSidePanel('customers')}
          >
            <span>👥</span>
            Customers
          </button>
        )}

        {canAccessAny(['deliveryDispatch', 'assignRider', 'reassignRider', 'deliveryRecords', 'riderManage']) && (
          <button
            type="button"
            className={sidePanel === 'delivery' ? 'active' : ''}
            onClick={() => openSidePanel('delivery')}
          >
            <span>🛵</span>
            Delivery
          </button>
        )}

        {canAccess('staffManagement') && (
          <button
            type="button"
            className={sidePanel === 'staff' ? 'active' : ''}
            onClick={() => openSidePanel('staff')}
          >
            <span>👥</span>
            Staff
          </button>
        )}

        {canAccessAny(['reportsView', 'expenseManage']) && (
          <button
            type="button"
            className={sidePanel === 'reports' ? 'active' : ''}
            onClick={() => openSidePanel('reports')}
          >
            <span>📊</span>
            Reports
          </button>
        )}

        {canAccess('settingsManage') && (
          <button
            type="button"
            className={sidePanel === 'settings' ? 'active' : ''}
            onClick={() => openSidePanel('settings')}
          >
            <span>⚙️</span>
            Settings
          </button>
        )}
      </nav>

      <button
        type="button"
        className="ff-side-logout"
        onClick={() => {
          localStorage.removeItem('token');
          localStorage.removeItem(STAFF_TOKEN_KEY);
          localStorage.removeItem(STAFF_USER_KEY);
          localStorage.removeItem(STAFF_PERMISSIONS_KEY);
          localStorage.removeItem(STAFF_VENDOR_ID_KEY);
          localStorage.removeItem(STAFF_VENDOR_TYPE_KEY);
          sessionStorage.removeItem(STAFF_TOKEN_KEY);
          sessionStorage.removeItem(STAFF_USER_KEY);
          sessionStorage.removeItem(STAFF_PERMISSIONS_KEY);
          sessionStorage.removeItem(STAFF_VENDOR_ID_KEY);
          sessionStorage.removeItem(STAFF_VENDOR_TYPE_KEY);
          window.location.href = isStaffLogin
            ? `/staff-login?vendor=${vendorId || ''}&vendorType=${encodeURIComponent(vendorType || 'fastfood')}`
            : '/login';
        }}
      >
        <span>↪</span>
        Logout
      </button>
    </aside>
  );


  const renderMobileBottomNav = () => (
    <>
      {canAccess('counterOrder') && (
        <button
          type="button"
          className="ff-counter-floating-btn"
          onClick={openCounterOrder}
        >
          <span>＋</span>
          Counter
        </button>
      )}

      <nav className="ff-mobile-bottom-nav" aria-label="Mobile quick navigation">
        {canViewOrdersPanel && (
          <button
            type="button"
            className={currentScreen === 'list' && activeTab === 'active' && sidePanel === 'orders' ? 'active' : ''}
            onClick={() => openSidePanel('orders')}
          >
            <span>🧾</span>
            Orders
          </button>
        )}

        {canViewOrdersPanel && (
          <button
            type="button"
            className={sidePanel === 'onlineOrders' ? 'active' : ''}
            onClick={() => openSidePanel('onlineOrders')}
          >
            <span>🌐</span>
            Online
          </button>
        )}

        {canUseKitchenTools && (
          <button
            type="button"
            className={statusFilter === 'preparing' ? 'active' : ''}
            onClick={() => openSidePanel('kitchen')}
          >
            <span>👨‍🍳</span>
            Kitchen
          </button>
        )}

        {canUseBillingTools && (
          <button
            type="button"
            className={currentScreen === 'detail' || sidePanel === 'invoice' ? 'active' : ''}
            onClick={() => openSidePanel('invoice')}
          >
            <span>📄</span>
            Invoice
          </button>
        )}

        {canAccess('recordsView') && (
          <button
            type="button"
            className={activeTab === 'history' ? 'active' : ''}
            onClick={() => {
              setSidePanel('orders');
              setActiveTab('history');
              setCurrentScreen('list');
            }}
          >
            <span>↺</span>
            History
          </button>
        )}

        {canAccessAny(['deliveryDispatch', 'assignRider', 'reassignRider', 'deliveryRecords', 'riderManage']) && (
          <button
            type="button"
            className={sidePanel === 'delivery' ? 'active' : ''}
            onClick={() => openSidePanel('delivery')}
          >
            <span>🛵</span>
            Delivery
          </button>
        )}
      </nav>
    </>
  );


  const renderWorkflowSummary = () => (
    <section className="ff-workflow-summary">
      <strong>WORKFLOW SUMMARY</strong>

      <div className="ff-workflow-icons">
        <div><span>📋</span><small>Order<br />Received</small></div>
        <b>→</b>
        <div><span>✅</span><small>Accept</small></div>
        <b>→</b>
        <div><span>👨‍🍳</span><small>Kitchen</small></div>
        <b>→</b>
        <div><span>🍽️</span><small>Ready</small></div>
        <b>→</b>
        <div><span>🧾</span><small>Invoice</small></div>
        <b>→</b>
        <div><span>✅</span><small>Complete</small></div>
        <b>→</b>
        <div><span>📄</span><small>History<br />Records</small></div>
      </div>

      <div className="ff-logic-note">
        💡 Payment partial/credit ho to bhi order complete ho sakta hai. Due amount records me save rahega.
      </div>
    </section>
  );

  return (
    <>
      {!isStaffLogin ? <Dashboard /> : null}

      {renderCancelOrderPopup()}
      {renderCounterOrderModal()}
      {renderInvoiceProductPicker()}
      {renderShortcutHelpPopup()}

      <main className="ff-image-page">
        {message ? (
          <div className={`ff-image-toast ${messageType}`}>
            <span>{messageType === 'error' ? '⚠️' : '✅'}</span>
            <p>{message}</p>
            <button type="button" onClick={() => setMessage('')}>×</button>
          </div>
        ) : null}

        <header className="ff-image-header">
          <div>
            <h1>Simple Fast Food Order Workflow</h1>
            <p>Easy, fast, low-confusion flow for vendors</p>
            {isStaffLogin ? <small className="ff-staff-role-badge">Staff Login: {staffName}</small> : null}
          </div>

          <div className="ff-image-header-actions">
            <button
              type="button"
              onClick={() => setShortcutHelpOpen(true)}
              title="Keyboard Shortcuts (?)"
            >
              ⌨ Shortcuts
            </button>

            {canAccess('counterOrder') && (
              <button
                type="button"
                className="ff-counter-header-btn"
                onClick={openCounterOrder}
              >
                + Counter Order
              </button>
            )}

            {!isStaffLogin && (
            <button
              type="button"
              onClick={enablePhoneNotifications}
              disabled={notificationSaving}
            >
              {notificationSaving
                ? '🔔 Enabling...'
                : notificationPermission === 'granted'
                  ? '🔔 Notifications On'
                  : '🔔 Enable Notifications'}
            </button>
            )}
            <button type="button" onClick={testAndActivateRing}>
              {ringReady ? '🔊 Ring Ready' : '🔊 Activate Ring'}
            </button>
            <button type="button" onClick={enableVoice}>
              🗣 Voice {voiceEnabled ? 'On' : 'Off'}
            </button>
            <button type="button" onClick={() => fetchOrders({ silent: false })}>
              ↻ Refresh
            </button>
          </div>
        </header>

        <div className="ff-image-app-shell">
          {renderDesktopSidebar()}

          {renderMainWorkspace()}
        </div>

        {!isStaffLogin ? renderWorkflowSummary() : null}

        {renderMobileBottomNav()}
      </main>
    </>
  );
};

export default StandardOrdersReceived;
